/**
 * Block inspector — structural port of the monolith's esInsp() (2026-08-08 deep-parity pass):
 *
 *   · Manual/Dynamic strip (.dynseg) on every block except divider/spacer — real toggle
 *     on blocks with a livefill field (floorplan/listing); disabled "Dynamic" elsewhere
 *   · ONE CLEAN BOX (.inspcard): every field is an .essec (centered .essech head +
 *     .essecb body) inside a single card, compat notes (COMPAT) on top
 *   · card-style image fields (esImageField → ImgCard): 5:4 contain preview, empty
 *     "No photo yet — select from Bynder" state, navy "📁 Select a photo" action grid,
 *     × remove, hidden merged Alt input (auto-filled from the Bynder pick), hidden
 *     hosted-URL strip (.esurlbox) — Bynder-only, no upload (monolith 2026-07-25)
 *   · hero gets the Image width select INSIDE the card (__insetInlineHtml); the
 *     image band gets the Overlay effect select (__overlayInlineHtml)
 *   · theme selects for body/cta (white) + community/floorplan/event (midnight) —
 *     inspector-level controls, not fields (__themeControlHtml)
 *   · repeat → image-forward .repc cards + "Edit details" .repmodal (repEditor);
 *     listrows → grouped Collection/home cards + edit modal (listRowsEditor);
 *     amchips with the real amenity icons; socgrid icon grid; gifgen with the
 *     monolith's live previews (.sbprev marquee / .gigprev); film strip (esFilm)
 *   · guardrails stay: hard maxlength clamp + .grc counters + red .gr-block ring
 *   · "Delete this block" (.esdelf) at the bottom
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { FieldDescriptor } from "../types/blocks";
import type { AnyBlockInstance } from "../email/document";
import { blockDef } from "../email/blocks";
import AMENITY_ICONS from "../email/amenity-icons.json";
import { SOC_KEYS, LEN, socDefaults } from "../email/brand";
import { GIF_GENERATORS } from "../services/gif";
/* `getCommunityPack` / `searchCommunities` / `FillLevel` dropped 2026-08-20 with
   LiveFillField — the picker owns the fetching now, and this file only consumes the
   packs the callback hands back. `Pack` came back the same day: `onPacks` carries
   them up to the Studio so a pick outlives the click that made it. */
import { itemsForLevel, rowsForLevel, type Pack } from "../services/dynamicFill";
import { bakeOverlay, type OverlaySpec } from "../services/overlay";
import { skyExtendEnabled } from "../services/skyExtend";
import { ovActive } from "../services/heroOverlay";
import { applySkyBase, isBake, rebake, slotKey, startSession, type Slot } from "../services/overlaySession";
import { openOverlayEditor, __OVPRESETS, type OvModel } from "../vendor/ovSky";
import "../vendor/ovSkyBadges.css";   /* the Included-badge styles the editor's injected CSS never carried */
import { capOf, capState, clamp, echCntTxt, echPct, limitMessage, type Guardrails, type GuardrailField } from "../services/guardrails";
import { checkPattern, normalizeUrl, urlSplit, urlJoin } from "../services/echPattern";
import { scrubNote, scrubTracking } from "../services/urlScrub";
import { ctaLinkWarning, urlLabelPatch, urlLabelRelease } from "../services/echCtaLink";
import { Dims, View, ZOOM_MAX, bynderBase, clampView, dimsKey, isBynderTransform, parseDims, viewFromUrl, viewUrl, zoomOf, zoomTo } from "../services/bynderCrop";
import { gifFrameCount, gifNote, isGifAsset } from "../services/bynderGif";
import { HERO_GIF_DEFAULT_DELAY, HERO_GIF_MAX, HERO_GIF_MIN, buildHeroGif, dataUrlBytes, heroGifSpecOf, heroGifSpecStr, type HeroGifSpec } from "../services/heroGif";
import { bynderImageBytes } from "../services/api";
import { patternRulesFor } from "../services/echRegistry";
import { bynAlt } from "../services/bynder";
import { cdParts, pfFmt } from "../email/helpers";
import { echAmenLib, echByName, echMeta, echNameOf, grMeta, isEch } from "../services/echRegistry";
import { CHOICE_PLACEHOLDER, echChoiceFor, echChoiceGroups, echFieldList } from "../services/echChoice";
import { imgSizeKeys, imgSizeSlot, markupImgWidth, refillForSlot } from "../services/imgSize";
import { echListStale } from "../services/echLists";
import { AMEN_MIN, amenApply, amenGroupOrder, amenPairs, amenPicked, amenToggle } from "../services/echAmen";
import AmenPicker from "./AmenPicker";
import SpellHint from "./SpellHint";
import SpellField from "./SpellField";
import { echBreaksAllowed, echBulletsAllowed } from "../services/echBreaks";
import { copyLink, urlOk, urlTail } from "../services/echUrl";
import { echFeaturedMax } from "../services/echTemplates";
import { oneLineOverflow } from "../services/textFit";
import { echLockSentence, echMostlyLocked, echRefRows, echEffectiveLink, type EchRefRow } from "../services/echLock";
import { contactDerives, contactFields, contactButtons, contactExtraFields } from "../services/echContact";
/* echImg's dimension chip + fit warning were removed from this panel 2026-08-26 —
   see the note above ImgCard. `dropEmptyImage` lives there too and is used by the
   canvas, not here. */
import { ROW_FIELDS, hasHomeRows, rowCollections, rowOf, rowSummary, rowClearPatch, rowAddColl, rowDropColl, rowRenameColl, EXTRA_COLLS, type ExtraColl, type RowCollection } from "../services/echRows";
import { linkProblem, requiredBlanks, urlBlank } from "../services/echRequired";
import { feedDirect } from "../services/echFeeds";
import { homeUrlLookup } from "../services/echAutoLink";
import { socKeys, socNote, socPickerOn, socRows, socTogglePatch } from "../services/echSoc";
import { echSwapFor, echSwapTo } from "../services/echSwap";
import { m2LookGroups, m2SwapFor } from "../services/m2Blocks";
import { echSameSection, echSectionLabels, echSectionSwap, echSectionMemory, echReplaceSection, echPinShown, echSectionLevel, ECH_LBL_SET, type SectionCard } from "../services/echSection";
import { isListingTable, layTableReady, layCardsReady, layToTable, layToCards } from "../services/echLayout";
import { sheetHomesFile, type SheetGroup } from "../services/echSheet";
import DynLiveFill from "./DynLiveFill";
/* ── AddById REMOVED (2026-08-22) ─────────────────────────────────────────────
   It was a paste-a-community-ID / paste-homesite-IDs box, built 2026-08-21 while
   the picker could only be reached by walking Communities → Floorplans → Homesites.
   The direct-jump search replaced the need: the wizard opens on the level with the
   whole division in it and the search box matches a homesite number outright
   (`groupedCards`, echDirect.ts), so pasting an ID and typing it now do the same
   job — and one of them is the control the requester is already looking at. Jack,
   2026-08-22: "fine delete add by after you confirm it's not needed."
   The file is deleted rather than left unrendered: dead UI this codebase removes
   on sight, and its wire-back comment would have outlived the reason for it. */
import {
  ECH_CTA_PAIRS, ctaUrlFor, echDynFor, echDynOn, echDynOnly, echManualOnly, echAlwaysDyn, echFixed, echTitleChoices, echDynPatch, echDynVal, echIsSeed, echSeedHint, echClaimPatch,
  echBlockLevel, echSrcOf, echSrcOk, socIsOff, socOffList, echCommInfo,
  echPickPatch, echPacks, EMPTY_CTX, ECH_PLAN_PICK, ECH_HOME_PICK,
  /* THE FEATURE LEVEL (2026-08-20). `echFeatOf` reads the level the block is at,
     `echFeatPickKind` says which pick that level consumes — both
     all three answer out of `ECH_FEAT`, the ONE table, so this file never restates
     "at level X, field Y comes from source Z". */
  echFeatOf, echFeatPickKind,
  echCutListing, ECH_HTML_BASE, ECH_ROW_SPLIT,
  ECH_ISC_RE, ECH_ISC_NO_PHOTOS, echIscNoPhotos, echIscHasPhotos, echDataSource, echContactChoiceState,
  type DynBlock, type EchCtx,
} from "../services/echDyn";
/* the wizard's index-shaped keys → the STABLE per-block pick an ECH block stores.
   The translation lives in echDynPick (which already owns the picker's decisions)
   so nothing here has to know the key format. */
import { CU_BTN2, CU_LABEL1, CU_LABEL2, CU_URL2, CU_SWAP, type CuState } from "../services/echContactChoices";
import { contactLinkProblem } from "../services/echRequired";
import { planPickKey, planPickList, homePickList, homePickMerge, homePickFromRows, pickKeysBack, planPickMerge } from "../services/echDynPick";
import { echMintCards, CARD_CAP, type CardMint } from "../services/echCardMint";

/* the Email Refresh fields a never-edited hero opens on (ovBoxH stays the slot's frame) */
const FRESH_FIELDS = ["ovKicker", "ovHead", "ovSub", "ovCol", "ovAlign", "ovV", "ovSize", "ovScrim", "ovFrame", "ovSky", "ovFade", "ovTop"];

type SetData = (patch: Record<string, unknown>) => void;

/** what the Bynder picker hands back (BynderAsset — alt is optional server-side) */
export interface BynderPickAsset { name?: string; alt?: string; /** the asset's own pixels (2026-09-15, for the zoom + drag view) */ w?: number; h?: number; /** Bynder's file extension — "gif" marks an animated pick (2026-09-16) */ ext?: string }
/** `need` is the SLOT's declared pixels — the picker asks Bynder's transform for
 *  exactly that size instead of scaling one generic file (2026-08-22) */
/** `multi` (2026-09-16): open the picker in multi-select mode — `apply` is then unused and the set comes back through `multi.onDone` */
export interface BynderMultiOpen { initial: string[]; max: number; title?: string; hint?: string; onDone: (urls: string[], assets?: BynderPickAsset[]) => void }
export type OpenBynder = (apply: (url: string, asset: BynderPickAsset) => void, need?: { w?: number; h?: number }, multi?: BynderMultiOpen) => void;

interface Props {
  /** field keys the Done gate flagged as required-blank on THIS block (Jack
      2026-08-21) — the field card wears the cherry ring while its key is here */
  reqKeys?: Set<string>;
  block: AnyBlockInstance;
  onData: SetData;
  /** open the Bynder picker; the pick lands wherever `apply` routes it */
  onOpenBynder: OpenBynder;
  /** the block's SFMC guardrails, when it has any (services/sfmcBlocks.ts) */
  gr?: Guardrails | null;
  /** "Delete this block" (monolith .esdelf) */
  onDelete?: () => void;
  /** why deleting this block is refused (a pinned block, Layne 2026-09-28) — the button
   *  reads as off with this as its tooltip; the click still goes to onDelete, which refuses */
  deleteWhy?: string;
  /**
   * Step 1's scope, which is what every dynamic ECH field resolves against, plus
   * the whole document — a `plan.self.*` source has to know WHICH Featured block is
   * asking (`echPlanIdx`). Optional: with no ctx the panel cannot resolve a feed
   * value, so every field falls back to the plain editable control rather than
   * rendering an empty read-only box.
   */
  echCtx?: EchCtx | null;
  doc?: DynBlock[];
  /**
   * The red flash pill (monolith `__grFlash`, index.html L12991–12998). Fires
   * only when a write was actually TRUNCATED, and repeats the exact limit
   * wording the panel counter uses — "Limit reached — 2 lines max" (Lauren,
   * 2026-08-04). The pill itself lives in StudioView because it is `position:
   * fixed` over the whole Studio, not inside the panel.
   */
  onFlash?: (msg: string) => void;
  /**
   * The Studio's own bottom-centre toast (monolith `toast()`). The amenity picker
   * is the only caller: refusing a click that would drop below AMEN_MIN has to say
   * WHY, and it is a nudge rather than a guardrail breach, so it is not the red
   * `grflash` pill.
   */
  onToast?: (msg: string) => void;
  /**
   * Replace this block with its SFMC sibling, in place (audit row 16). The
   * monolith mutates `m.type`/`m.data` directly; React state lives in StudioView,
   * so the swap is handed up. Absent = no swap strip, which is what the template
   * inspector and every test that doesn't care about CTAs gets.
   */
  onSwap?: (type: string, data: Record<string, unknown>) => void;
  /**
   * Rewrite SEVERAL blocks in one document write, ids kept — the Featured section
   * moving as one (2026-09-18, Jack: "buttons 1 or 2 should affect all not just one").
   * Absent = the swap and the label fall back to the selected block alone.
   */
  onSwapMany?: (list: { id: string; type: string; data: Record<string, unknown> }[]) => void;
  /** Milestone 2 test bed (?m2=1): block FAMILIES get the variant strip too (m2Blocks.M2_FAMILIES) */
  m2?: boolean;
  /**
   * Insert ANOTHER block of this same type directly below this one and select it —
   * the Featured card's "+ Add a homesite/collection/floorplan" (2026-08-21, Jack:
   * "i should be able to add up to 5"). Layne's block renders ONE card, so more
   * cards = more instances of her block, which is SFMC-legal repetition; the cap
   * is enforced where the count lives, in the Inspector.
   */
  onAddBelow?: () => void;
  /** insert a SPECIFIC block with seed data below this one — the layout toggle's
      restore path (table → cards brings the stashed card set back verbatim) */
  onInsertBelow?: (type: string, extra: Record<string, unknown>) => void;
  /**
   * Replace the whole Featured section with `cards` in ONE document write (2026-09-25):
   * the blocks in `ids` go, `cards` go in at the first one's position. A card with an `id`
   * from `ids` keeps it; one with an empty id is a new block. Absent = the picker keeps its
   * older "fill this card, add the rest below" behaviour.
   */
  onReplaceSection?: (ids: string[], cards: { id: string; type: string; data: Record<string, unknown> }[]) => void;
  /** select a different block FOR EDITING (the plan cards' Edit details on a
      sibling card) — the Studio also flips that block to Manual, because Edit
      details must land in the editor, never on a Choose pill (2026-08-21, Jack:
      "i have to double click… should not do that") */
  onSelectBlock?: (id: string) => void;
  /** delete a specific block (a plan card's ✕) */
  onDeleteBlock?: (id: string) => void;
  /** move a block to a document index (the plan cards' drag reorder) */
  onMoveBlock?: (fromId: string, toIdx: number) => void;
  /**
   * THE PACKS THE DYNAMIC WIZARD LOADED, HANDED UP TO THE STUDIO (2026-08-20).
   *
   * A pick is stored as a community CODE on the block, and since Step 1 is no longer
   * a precondition for picking, that code can name a community `useEchDyn` has never
   * fetched. The Studio keeps these as `EchCtx.pickPacks` so every LATER fill — the
   * next `echFillAll` pass, a Step-1 change, re-selecting the block — can still
   * resolve the pick. Absent = the picker still works, it just cannot outlive the
   * click, which is why StudioView always passes it.
   */
  onPacks?: (packs: Pack[]) => void;
}

const cx = (v: unknown) => String(v ?? "");

/* Registry labels are byte-equal to the monolith's `M` defs, which carry HTML
   entities (e.g. listing's "Homes &amp; collections") because the monolith
   injects them via innerHTML. React renders labels as TEXT, so decode the
   entities at display time — display-only, never touches render output. */
const unent = (s: unknown) =>
  cx(s)
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&rsquo;/g, "’")
    .replace(/&amp;/g, "&");

/* monolith option tables (__INSET_OPTS / __THEME_OPTS / __OVOPTS) */
const INSET_OPTS: [string, string][] = [["full", "Full width"], ["20", "20px inset"], ["50", "50px inset"]];
const THEME_OPTS: [string, string][] = [["midnight", "Midnight Blue"], ["cobalt", "Cobalt Blue"], ["white", "White"]];
const OVOPTS: [string, string][] = [["none", "None"], ["fireworks", "Fireworks"], ["confetti", "Confetti"], ["sparkle", "Sparkle"], ["snow", "Snow"]];

/* email-client compatibility flags (monolith COMPAT, 2026-08-04) — one quiet amber
   line for modules that genuinely degrade in certain clients. */
const COMPAT: Record<string, { risk: "low" | "med"; note: string }> = {
  scrollbanner: { risk: "med", note: "Outlook on Windows shows only the FIRST FRAME of the GIF — make that frame read on its own." },
  priceflip: { risk: "med", note: "Outlook on Windows shows only the first frame — it will land on the “from” price, not the flip." },
  scratchreveal: { risk: "med", note: "Outlook on Windows shows only the first frame — the offer stays covered. Consider revealing it in the first frame." },
  splitflap: { risk: "med", note: "Outlook on Windows shows only the first frame of the animation." },
  countdown: { risk: "med", note: "Live mode re-draws at open, but Apple Mail Privacy Protection and Gmail pre-fetch and cache images — the clock can freeze near the pre-fetch time. Baked-GIF mode shows one static frame in Outlook." },
  livevideo: { risk: "low", note: "True in-inbox playback isn't supported in most clients — this is a poster image that clicks out to the video (the standard, reliable approach)." },
  livemeter: { risk: "low", note: "Outlook on Windows squares off the rounded bar ends. Everything else renders as designed." },
  livecoupon: { risk: "low", note: "Outlook on Windows squares off the rounded button. The dashed code box renders fine." },
  livepoll: { risk: "low", note: "Outlook on Windows squares off the rounded option buttons — still fully clickable." },
  carousel: { risk: "med", note: "Outlook on Windows shows only the first slide." },
};

/* live character/line counter under a governed field — monolith __grCounterHtml
   (index.html L13027–13041).

   ALWAYS SHOWS THE COUNT (2026-08-14). This used to render an EMPTY div while the
   state was "ok" — the limit was invisible until you were past 90% of it, so a
   division never learned a cap existed until they were nearly over it. `.grc.ok`
   is display:none in CSS (monolith L780) and the ECH label-row `.ech-cnt` is what
   surfaces the cap from the first keystroke; this keeps the DOM honest either way. */
/**
 * `patRule` IS A SECOND, WIDER LOOKUP — AND IT HAS TO BE (2026-08-25).
 *
 * Layne reported every pattern silently doing nothing, on `url`, on `number`, and on text
 * fields alike. She was right, and the cause is that `rule` here comes from `gr.fields`, which
 * is `echLimits` — a map deliberately filtered down to **only fields that declare a cap AND
 * are not image/url/number/tel/select**, because that is the correct set to CLAMP.
 *
 * `checkPattern(value, undefined)` returns PASS, so every field outside that narrow set could
 * never fail a format check no matter what it declared. Measured against the live folder:
 * 30 url + 36 number + 2 tel patterns — **68 of 88** — plus any uncapped text field, were
 * unreachable. The reference doc I wrote said the opposite; it was wrong, and it was wrong
 * because I read `checkPattern` and the render site without checking what `rule` actually
 * held at the call.
 *
 * The two lookups stay SEPARATE rather than widening `echLimits`: capping a url or a number is
 * still the wrong behaviour (that exemption is deliberate and predates this), while checking
 * its FORMAT is exactly right. Same reason a select is patternable in principle but not
 * cappable. `patRule` reads the block's full DECLARED map; `rule` keeps clamping the narrow one.
 */
/**
 * The grey provenance line under a fed field — and NOTHING when there is nothing to say
 * (2026-08-27).
 *
 * Removing the "Pick a market on Step 1 to fill this." family left several call sites
 * rendering an empty `<div class="ech-dyns">`, which is not invisible: it carries the
 * line's own top margin and line-height, so every empty fed field would keep a blank
 * grey gap where the sentence used to be. Rendering null is the difference between
 * deleting the copy and hiding it.
 */
function Dyns({ children }: { children?: React.ReactNode }) {
  const txt = typeof children === "string" ? children.trim() : children;
  if (!txt) return null;
  return <div className="ech-dyns">{txt}</div>;
}

/**
 * A url control whose scheme cannot be typed over (2026-08-27).
 *
 * Jack: "can you lock this in place? like so you cant even delete it? and it's just a
 * faded text." The `https://` is rendered as a faded, `user-select:none` span OUTSIDE
 * the input, so there is nothing there to backspace into — the input holds only what
 * follows the scheme, and `urlJoin` puts it back on the way to the block.
 *
 * A value that is not an https URL (mailto:, tel:, http://, a relative path, an
 * AMPscript or merge token) falls back to the plain full box: hiding a prefix that is
 * not there would make the real value unreadable. `urlSplit` makes that call.
 */
function UrlBox({ value, onChange, onBlur, rows, cls, max, ph }: {
  value: string; onChange: (v: string) => void; onBlur?: (v: string) => void;
  rows?: number; cls?: string; max?: number; ph?: string;
}) {
  const { affix, rest } = urlSplit(value);
  /* ── A LINK COPIED OUT OF AN OLD EMAIL LOSES THAT EMAIL'S TAGS (2026-09-22) ─────────
     Justin, FNO 68759: a requester copied the View Community link out of the 9/11 eblast,
     so the new email carried the old campaign's utm tags and one recipient's subscriber
     key. Jack: "can the app just scrub that link … in the app itself?" — every value
     that enters this box goes through `scrubTracking`; the tracking parameters go, the
     page link stays, and one grey line under the box says so. A value with nothing to
     strip is passed through byte-for-byte, so typing is never fought. */
  const [scrubbed, setScrubbed] = useState<string[]>([]);
  const clean = (v: string): string => {
    const r = scrubTracking(v);
    if (r.removed.length) setScrubbed(r.removed);
    else if (scrubbed.length && !v) setScrubbed([]);
    return r.url;
  };
  const note = scrubbed.length ? <div className="urlscrub" data-testid="url-scrubbed">{scrubNote(scrubbed)}</div> : null;
  /* the placeholder is the block's own example link, and it is shown WITHOUT the scheme
     when the affix is drawn — the box already says https:// to its left (2026-08-27) */
  const common = { ...(max ? { maxLength: max } : {}), ...(ph ? { placeholder: ph } : {}) };
  if (!affix) {
    return (
      <>
        {rows && rows > 1
          ? <textarea className={cls} rows={rows} value={value} {...common}
            onChange={(e) => onChange(clean(e.target.value))} onBlur={(e) => onBlur && onBlur(clean(e.target.value))} />
          : <input className={cls} value={value} {...common}
            onChange={(e) => onChange(clean(e.target.value))} onBlur={(e) => onBlur && onBlur(clean(e.target.value))} />}
        {note}
      </>
    );
  }
  return (
    <>
      <div className="urlbox" data-urlbox>
        <span className="urlbox-p" aria-hidden="true">https://</span>
        <input className={cls ? cls + " urlbox-in" : "urlbox-in"} value={rest} {...common}
          placeholder={ph ? String(ph).replace(/^https?:\/*/i, "") : undefined}
          onChange={(e) => onChange(clean(urlJoin(e.target.value)))}
          onBlur={(e) => onBlur && onBlur(clean(urlJoin(e.target.value)))} />
      </div>
      {note}
    </>
  );
}

/**
 * A link that cannot work, said under the box it was typed into (2026-08-27).
 *
 * OCH, via Layne's Review Board: "no validation on URLs, could include commas & pass."
 * `urlProblem` is the same function the Done gate uses, so the panel and the gate can
 * never disagree about whether a link is usable — and it stays silent on everything it
 * is not certain about (long URLs, tracking parameters, non-Lennar domains, mailto:,
 * relative paths), because a false alarm here trains people to ignore the real one.
 */
function UrlWarn({ v }: { v: string }) {
  /* linkProblem = urlProblem + "a Bynder address is not a destination" (FNO 68759, 2026-09-24) */
  const p = linkProblem(v);
  if (!p) return null;
  return <div className="urlbad" data-testid="url-bad">This link {p} — fix it before sending</div>;
}

/* ── CONTACT US: WEB PAGE OR EMAIL, AS A CHOICE (mock, 2026-09-22) ─────────────────────
   Layne's ask (ASK-JACK-INPUT-TRANSFORM-20260922): a requester types a subject line and
   the Hub writes `mailto:sales@lennar.com?subject=Hello%20There` — the block cannot do
   that itself, because it cannot tell a mailto from an https. Jack: "it should be an
   option or toggle". So the Contact Us link field carries a Web page | Email switch:
   Web page is the plain URL box; Email is two boxes, address + subject, and the value
   the block receives is composed here, encoded, with no dangling `?subject=` when the
   subject is blank. The stored value stays ONE string in `ContactUrl`, so the block,
   its pattern (which already accepts `?subject=…`), the Done gate and the payload are
   untouched. Switching to Web page hands the field back (the Step 1 link returns). */
const CONTACT_EMAIL_BLOCK = /^ECH_ContactUs/i;
function splitMailto(v: string): { addr: string; subject: string } {
  const m = /^mailto:([^?]*)(?:\?(.*))?$/i.exec(String(v || "").trim());
  if (!m) return { addr: "", subject: "" };
  let subject = "";
  for (const kv of String(m[2] || "").split("&")) {
    const [k, val = ""] = kv.split("=");
    if (k.toLowerCase() === "subject") { try { subject = decodeURIComponent(val.replace(/\+/g, " ")); } catch { subject = val; } }
  }
  return { addr: m[1] || "", subject };
}
function joinMailto(addr: string, subject: string): string {
  const a = String(addr || "").trim();
  const s = String(subject || "").trim();
  return "mailto:" + a + (s ? "?subject=" + encodeURIComponent(s) : "");
}
/* the subject a fresh email link starts with (Jack, 2026-09-22: "a standard auto fill with
   the ability for them to edit it") — the community's name when Step 1 has one */
function defaultSubject(comm: string): string {
  const c = String(comm || "").trim();
  return c ? "New Home Inquiry \u2013 " + c : "New Home Inquiry";
}
function ContactLinkKind({ value, onChange, onWeb, bar, seedSubject, children }: {
  value: string; onChange: (v: string) => void; onWeb: () => void; bar: React.ReactNode; seedSubject: string; children: React.ReactNode;
}) {
  /* THE DEFAULT VIEW IS THE PANEL AS IT IS TODAY (Jack, 2026-09-22, on the QA screenshot:
     "should be like this for the main/default view") — URL box, Copy link / Open in a new
     tab, nothing else. The email option is one quiet line under that row; choosing it
     swaps the box for address + subject, with the way back on the same line. */
  const isMail = /^mailto:/i.test(String(value || "").trim());
  const { addr, subject } = splitMailto(value);
  if (!isMail) {
    return (
      <>
        {children}
        {bar}
        <button type="button" className="echlinkkind" data-echlinkkind-mail
          onClick={() => onChange(joinMailto("", seedSubject))}>Use an email address instead ›</button>
      </>
    );
  }
  return (
    <>
      <div className="echmailto" data-echmailto>
        <label className="echmailto-l">Email address</label>
        <input className="ech-url" data-echmailaddr value={addr} placeholder="sales@lennar.com"
          onChange={(e) => onChange(joinMailto(e.target.value, subject))} />
        <label className="echmailto-l">Subject line <span className="echmailto-c">{subject.length}/70</span></label>
        <input className="ech-url" data-echmailsubj value={subject} maxLength={70} placeholder="Hello There"
          onChange={(e) => onChange(joinMailto(addr, e.target.value))} />
      </div>
      {bar}
      <button type="button" className="echlinkkind" data-echlinkkind-web
        onClick={onWeb}>Use a web page instead ›</button>
    </>
  );
}

/**
 * `fed` — THE VALUE CAME FROM STEP 1, NOT FROM THEM (2026-09-01, Jack's option C).
 *
 * A community record holding `lennar.com/x`, or an inventory price arriving as
 * `399990`, breaks the block's format rule through no fault of the person looking at
 * the panel. Two answers were rejected: saying nothing (the email still ships wrong,
 * and nobody finds out until the send report), and saying it exactly as if they had
 * typed it (an error they cannot act on is how people learn to ignore the warnings
 * that matter). So the message stays and gains one grey line naming the source, which
 * turns an accusation into something actionable — type over it, or send as is.
 */
function Grc({ value, rule, patRule, fed }: { value: string; rule?: GuardrailField | null; patRule?: GuardrailField | null; fed?: boolean }) {
  /* ── A FAILED FORMAT RULE RIDES THIS SAME LINE (2026-08-24) ──────────────────
     The block's `patternMessage` needs somewhere to appear, and this note already
     sits under every field — so a declared format costs the panel no new element
     and no layout change, which is the constraint Jack set: guardrails may tighten
     what a field accepts, they may not restyle the requester's panel.

     It takes precedence over the character count on purpose. Both can be true at
     once ("47/100" and "use sentence case"), and the count is the one visible from
     the first keystroke in the label row anyway — repeating it here while
     suppressing the actual problem would bury the only new information.

     `.over` is reused rather than given its own colour: to the requester this is
     the same class of thing as blowing the cap — something to fix before sending —
     and inventing a second warning colour for it would say otherwise. */
  const pat = checkPattern(value, patRule ?? rule);
  const cap = capOf(rule);
  if (!pat.ok) {
    const txt = cap ? " · " + value.length + "/" + cap : "";
    /* ── `patbad` EXISTS SO THE MESSAGE CAN BE SEEN AT ALL (2026-08-27) ──────────
       Layne, third report in a row: "no `pattern` produces a warning on any field
       type… `max` and `cpl` warnings render on the same fields in the same panel."
       She was right all three times, and after the 08-26 fix reached `checkPattern`
       she was STILL right — for a different reason, which is why a retest looked
       like the same bug.

       `.es .esinsp .essec.echf .grc { display: none }` (creative-studio.css) hides
       this line on every ECH field. That rule is correct for what it was written
       for: the character COUNT moved to the label row, so repeating it underneath
       was saying it twice. A pattern message was never in the label row — it has
       nowhere else to appear — so the same rule silenced it. Measured in jsdom
       against the real stylesheet: display `none` on an `.echf` field, `block` on a
       repo field, which is exactly the split Layne kept seeing.

       `patbad` un-hides only the pattern case. The count stays hidden. */
    return (
      <>
        <div className="grc over patbad">{pat.message}{txt}</div>
        {fed && <div className="ech-dyns">Filled from Step 1. Type over it, or send as is.</div>}
      </>
    );
  }
  if (!cap) return null;
  const st = capState(value, rule);
  const txt = value.length + "/" + cap;
  const msg = st === "over" ? limitMessage(rule) : st === "near" ? cap - value.length + " characters left" : "";
  return <div className={"grc " + st}>{st === "ok" ? txt : msg + " · " + txt}</div>;
}

/* one .essec inside the inspcard: centered small-caps head + body.
   `cls` carries the monolith's `.echf` marker so the ECH typography and the
   read-only/URL treatments apply to SFMC fields only (index.html L805–819).
   `cnt` is the ECH count that RIDES THE LABEL ROW (monolith L13461) — the head
   goes left, the count hard right, which is why `.essec.echf .essech` flips to
   space-between/left in CSS. */
/* ── CONTACT US BUTTONS (2026-09-25, Jack: "the cta options and 2nd button") ─────────
   The record-filled block's buttons, as the requester can change them: the first
   button's wording (its link is the Contact Us URL box above), a second button to add,
   edit or remove, and which of the two goes first. Wording comes from the approved
   list, the same select every other button uses. Every choice is stored on the block
   and applied over the division's row by `echContactChoices`; clearing one puts the
   division's value back. */
function ContactButtons({ st, d, onData }: { st: CuState; d: Record<string, unknown>; onData: (p: Record<string, unknown>) => void }) {
  const S = (v: unknown) => String(v ?? "");
  const pick = (value: string, onPick: (v: string) => void, k: string) => (
    <select className="ech-choice" data-k={k} value={value} onChange={(e) => onPick(e.target.value)}>
      {!value && <option value="" disabled>{CHOICE_PLACEHOLDER}</option>}
      {echChoiceGroups(value, 20).map((g) => (
        <optgroup key={g.label} label={g.label}>
          {g.opts.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.text}</option>)}
        </optgroup>
      ))}
    </select>
  );
  const u2 = S(d[CU_URL2]) || (st.b2 ? st.b2.url : "");
  const u2bad = st.b2 && u2 ? contactLinkProblem(u2) : "";
  return (
    <div className="inspcard" data-echcubtns>
      <Sec head="Button 1 label" cls="echf">
        {pick(st.b1.label, (v) => onData({ [CU_LABEL1]: v }), CU_LABEL1)}
      </Sec>
      {st.b2 ? (
        <>
          <Sec head="Button 2 label" cls="echf">
            {pick(st.b2.label, (v) => onData({ [CU_LABEL2]: v }), CU_LABEL2)}
          </Sec>
          <Sec head="Button 2 link" cls="echf">
            <input className="ech-url" data-k={CU_URL2} value={u2} placeholder="https://, tel: or mailto:"
              onChange={(e) => onData({ [CU_URL2]: e.target.value })} />
            {u2bad && <div className="urlbad" data-testid="url-bad">This link {u2bad} — fix it before sending</div>}
          </Sec>
          <div className="dynseg" data-echcuorder style={{ margin: "0 0 10px" }}>
            <button type="button" className={st.swapped ? "" : "on"} onClick={() => onData({ [CU_SWAP]: "" })}>{st.b1.label || "Button 1"} first</button>
            <button type="button" className={st.swapped ? "on" : ""} onClick={() => onData({ [CU_SWAP]: "1" })}>{st.b2.label || "Button 2"} first</button>
          </div>
          <button type="button" className="echlinkkind" data-echcuremove
            onClick={() => onData({ [CU_BTN2]: "off", [CU_SWAP]: "" })}>Remove the second button</button>
        </>
      ) : (
        <button type="button" className="echlinkkind" data-echcuadd
          onClick={() => onData({ [CU_BTN2]: st.b2FromRow ? "" : "add" })}>Add a second button ›</button>
      )}
    </div>
  );
}

function Sec({ head, children, cls, cnt }: { head: string; children: React.ReactNode; cls?: string; cnt?: React.ReactNode }) {
  return (
    /* `nohd` — an ECH image field has NO label, because the photo IS the label
       (monolith L13459 `if(_echf&&f.type==="image")head=""`, and the `.essec.nohd`
       rule at L14957 that hides the header row and drops the card's padding). */
    <div className={"essec open" + (cls ? " " + cls : "") + (head ? "" : " nohd")}>
      <div className="essech">{unent(head)}{cnt}</div>
      <div className="essecb">{children}</div>
    </div>
  );
}

/* ── COPY LINK + OPEN IN A NEW TAB (audit row 6, monolith L12900–12971) ────────
   The pair a URL control carries underneath it. Two things are load-bearing and
   easy to lose in a React rewrite:

     · Open is a real <a target="_blank" rel="noopener noreferrer">, never
       window.open — a popup call from inside the CloudPage frame gets blocked,
       an anchor click cannot be.
     · the href re-syncs on EVERY keystroke and both actions go dim when the
       value isn't a usable link. Here that falls out of the value being
       controlled state; in the monolith it is an explicit `__urlBarSync` on the
       control's `input` event.

   Copy uses navigator.clipboard with an execCommand fallback (the prototype runs
   on file://, where clipboard is unavailable) and flips the label to "Copied ✓"
   for ~1.4s. `mini` is the tight icon pair for a dense row. */
function UrlBar({ value, mini }: { value: string; mini?: boolean }) {
  const [copied, setCopied] = useState(false);
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (t.current) clearTimeout(t.current); }, []);
  const v = String(value || "").trim();
  const ok = urlOk(v);
  return (
    <div className={"ech-ua" + (mini ? " mini" : "") + (ok ? "" : " off")}>
      <button
        type="button"
        className={"ech-uab" + (copied ? " ok" : "")}
        data-urlcopy
        disabled={!ok}
        title="Copy this link"
        onClick={(e) => {
          e.preventDefault(); e.stopPropagation();
          copyLink(v).then((done) => {
            if (!done) return;
            setCopied(true);
            if (t.current) clearTimeout(t.current);
            t.current = setTimeout(() => setCopied(false), 1400);
          });
        }}
      >
        {copied ? "Copied ✓" : mini ? "⧉" : "Copy link"}
      </button>
      {/* href is OMITTED, not emptied, when the value isn't usable — an <a> with
          no href is inert and unfocusable, which is the dim state we want */}
      <a
        className="ech-uab ech-uao"
        data-urlopen
        target="_blank"
        rel="noopener noreferrer"
        title="Open in a new tab"
        {...(ok ? { href: v } : {})}
        /* the section header toggles collapse on click — a link inside it must
           not fold the field (monolith L12968–12971) */
        onClick={(e) => e.stopPropagation()}
      >
        {mini ? "↗" : "Open in a new tab ↗"}
      </a>
    </div>
  );
}

/* ── READ-ONLY REFERENCE ROWS ON A LOCKED BLOCK (audit row 7, monolith
   L13558–13571) ──────────────────────────────────────────────────────────────
   Option B, after the first version "looked awful": a tiny label stacked ABOVE
   the value, PLAIN TEXT not monospace, and the meaningful part of the URL
   bolded. A big grey key beside a monospace URL made the label shout and wrapped
   the link into four ragged lines.

   Copy/Open go on the `Links to` rows ONLY — the Image row is telling you which
   asset the block uses, not offering somewhere to go. */
function RefRows({ rows }: { rows: EchRefRow[] }) {
  if (!rows.length) return null;
  return (
    <div className="ech-ref">
      {rows.map((r, i) => {
        const tail = urlTail(r.url);
        return (
          <div className="ech-refr" key={i}>
            <span className="ech-refk">
              {r.key}
              {r.dim ? <> <span className="ech-refdim">{r.dim}</span></> : null}
            </span>
            <span className="ech-refv">{tail.text}{tail.bold ? <b>{tail.bold}</b> : null}</span>
            {r.link && <UrlBar value={r.url} />}
          </div>
        );
      })}
    </div>
  );
}

/* The note under a dynamic ECH field, keyed to Step 1's Initiative Request Focus —
   the level is never a second control in the panel, it IS the focus (monolith
   `_from` / `_wait`, index.html L13355–13364). */
const ECH_FROM: Record<string, string> = {
  division: "From the division on Step 1.",
  market: "From the market on Step 1.",
  aoi: "From the area of interest on Step 1.",
  community: "From the community on Step 1.",
};
/* ── THE "GO BACK TO STEP 1" LINES ARE GONE (2026-08-27) ────────────────────────
   Jack: "should also remove Pick a market on Step 1 to fill this. for all blocks as we
   combined manual and dynamic."

   They were written for a mode. While Dynamic was a state a field could be stuck in,
   an empty fed box genuinely needed explaining — the requester could not type into it,
   so the only way to fill it was to go back and answer Step 1. Now every fed field is
   an ordinary editable box that happens to fill itself, and an empty one needs no
   instruction: you type in it. Sending someone two steps back to fill a field they are
   looking at is the worse product.
   What stays is PROVENANCE, which is not an instruction: "From the community on Step 1.
   Edit freely." on a filled field, "Your text — clear it to go back…" on a claimed one,
   and the transient loading line so a fetch in flight never reads as empty. */
const ECH_WAIT: Record<string, string> = {};
/* ── AN EMPTY FEED IS NOT ALWAYS AN UNANSWERED FORM (Jack, 2026-08-19) ─────────
   His Get Directions panel had "Aloravita" picked and filled Community, Address
   Line 2 ("Peoria, AZ") and Phone — and told him to "Pick a community on Step 1"
   under the one field that came back empty. He had picked one; Airtable's
   Masterplans row for Aloravita simply carries no street address (verified live
   via /refdata?div=AZH: `{city:"Peoria", state:"AZ"}`, no `address`).

   That fix distinguished "you haven't answered" from "nothing on file" so the
   sentence stopped sending him back to a step he had completed. SUPERSEDED
   2026-08-27: with the field editable, NEITHER sentence is worth a line — the empty
   box is the whole message, and `echScopeReady` is no longer read here at all. Kept
   as the record of why the distinction existed, in case a future surface needs it. */
const ECH_NONE: Record<string, string> = {};
/* the one line that survives: a feed still in flight must not read as an empty field,
   or the requester types into a box that is about to be filled underneath them */
const ECH_LOADING = "Loading from Step 1…";
/* a URL and a seed keep their own provenance lines — same rule, different wording */
/* ECH_URL_OWN retired 2026-09-11 — a claimed url now carries its hand-back as an action (see the url branch) */
/* ECH_SEED_OWN retired 2026-09-22 (#124) — a claimed seed carries "Restore auto-fill" as an action, like the url branch */
const ECH_SEED_FROM = "Approved copy from the initiative on Step 1 — edit freely.";
/* the seed's own wait line, removed with the rest (2026-08-27) */
const ECH_SEED_WAIT = "";

/* monolith seg fields render as .seg2 pill buttons */
function Seg2({ value, opts, onPick }: { value: string; opts: readonly (readonly [string, string])[]; onPick: (v: string) => void }) {
  return (
    <div className="seg2">
      {opts.map(([v, lab]) => (
        <button key={v} type="button" className={value === v ? "on" : ""} onClick={() => onPick(v)}>{lab}</button>
      ))}
    </div>
  );
}

/* generic drag-to-reorder over an index list (monolith dragReorder, grip-initiated) */
function useDragReorder(onMove: (from: number, to: number) => void) {
  const dragI = useRef<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  return {
    over,
    rowProps: (i: number) => ({
      onDragStart: (e: React.DragEvent) => { dragI.current = i; try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "x"); } catch { /* noop */ } },
      onDragEnd: () => { dragI.current = null; setOver(null); },
      onDragOver: (e: React.DragEvent) => { e.preventDefault(); setOver(i); },
      onDragLeave: () => setOver((o) => (o === i ? null : o)),
      onDrop: (e: React.DragEvent) => { e.preventDefault(); setOver(null); const from = dragI.current; if (from == null || from === i) return; onMove(from, i); dragI.current = null; },
    }),
  };
}

/* ── THE DIMENSION CHIP AND THE FIT WARNING ARE GONE (2026-08-26, Jack: "why are we
   showing the image dimensions like this — think it should be removed entirely").
   Both were ported from the monolith (`__imgDimCheck` L12785–12810, `_dim` L12822):
   a "500 × 350" chip stamped on the thumbnail, and a red "Wrong shape — this photo
   is 800 × 531, the block needs 500 × 350." line under it.

   Why removing them loses nothing: the panel's own heading already reads
   "Main Image (500×350)", so the required size is on screen either way, and the
   warning never blocked anything — SFMC scales the photo regardless. It was prose on
   a built surface, which is the standing rule it broke.

   `imgDimVerdict` / `imgDimLabel` stay in `services/echImg.ts` with their unit tests.
   They are the monolith's wording preserved, and the next place this belongs is a
   click-through, not a standing block. `dropEmptyImage` in that module is unrelated
   and still very much live. */

/* ── card-style image field (monolith esImageField, Bynder-only 2026-07-25).
   Exported: the TEMPLATE SECTION inspector (StudioView) reuses the exact card.

   `w`/`h` are the block's DECLARED image size and only ECH blocks have them
   (audit row 21). When present the card shapes its preview like the BLOCK rather
   than a generic 5:4 — otherwise a 600×350 photo sits letterboxed in the panel
   and reads as the wrong crop — stamps the locked size as a chip, and runs the
   fit check above. Legacy callers pass neither and are untouched.

   NO OVERLAY BAKING HERE, EVER. The monolith added the control and removed it
   within the hour (L13438–13445): baking draws the photo into a <canvas>, Bynder
   taints it, and every attempt failed with "Couldn't bake this photo". The repo's
   `OverlayFxSelect` is gated on `block.type === "image"`, and an ECH block's type
   is `sfmc_<contentId>`, so it cannot reach an ECH image field. ── */
export function ImgCard({ v, altV, onUrl, onAlt, openBynder, extra, w, h, nat: natIn, onNat, gif, secondary }: {
  v: string;
  altV?: string;
  onUrl: (u: string) => void;
  onAlt?: (a: string) => void;
  openBynder: () => void;
  extra?: React.ReactNode;
  w?: number;
  h?: number;
  /** the picked asset's own pixels ("2000x3000"), stored beside the photo at pick time */
  nat?: string;
  /** learned lazily for a photo picked before the size was stored */
  onNat?: (dims: string) => void;
  /** the pick is an animated GIF (2026-09-16): plain preview, no crop or zoom, a note on what
      the clients will do with it — services/bynderGif.ts */
  gif?: boolean;
  /** a second button under "Replace photo" (the hero GIF builder), stacked like the monolith's Carousel */
  secondary?: React.ReactNode;
}) {
  /* `cover` because that is how the email renders it (monolith `_ar`, L12825) */
  const ar = w && h ? { aspectRatio: `${w}/${h}`, backgroundSize: "cover" } : null;
  /* ── ZOOM + DRAG WHEN THE PHOTO'S SHAPE DISAGREES WITH THE SLOT (2026-09-15) ──────────
     Justin Bradford: "the image itself is very tall so the builder cut it … a much bigger issue
     if the image can not be adjusted inside of the builder." Jack (09-15): keep today's centre
     fill as the DEFAULT and let the requester drag to choose the cut; the frame never changes size. The view is three numbers written into
     the Bynder URL (services/bynderCrop.ts) — the same URL the canvas draws, the email ships and
     the row stores — so nothing new is persisted and nothing is left for an email client to
     interpret. The source's pixels come from the asset at pick time (`nat`); an older pick
     without them learns them from the base image once, then stores them. 2026-09-17 (Justin,
     Jack on the mock): the frame is ALWAYS full — the slider zooms IN from the fill (to 2×),
     never out to white — and every Bynder photo with a known size gets the tool. */
  const slot: Dims | null = w && h ? { w, h } : null;
  /* a GIF is never a transform URL (the picker stores the bare base) and never adjustable —
     Bynder's transform would render one frame; the frame count below is the proof */
  const bynder = !!v && !!slot && !gif && isBynderTransform(v);
  const [gifFrames, setGifFrames] = useState<number | null>(null);
  useEffect(() => {
    setGifFrames(null);
    if (!gif || !v) return;
    let live = true;
    const local = dataUrlBytes(v);   /* a GIF the Hub built itself is already here — no fetch */
    if (local) { setGifFrames(gifFrameCount(local)); return; }
    bynderImageBytes(v).then((b) => { if (live) setGifFrames(gifFrameCount(b)); }).catch(() => { /* could not check — the note stays generic */ });
    return () => { live = false; };
  }, [gif, v]);
  const [learned, setLearned] = useState<Dims | null>(null);
  /* ── THE SOURCE'S PIXELS COME FROM THE TRANSFORM, NOT THE ASSET RECORD (2026-09-17) ────────
     The picker stores Bynder's `width`/`height` for the asset — the ORIGINAL file. The transform
     endpoint does not serve the original: TPU_Hillcrest_Pic_Capri_Exterior_2of2 is 2048×3072 in the
     record and 2000×3000 out of /transform (measured, `fit,width:6000,height:6000` → 2000×3000). A
     crop written in record pixels (`crop,x:0,y:204,width:2048,…`) overruns the real source, Bynder
     drops it and serves the centre fill — so the card showed the roof while the canvas and the
     email kept the garage (Jack, QA 2026-09-17: "the adjustment tool isn't working"). The record's
     size is now only the first guess: the card always learns the transform's own size from the base
     image (one cached load), prefers it, and writes it back so the row and the next open agree. */
  const hinted = parseDims(natIn);
  const nat = learned || hinted;
  useEffect(() => {
    setLearned(null);
    if (!bynder) return;
    let live = true;
    try {
      const im = new Image();
      im.onload = () => {
        if (!live) return;
        const d = { w: im.naturalWidth, h: im.naturalHeight };
        if (!d.w || !d.h) return;
        setLearned(d);
        if (onNat && (!hinted || hinted.w !== d.w || hinted.h !== d.h)) onNat(dimsKey(d));
      };
      im.src = bynderBase(v);
    } catch { /* plain preview */ }
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bynder ? bynderBase(v) : "", natIn]);
  /* every Bynder photo with a known size is adjustable (2026-09-17): zoom-in + drag is useful
     on a photo that already matches the slot's shape, not only on one that disagrees with it */
  const adjustable = bynder && !!nat && !!slot;
  const view: View | null = adjustable && nat && slot ? viewFromUrl(v, nat, slot) : null;
  /* the frame draws at the card's width; `k` is card px per slot px */
  const frameRef = useRef<HTMLDivElement>(null);
  const [fw, setFw] = useState(0);   /* the frame's drawn width — measured, so the photo scales with the card */
  useLayoutEffect(() => {
    const el = frameRef.current; if (!el) { setFw(0); return; }
    const m = () => setFw(el.clientWidth);
    m();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(m); ro.observe(el);
    return () => ro.disconnect();
  }, [adjustable]);
  const [live, setLiveState] = useState<View | null>(null);   /* the view while a drag or slide is in progress */
  /* the same view in a ref: pointermove is a continuous event React batches, so a fast drag can
     reach pointerup before the state has re-rendered — the ref is what commit reads */
  const liveRef = useRef<View | null>(null);
  const setLive = (nv: View | null) => { liveRef.current = nv; setLiveState(nv); };
  const shown = live || view;
  const commit = (nv: View) => { if (!nat || !slot) return; setLive(null); onUrl(viewUrl(v, nat, slot, nv)); };
  const commitLive = () => { const nv = liveRef.current; if (nv) commit(nv); };
  const drag = useRef<{ x: number; y: number; v: View; k: number } | null>(null);
  const canDrag = !!(shown && nat && slot && (nat.w * shown.s > slot.w + 0.5 || nat.h * shown.s > slot.h + 0.5));
  const onDown = (e: React.PointerEvent) => {
    if (!canDrag || !shown || !frameRef.current || !slot) return;
    const kk = (frameRef.current.clientWidth || fw) / slot.w;
    if (!kk) return;
    drag.current = { x: e.clientX, y: e.clientY, v: shown, k: kk };
    try { frameRef.current.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
    e.preventDefault();
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current; if (!d || !nat || !slot || !Number.isFinite(e.clientX) || !Number.isFinite(e.clientY)) return;
    setLive(clampView({ s: d.v.s, ox: d.v.ox + (e.clientX - d.x) / d.k, oy: d.v.oy + (e.clientY - d.y) / d.k }, nat, slot));
  };
  const onUp = () => { const d = drag.current; if (!d) return; drag.current = null; commitLive(); };
  const k = fw && slot ? fw / slot.w : 0;
  return (
    <>
      <div className="imgc">
        {adjustable && shown && nat && slot ? (
          <div
            ref={frameRef}
            className={"esprev imgc-img imgc-frame" + (canDrag ? " drag" : "") + (live ? " dragging" : "")}
            style={{ aspectRatio: `${w}/${h}` }}
            data-testid="img-frame"
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
          >
            <img
              src={bynderBase(v) + "?io=transform:fit,width:" + Math.round(nat.w * Math.max(slot.w / nat.w, slot.h / nat.h) * ZOOM_MAX) + ",height:" + Math.round(nat.h * Math.max(slot.w / nat.w, slot.h / nat.h) * ZOOM_MAX)}
              alt=""
              draggable={false}
              style={k ? { width: nat.w * shown.s * k, height: nat.h * shown.s * k, left: shown.ox * k, top: shown.oy * k } : { width: "100%" }}
            />
            <div className="imgc-grid" />
            <button type="button" className="imgc-rm" title="Remove photo" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onUrl(""); if (onAlt) onAlt(""); }}>×</button>
          </div>
        ) : (
          <div
            className={"esprev imgc-img" + (v ? "" : " soon")}
            style={{ ...(ar || {}), ...(v ? { backgroundImage: `url('${v}')` } : {}) }}
            title={v ? undefined : "Select a photo from Bynder"}
            onClick={v ? undefined : openBynder}
          >
            {v ? (
              <button type="button" className="imgc-rm" title="Remove photo" onClick={(e) => { e.stopPropagation(); onUrl(""); if (onAlt) onAlt(""); }}>×</button>
            ) : (
              <span className="esprevph">
                <span className="imgc-soonh">🏠</span>
                <span className="imgc-soont">No photo yet — select from Bynder</span>
              </span>
            )}
          </div>
        )}
        {adjustable && shown && nat && slot && (
          <div className="imgc-zoom" data-testid="img-zoom">
            <span className="zi">−</span>
            <input
              type="range" min={0} max={100} aria-label="Zoom"
              value={Math.round(zoomOf(shown, nat, slot) * 100)}
              onChange={(e) => setLive(zoomTo(shown, +e.target.value / 100, nat, slot))}
              onPointerUp={commitLive}
              onKeyUp={commitLive}
              onBlur={commitLive}
            />
            <span className="zi">+</span>
          </div>
        )}
        {gif && v && (
          <div className={"imgc-gifnote" + (gifFrames === 0 || gifFrames === 1 ? " warn" : "")} data-testid="img-gifnote">
            {gifNote(gifFrames, parseDims(natIn), slot)}
          </div>
        )}
        <div className={"imgc-bd" + (secondary ? " one" : "")}>
          <button type="button" className="imgc-main" onClick={openBynder}>📁 {v ? "Replace photo" : "Select a photo"}</button>
          {secondary}
        </div>
        {onAlt != null && (
          <div className="imgc-alt" style={{ display: "none" }}>
            <input value={altV || ""} onChange={(e) => onAlt(e.target.value)} />
          </div>
        )}
        {extra}
      </div>
      {/* ── THE PICKED PHOTO'S URL IS BACK-END ONLY (2026-09-11, Jack: "think we can hide
          this image url and just be back end and ensure it's going into airtable").
          Shown for one day (2026-09-10) under the photo; reverted to the monolith's
          hidden .esurlbox. The value is still the pick's Bynder transform url, still
          persisted on the block, and it reaches the row as `Main Image Info`
          (services/echColumns.ts) — nothing about the payload changed, only the strip. */}
      <div className="esurlbox" data-imgurl>
        <div className="esurlbox-l">Image URL</div>
        <input className="mono" value={v} placeholder="Pulled from Bynder" onChange={(e) => onUrl(e.target.value)} />
      </div>
    </>
  );
}

/* ── repeat: image-forward cards + "Edit details" modal (monolith repEditor/repOpenModal) ── */
function repSpecLine(sub: readonly FieldDescriptor[], it: Record<string, unknown>): string {
  const nameK = (sub.find((s) => s.type === "text") || {}).k;
  const parts: string[] = [];
  sub.forEach((s) => {
    if (!s.k || s.k === nameK) return;
    if (s.type === "url" || /url|cta|link|button|btn/i.test(s.k)) return;
    if (s.type === "text" && it[s.k]) parts.push(String(it[s.k]));
  });
  const line = parts.join(" · ");
  return line.length > 72 ? line.slice(0, 70) + "…" : line;
}

/**
 * The Featured Plan photo slot's declared pixels, for the Bynder picker's transform
 * (2026-09-09). Read off the block's own guardrail (`PlanImage: {width:270, height:180}`
 * on both Featured variants) so the number lives in one place — the header Layne owns —
 * and a resize there follows through with no code change. Undefined when the block
 * declares none, which leaves the picker on its size-less path.
 */
export function planImgNeed(gr?: Guardrails | null, markup?: unknown): { w?: number; h?: number } | undefined {
  const f = gr && gr.fields && (gr.fields as Record<string, GuardrailField | undefined>).PlanImage;
  if (f && f.width && f.height) return { w: f.width, h: f.height };
  /* ── THE MARKUP IS THE SECOND WITNESS (2026-09-17) ──────────────────────────────────
     A block whose header declares no PlanImage size still draws the photo at a fixed size —
     Layne's `<img src="{{PlanImage}}" width="270" height="180">`. The card's adjust frame
     needs that number, so it is read off the img tag when the guardrail is silent. */
  const m = /<img\b[^>]*\{\{\s*PlanImage\s*\}\}[^>]*>/i.exec(String(markup || ""));
  if (!m) return undefined;
  const w = /\bwidth\s*=\s*["']?(\d+)/i.exec(m[0]), h = /\bheight\s*=\s*["']?(\d+)/i.exec(m[0]);
  if (!w || !h) return undefined;
  return { w: +w[1], h: +h[1] };
}

function RepSubInput({ s, val, onVal, openBynder }: { s: FieldDescriptor; val: string; onVal: (v: string) => void; openBynder: OpenBynder }) {
  if (s.type === "textarea") return <SpellField className="rep-in" value={val} onChange={onVal} />;
  if (s.type === "seg" && s.opts) {
    return (
      <div className="seg2 rep-seg">
        {s.opts.map(([v, lab]) => (
          <button key={v} type="button" className={val === v ? "on" : ""} onClick={() => onVal(v)}>{lab}</button>
        ))}
      </div>
    );
  }
  if (s.type === "image") {
    const pick = () => openBynder((url) => onVal(url));
    return (
      <>
        <div
          className={"thumb rep-imgbox" + (val ? "" : " soon")}
          title={val ? undefined : "Select a photo from Bynder"}
          style={{ backgroundImage: val ? `url('${val}')` : "none" }}
          onClick={val ? undefined : pick}
        >
          {val ? (
            <button type="button" className="rep-imgrm" title="Remove photo" onClick={(e) => { e.stopPropagation(); onVal(""); }}>×</button>
          ) : (
            <span className="rep-imgph"><span className="rep-imgph-h">🏠</span>Select a photo</span>
          )}
        </div>
        <input className="rep-in mono" value={val} placeholder="Pulled from Bynder" onChange={(e) => onVal(e.target.value)} />
        <div className="rep-imgbtns"><button type="button" className="esbynderb" onClick={pick}>📁 Select a photo</button></div>
      </>
    );
  }
  /* a repeat-item url box gets the locked scheme too — same rule as the main fields
     (2026-08-27), so a row's link cannot be typed schemeless either */
  if (s.type === "url") return <UrlBox value={val} cls="rep-in mono" onChange={onVal} />;
  return <input className="rep-in" value={val} onChange={(e) => onVal(e.target.value)} />;
}

/**
 * `onCancel` is OPTIONAL and only the homes window passes it (2026-08-20). The
 * repeater's fields write straight through on change, so for it there is nothing to
 * cancel and one Done button is honest. A home is a seven-field form Jack opens to
 * fix one number, so it gets Cancel — which restores the snapshot taken on open — and
 * closing by ✕ or the backdrop means the same thing as Cancel there, not as Done.
 */
function RepModal({ title, onClose, onCancel, wide, children }: {
  title: string; onClose: () => void; onCancel?: () => void; wide?: boolean; children: React.ReactNode;
}) {
  const dismiss = onCancel || onClose;
  return (
    <div className="repmodal-ovl on" onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}>
      <div className={"repmodal" + (wide ? " wide" : "")}>
        <div className="repmodal-hd"><b>Edit — {title}</b><button type="button" className="repmodal-x" title="Close" onClick={dismiss}>×</button></div>
        <div className="repmodal-bd">{children}</div>
        <div className="repmodal-ft">
          {onCancel && <button type="button" className="repmodal-cancel" onClick={onCancel}>Cancel</button>}
          <button type="button" className="repmodal-done" onClick={onClose}>{onCancel ? "Save" : "Done"}</button>
        </div>
      </div>
    </div>
  );
}

/* ── LAYNE'S HOMES, AS A LIST YOU CAN REORDER (2026-08-20) ─────────────────────
   Chosen off two rounds of mockups. Jack picked the Option A row ("two lines,
   price right-aligned"), then D2 for movement ("d2 but want to edit or swap
   communities or sections as well"), then "add this for now".

   FOUR THINGS THIS CHANGES, and why each one is safe on HER block:

   1. ONLY FILLED HOMES ARE ROWS. Ten `<details>` disclosures, six of them reading
      "empty — not shown in the email", was the panel telling you six times about
      something that does not exist. Unused slots collapse into ONE dashed
      + Add a home card per section, which is where Jack landed after the plus-card
      screenshot. Capacity is still visible — "4 of 5" in the header, "1 open" above
      the card — because the ceiling is real and hers, not ours.

   2. MOVEMENT IS AN INSERT, NOT A SWAP. A navy line shows where the home lands.
      Under it the data model is unchanged: her block declares ten FIXED slots
      (`home1..home10` × 7 fields), so an insert RE-PACKS the values across the
      slots a section owns. Nothing new is invented, no key is created that the
      block does not declare, and a slot past the last filled home is cleared.

   3. A SECTION CAN MOVE TOO. Sections are markup, not data — `{{collection1Name}}`
      sits at a fixed place in her HTML — so "move the section" swaps the two
      sections' NAMES and their CONTENTS. The email reorders; the block does not
      have to be re-cut. It is refused (and says so) when the other section cannot
      hold what it would receive.

   4. EDITING IS ON THE CARD (2026-08-21, Jack: "for listing table i want the
      visual to look like featured plan but without the image"). The window is
      gone — each home is a CARD in the plan-card style, minus the photo: bold
      "Homesite / Plan" line, spec line, price, all click-to-edit in place, and
      the home's View URL rides in a collapsed Link section (the same pattern as
      the plan card's Buttons & links, built on the SAME URL field element so
      the copy-link/open-in-tab machinery is untouched).

   5. A SPREADSHEET CAN FILL THE WHOLE TABLE (same day: "should also have an
      option to upload a spreadsheet for listing table manual if desired").
      CSV export from Excel/Sheets, headers matched loosely, an optional
      Collection column splits the sections — see services/echSheet.ts.        */
interface EchHomeVals { [k: string]: string }
function EchHomes({ colls, d, rowUrl, linkMiss, lookupUrl, onData, onName, onFlash, onDropColl, onAddFromInventory, blockType }: {
  colls: RowCollection[];
  /** the block's type — lets a full section BORROW a free row from a neighbour by
   *  re-cutting the markup (`echCutListing`), see `freeSlotFor` (2026-09-15) */
  blockType?: string;
  /**
   * Remove a whole collection — its heading AND its homes (2026-08-27).
   *
   * Handed down rather than built here: emptying the slots is the easy half, and the
   * other half (dropping the community from `__homePick` so the grouped fill does not
   * simply rebuild it into the freed slots) needs the packs, which only the call site
   * has. See the call site for why a claim alone is not enough.
   */
  onDropColl?: (g: RowCollection) => void;
  /**
   * "+ Add a home" opens the picker scoped to this section (2026-08-22).
   * `draft` is handed UP rather than re-derived: only this component knows which slot
   * the section has free and how to put a card in edit mode, so the picker's "or type
   * one by hand" link just calls it back.
   */
  onAddFromInventory?: (sectionName: string, open: number, draft: () => void) => void;
  d: Record<string, unknown>;
  /** the per-home URL field sections, built by the field loop — the one field
      that does NOT edit inline on the card */
  rowUrl: Record<number, React.ReactNode[]>;
  /** home numbers whose View URL the Done gate flags — computed by the SAME
      requiredBlanks the gate runs, so card and gate can never disagree */
  linkMiss: ReadonlySet<number>;
  /** AUTO-DETECT (2026-08-21, Jack: "this kind of thing should just be in
      there") — exact-match a typed homesite against live inventory; "" when
      unknown or ambiguous. Fires on its own, never a button. */
  lookupUrl?: (typed: string) => string;
  onData: (patch: Record<string, unknown>) => void;
  onName: (key: string, v: string) => void;
  onFlash?: (m: string) => void;
}) {
  /* which card value is being edited in place — "<n>:<suffix>" */
  const [inline, setInline] = useState<string | null>(null);
  /* which card's Link section is open */
  const [linkOpen, setLinkOpen] = useState<number | null>(null);
  /* empty slots the user just added — rendered as editable cards until filled */
  const [drafts, setDrafts] = useState<number[]>([]);
  /* the section banner (2026-08-21, Jack: "just have the midnight blue banner
     and can click to edit rather than always show the white background… would
     be great if this was collapsible as well") */
  const [nameEdit, setNameEdit] = useState<number | null>(null);
  const [closed, setClosed] = useState<number[]>([]);
  const fileRef = useRef<HTMLInputElement | null>(null);
  /**
   * WHAT THE POINTER IS CARRYING LIVES IN A REF, NOT IN STATE. The first `dragover`
   * can arrive in the same tick as `dragstart` — measured in the live app: the source
   * row got its `dragging` class but the target never got its line, because the
   * dragover handler's closure still saw `null`. A ref is readable immediately. State
   * is kept alongside it purely so the two classes repaint.
   */
  type DragWhat = { kind: "home"; n: number } | { kind: "coll"; gi: number } | null;
  const dragRef = useRef<DragWhat>(null);
  const [drag, setDragState] = useState<DragWhat>(null);
  const setDrag = (v: DragWhat) => { dragRef.current = v; setDragState(v); };
  const [line, setLine] = useState<string | null>(null);   /* "h:<n>:before" | "c:<gi>:after" | "pad:<gi>" | "no:<gi>" */

  const used = (n: number) => rowSummary(d, n).used;
  /** the 7 values of one home — DECLARED keys only, same guard as `rowClearPatch` */
  const readRow = (n: number): EchHomeVals => {
    const v: EchHomeVals = {};
    ROW_FIELDS.forEach((sfx) => {
      const k = "home" + n + sfx;
      if (d[k] !== undefined) v[sfx] = String(d[k] ?? "");
    });
    return v;
  };
  const filledOf = (g: RowCollection) => g.homes.filter(used);
  /** write an ordered list of homes into the slots a section owns, clearing the tail */
  const packPatch = (g: RowCollection, list: EchHomeVals[]) => {
    const patch: Record<string, string> = {};
    g.homes.forEach((n, i) => {
      const v = list[i];
      ROW_FIELDS.forEach((sfx) => {
        const k = "home" + n + sfx;
        if (d[k] === undefined) return;                    /* never invent a key */
        patch[k] = v ? String(v[sfx] ?? "") : "";
      });
    });
    return patch;
  };
  const collOf = (n: number) => colls.find((g) => g.homes.indexOf(n) > -1) || null;

  /** move home `n` so it sits at index `to` of section `tg` */
  const moveHome = (n: number, tg: RowCollection, to: number) => {
    const fg = collOf(n);
    if (!fg) return;
    const same = fg === tg;
    if (!same && filledOf(tg).length >= tg.homes.length) {
      if (onFlash) onFlash("That section is full — take a home out of it first.");
      return;
    }
    const val = readRow(n);
    const from = filledOf(fg);
    const src = from.filter((x) => x !== n).map(readRow);
    if (same) {
      let i = from.indexOf(n) < to ? to - 1 : to;
      i = Math.max(0, Math.min(i, src.length));
      src.splice(i, 0, val);
      onData(packPatch(fg, src));
      return;
    }
    const dst = filledOf(tg).map(readRow);
    dst.splice(Math.max(0, Math.min(to, dst.length)), 0, val);
    onData({ ...packPatch(fg, src), ...packPatch(tg, dst) });
  };
  /**
   * ── A FULL SECTION BORROWS A ROW THE TABLE STILL HAS (2026-09-15) ────────────────
   *
   * CHB, via Feedback, submitting "the old way" because of this: "Adding the Sienna II
   * plan under Landmark (It did not want to let us split up the collections with 4 and
   * 6 plans)". Their table had Andare 4-of-5 and Landmark 5-of-5, and the Landmark pad
   * read "All 5 slots used". Layne's own guardrail says the opposite: "The split
   * between collections is flexible. Move a home row above or below a collection row
   * to change the mix, for example 7 and 3, or 4 and 2 and 4." The 5/5 was only ever
   * how the block SHIPS (`rowCollections` reads it off the markup), and this panel had
   * been treating it as a wall.
   *
   * So: when this section's own slots are used and another section has one open, the
   * table is re-cut from its pristine markup with this section one row larger and the
   * donor one smaller (`echCutListing` with exact sizes — the same cut a pick uses),
   * and every section's homes are re-packed into the slots it now owns. The donor is
   * the section with the most open rows, nearest on a tie. Returns the slot to draft
   * into and the patch that makes it exist; null when the table is truly full.
   * Token-headed sections only — an ADDED collection (`lit`) sits at a recorded slot
   * the cut re-applies afterwards, and moving rows under it is a different job.
   */
  const liveOf = (c: RowCollection) => c.homes.filter((n) => used(n) || drafts.indexOf(n) > -1).length;
  const roomInTable = colls.reduce((a, c) => a + (c.homes.length - liveOf(c)), 0);
  const freeSlotFor = (gi: number): { n: number; patch: Record<string, unknown> } | null => {
    const g = colls[gi];
    if (!g) return null;
    const own = g.homes.find((x) => !used(x) && drafts.indexOf(x) < 0);
    if (own != null) return { n: own, patch: {} };
    if (!blockType || colls.some((c) => c.lit != null || !c.key)) return null;
    let donor = -1, best = 0;
    colls.forEach((c, i) => {
      if (i === gi) return;
      const open = c.homes.length - liveOf(c);
      if (open > best || (open === best && best > 0 && Math.abs(i - gi) < Math.abs(donor - gi))) { best = open; donor = i; }
    });
    if (donor < 0 || best < 1) return null;
    const sizes = colls.map((c, i) => c.homes.length + (i === gi ? 1 : 0) - (i === donor ? 1 : 0));
    const cut: Record<string, string> = {};
    echCutListing({ id: "", type: blockType, data: d } as unknown as DynBlock, cut, colls.length, sizes);
    if (!cut.__html) return null;
    const rows = colls.reduce<number[]>((a, c) => a.concat(c.homes), []).sort((a, b) => a - b);
    const next = rowCollections(cut.__html, rows);
    if (next.length !== colls.length || next[gi].homes.length !== sizes[gi]) return null;
    const vals = colls.map((c) => filledOf(c).map(readRow));
    /* the split is RECORDED, not just cut: the fill re-cuts from the pristine copy
       every pass and would deal the rows evenly again without it */
    const patch: Record<string, unknown> = { __html: cut.__html, [ECH_ROW_SPLIT]: sizes.join(",") };
    if (cut[ECH_HTML_BASE]) patch[ECH_HTML_BASE] = cut[ECH_HTML_BASE];
    next.forEach((c, i) => Object.assign(patch, packPatch(c, vals[i])));
    const n = next[gi].homes[vals[gi].length];
    return n == null ? null : { n, patch };
  };
  /**
   * Moving a section = swapping the two sections' names AND their homes, because
   * the ORDER lives in her markup and cannot be edited from here. Refused when the
   * receiving section is too small for what it would be handed — better to say so
   * than to silently drop the overflow.
   */
  /* the name of a section, wherever it lives — a token key or ADDED literal text */
  const litOrd = (g: RowCollection) => colls.filter((c) => c.lit != null).indexOf(g);
  const nameOf = (g: RowCollection) => (g.lit != null ? g.lit : g.key ? cx(d[g.key]) : "");
  const extraOf = () => (Array.isArray(d[EXTRA_COLLS]) ? (d[EXTRA_COLLS] as ExtraColl[]).map((e) => ({ ...e })) : []);
  /** rename a section into `patch` — token names via their key, ADDED names via
      an html rewrite (the render substitutes declared tokens only) */
  const nameInto = (g: RowCollection, v: string, patch: Record<string, unknown>) => {
    if (g.lit != null) {
      const o = litOrd(g);
      patch.__html = rowRenameColl(String(patch.__html ?? d.__html ?? ""), o, v);
      const extra = (patch[EXTRA_COLLS] as ExtraColl[]) || extraOf();
      if (extra[o]) extra[o].label = v;
      patch[EXTRA_COLLS] = extra;
    } else if (g.key) patch[g.key] = v;
  };
  const moveColl = (gi: number, dir: -1 | 1) => {
    const a = colls[gi], b = colls[gi + dir];
    if (!a || !b) return;
    const av = filledOf(a).map(readRow), bv = filledOf(b).map(readRow);
    if (av.length > b.homes.length || bv.length > a.homes.length) {
      if (onFlash) onFlash("Those sections hold different numbers of homes — they can't trade places.");
      return;
    }
    const patch: Record<string, unknown> = { ...packPatch(a, bv), ...packPatch(b, av) };
    const an = nameOf(a), bn = nameOf(b);
    nameInto(a, bn, patch);
    nameInto(b, an, patch);
    onData(patch);
  };

  /* ── the spreadsheet import ── */
  const applySheet = (gs: SheetGroup[]) => {
    const patch: Record<string, unknown> = {};
    let dropped = 0;
    if (gs.length > 1 && colls.length > 1) {
      /* the sheet's own Collection column maps group i → section i */
      colls.forEach((c, i) => {
        const src = gs[i] ? gs[i].homes.slice(0, c.homes.length) : [];
        if (gs[i]) dropped += Math.max(0, gs[i].homes.length - c.homes.length);
        Object.assign(patch, packPatch(c, src));
        if (gs[i] && gs[i].name && c.key) patch[c.key] = gs[i].name;
      });
      dropped += gs.slice(colls.length).reduce((a, g) => a + g.homes.length, 0);
    } else {
      /* one flat list fills the sections in order */
      const flat = gs.reduce<EchHomeVals[]>((a, g) => a.concat(g.homes), []);
      let at = 0;
      colls.forEach((c) => {
        Object.assign(patch, packPatch(c, flat.slice(at, at + c.homes.length)));
        at += c.homes.length;
      });
      dropped = Math.max(0, flat.length - at);
      if (gs[0] && gs[0].name && colls[0] && colls[0].key) patch[colls[0].key] = gs[0].name;
    }
    /* the import auto-detects missing links the same way typing does */
    let auto = 0;
    if (lookupUrl) {
      Object.keys(patch).forEach((hk) => {
        const mm = /^home(\d+)Homesite$/.exec(hk);
        if (!mm) return;
        const uk = "home" + mm[1] + "Url";
        if (!(uk in patch) || !urlBlank(String(patch[uk] ?? ""))) return;
        const typed = String(patch[hk] ?? "").trim();
        if (!typed) return;
        const u = lookupUrl(typed);
        if (u) { patch[uk] = u; auto++; }
      });
    }
    onData(patch);
    setDrafts([]);
    const cap = colls.reduce((a, c) => a + c.homes.length, 0);
    const autoNote = auto > 0 ? " · " + auto + " link" + (auto > 1 ? "s" : "") + " filled from inventory" : "";
    if (onFlash) onFlash((dropped > 0
      ? "Imported — " + dropped + " row" + (dropped > 1 ? "s" : "") + " didn't fit (the block holds " + cap + ")."
      : "Spreadsheet imported.") + autoNote);
  };
  const onCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    /* .xlsx and .csv both land here — sheetHomesFile routes by file type and
       lazy-loads the Excel parser only when one is actually picked */
    sheetHomesFile(f).then(({ groups, err }) => {
      if (err) { if (onFlash) onFlash(err); return; }
      applySheet(groups);
    });
  };

  /* ── drag plumbing. `line` is the only visual state; nothing moves until drop ── */
  const half = (e: React.DragEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return e.clientY < r.top + r.height / 2 ? "before" : "after";
  };
  /* `moveOut` (✕ takes the home out and closes the gap) was retired 2026-08-22: a
     removal now CLAIMS the slot instead of re-packing, because re-packing left the
     pick list intact and the next fill pass put the home straight back. The claim
     lives in the ✕ handler itself; see its comment. */
  /* a typed homesite that matches live inventory gets its OWN link written in,
     with a toast — automatic, exact-match-or-nothing (2026-08-21) */
  const autoLink = (n: number) => {
    if (!lookupUrl) return;
    const typed = String(d["home" + n + "Homesite"] ?? "").trim();
    const k = "home" + n + "Url";
    if (!typed || d[k] === undefined) return;
    if (!urlBlank(String(d[k] ?? ""))) return;             /* already a real link */
    const u = lookupUrl(typed);
    if (u) { onData({ [k]: u }); if (onFlash) onFlash("Link filled from inventory for " + typed + "."); }
  };

  /* one card value, plain text until clicked — the plan card's own contract */
  const seg = (n: number, sfx: string, ph: string, suffix = "", tiny = false) => {
    const key = n + ":" + sfx;
    const val = String(d["home" + n + sfx] ?? "");
    return inline === key ? (
      <input className={"echpc-sin" + (tiny ? " tiny" : "")} data-echhin={sfx} autoFocus value={val}
        placeholder={ph}
        /* the value is a short label the requester is replacing, not editing a word of,
           so it opens SELECTED — typing overwrites and one Delete clears it, which is
           what the "you have to hit backspace" report was really asking for */
        onFocus={(e) => { try { e.currentTarget.select(); } catch { /* noop */ } }}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          onData({ ["home" + n + sfx]: e.target.value });
          /* a draft GRADUATES on its first real character — otherwise the slot
             stays flagged forever and re-renders as an empty card after a later
             re-pack empties it (found by the ✕ test: 10 cards where 9 should be) */
          if (e.target.value.trim() && drafts.indexOf(n) > -1) setDrafts((s) => s.filter((x) => x !== n));
        }}
        onBlur={() => { setInline(null); if (sfx === "Homesite") autoLink(n); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") { setInline(null); if (sfx === "Homesite") autoLink(n); } }} />
    ) : (
      <span className="echpc-click" data-echhclick={sfx} title="Click to edit"
        onClick={(e) => { e.stopPropagation(); setInline(key); }}>{val ? val + suffix : <span className="echpc-ph">{ph + suffix}</span>}</span>
    );
  };
  const homeRow = (n: number, g: RowCollection, i: number) => {
    const k = "h:" + n + ":";
    return (
      <div
        key={n}
        className={"echpc home" + (line === k + "before" ? " ins-b" : "") + (line === k + "after" ? " ins-a" : "")
          + (drag && drag.kind === "home" && drag.n === n ? " dragging" : "")}
        data-echhome={n}
        /* ── DRAGGABLE OFF WHILE A FIELD IS OPEN (2026-08-25) ─────────────────────────
           A division, via Feedback: "it does not allow you to highlight the copy to
           delete it. You have to hit backspace to remove whatever it is you want to
           remove." That is the browser behaving as told: a `draggable` ANCESTOR turns a
           press-and-sweep inside its descendants into an HTML5 drag, so the text never
           selects. The card has to stay draggable — reordering homes is a real feature —
           so it drops the attribute exactly while one of its inline inputs is open,
           which is the only time a requester is selecting text inside it. */
        draggable={used(n) && !(inline || "").startsWith(n + ":")}
        onDragStart={(e) => { setDrag({ kind: "home", n }); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(n)); } catch { /* noop */ } }}
        onDragEnd={() => { setDrag(null); setLine(null); }}
        onDragOver={(e) => {
          const dg = dragRef.current;
          if (!dg || dg.kind === "coll") return;
          if (dg.n === n) { setLine(null); return; }
          const fg = collOf(dg.n);
          /* a cross-section insert into a full section is not legal — flag the
             section instead of accepting a drop that would drop a home */
          if (fg !== g && filledOf(g).length >= g.homes.length) { setLine("no:" + colls.indexOf(g)); return; }
          e.preventDefault();
          setLine(k + half(e));
        }}
        onDrop={(e) => {
          const dg = dragRef.current;
          if (!dg || dg.kind !== "home" || dg.n === n) return;
          e.preventDefault();
          const after = String(line || "").indexOf(":after") > -1;
          setLine(null); setDrag(null);
          moveHome(dg.n, g, i + (after ? 1 : 0));
        }}
      >
        <span className="echpc-g" title="Drag to reorder">⠿</span>
        <button type="button" className="echpc-x" data-echhx title="Remove this home"
          onClick={(e) => {
            e.stopPropagation();
            setDrafts((s) => s.filter((x) => x !== n));
            if (!used(n)) return;
            /* ── REMOVING A PICKED HOME HAS TO CLAIM ITS SLOT (2026-08-22) ─────────
               Jack: "the drop option isn't working." Measured live: the card left the
               panel and the home stayed in the email, and the section still counted
               it. `moveOut` re-packs the section's ROW DATA and nothing else, so the
               next fill pass re-seated every pick from `__homePick` and put the home
               straight back — the same "can't delete homes, you keep re-adding them"
               failure this file already warns about, made certain by the fair-share
               re-seat.
               So a removal CLAIMS the slot the way a hand-edit does (`__socOff`), and
               the fill then leaves it alone. The canvas drops an empty row, so the
               email loses the home too. "Choose homes from inventory" already RELEASES
               these keys (see echPickKeys), so re-picking works exactly as before. */
            const patch: Record<string, unknown> = { ...rowClearPatch(d, n) };
            const claimed = String(d.__socOff || "").split(",").filter(Boolean);
            ROW_FIELDS.forEach((sfx) => {
              const k = "home" + n + sfx;
              if (d[k] !== undefined && claimed.indexOf(k) < 0) claimed.push(k);
            });
            patch.__socOff = claimed.join(",");
            onData(patch);
          }}>✕</button>
        <div className="echpc-bd">
          <b className="echpc-n">{seg(n, "Homesite", "101")}<span className="echh-sep"> / </span>{seg(n, "Plan", "Plan Name")}</b>
          {/* price ON the spec line (2026-08-21, Jack: "can you fit the price in
              the line?") — one line, four segments */}
          <div className="echpc-s">
            {seg(n, "SqFt", "—", " sq ft")} · {seg(n, "Beds", "—", " Beds", true)} · {seg(n, "Baths", "—", " Baths", true)} · {seg(n, "Price", "$ price")}
          </div>
          {rowUrl[n] && rowUrl[n].length > 0 && (() => {
            /* A FILLED HOME WITHOUT A REAL LINK IS FLAGGED (2026-08-21, Jack:
               "make sure we flag if a homesite doesn't have a link… so every
               homesite has a link") — the SAME requiredBlanks the Done gate
               runs, so the card and the gate can never disagree. Dynamic
               auto-pulls the URL from live inventory; this is Manual's belt. */
            const miss = linkMiss.has(n);
            return (
              <>
                <button type="button" className={"echpc-sect" + (linkOpen === n ? " open" : "") + (miss ? " miss" : "")} data-echhlink
                  onClick={(e) => { e.stopPropagation(); setLinkOpen(linkOpen === n ? null : n); }}>
                  View Home link {miss && <b className="echpc-missflag" data-echhlinkmiss>· link missing</b>} <span className="echpc-chev">▾</span>
                </button>
                {linkOpen === n && <div className="echpc-sectbd" data-echhlinkbd>{rowUrl[n]}</div>}
              </>
            );
          })()}
        </div>
      </div>
    );
  };
  return (
    <>
      {/* the spreadsheet door — Manual only by construction (this list IS the
          Manual panel). CSV because that is what Excel and Sheets both export. */}
      <input ref={fileRef} type="file"
        accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        style={{ display: "none" }} data-echcsv onChange={onCsv} />
      <button type="button" className="echh-upload" data-echupload
        title="Headers like Homesite, Plan, Sq Ft, Beds, Baths, Price, URL — and optionally Collection to split sections. Extra columns are ignored."
        onClick={() => { if (fileRef.current) fileRef.current.click(); }}>
        ⬆ Fill from a spreadsheet (.xlsx / .csv)
      </button>
      {/* ── THE PANEL SHOWS WHAT THE EMAIL SHOWS (2026-08-22) ────────────────────
          Jack: "double check the correlation between the right panel and the email
          — i have a lot more collections showing on the right panel vs the email."
          He was right, and it is a real parity break rather than a display choice:
          the CANVAS drops a collection header whose name is empty
          (`dropEmptyCollectionHeaders`) and drops empty home rows
          (`dropEmptyHomeRows`), so a section with no name AND no filled homes
          renders as literally nothing — while the panel enumerated it straight off
          the markup and showed "Collection / community name · 0 of 2".
          So the panel hides exactly the sections the email will not render. A
          section keeps its card the moment it has a name OR a home, which is every
          section a requester can actually be editing. */}
      {colls.map((g, gi) => {
        /* HIDDEN, NOT RE-INDEXED: `gi` is the section's real position in the markup
           and every control below keys off it (move up/down, the added-collection
           ordinal, the drop target). Filtering the array would renumber them and
           quietly move the wrong section — so a hidden section returns null and
           keeps its index. */
        /* ── …BUT AN EMPTIED TABLE STILL OFFERS ONE PLACE TO ADD (2026-09-22, #127) ──
           Removing every home now also drops the headings (a heading over nothing is
           a bug in a live send), so on a table with nothing left EVERY section would
           hide by the rule above and the panel would show no "+ Add a home" at all.
           The first section stays when no other section would render. */
        const showable = (c: RowCollection) => !!(c.lit != null ? c.lit : c.key ? cx(d[c.key]) : "").trim()
          || c.homes.some((n) => rowSummary(d, n).used || drafts.indexOf(n) > -1);
        if (!showable(g) && (gi !== 0 || colls.some(showable))) return null;
        const live = g.homes.filter((n) => used(n) || drafts.indexOf(n) > -1);
        /* the SECTION is full — the caption says so; the pad stays live while the
           TABLE has a row left (2026-09-15, see `freeSlotFor`) */
        const full = live.length >= g.homes.length;
        const canBorrow = full && roomInTable > 0 && !!blockType && !colls.some((c) => c.lit != null || !c.key);
        const padOff = full && !canBorrow;
        const ck = "c:" + gi + ":";
        return (
          <div
            key={g.key || "g" + gi}
            className={"lrc-grp" + (line === ck + "before" ? " ins-b" : "") + (line === ck + "after" ? " ins-a" : "")
              + (line === "no:" + gi ? " nofit" : "")
              + (drag && drag.kind === "coll" && drag.gi === gi ? " dragging" : "")}
            data-echcoll={gi}
            onDragOver={(e) => {
              const dg = dragRef.current;
              if (!dg || dg.kind !== "coll" || dg.gi === gi) return;
              e.preventDefault();
              setLine(ck + half(e));
            }}
            onDrop={(e) => {
              const dg = dragRef.current;
              if (!dg || dg.kind !== "coll" || dg.gi === gi) return;
              e.preventDefault();
              const after = String(line || "").indexOf(":after") > -1;
              const from = dg.gi;
              setLine(null); setDrag(null);
              /* only ever a one-step trade: two sections, and a swap is what a move
                 means when the order is fixed in markup */
              moveColl(from, (after ? gi + 1 : gi) > from ? 1 : -1);
            }}
          >
            {(g.key || g.lit != null) && (
              <div className="lrc-h">
                <span
                  className="echc-g"
                  draggable
                  title="Drag to reorder sections"
                  onDragStart={(e) => { setDrag({ kind: "coll", gi }); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "c" + gi); } catch { /* noop */ } }}
                  onDragEnd={() => { setDrag(null); setLine(null); }}
                >⠿</span>
                <span className="lr-nudge echc-nu">
                  <button type="button" title="Move this section up" disabled={gi === 0}
                    onClick={() => moveColl(gi, -1)}>↑</button>
                  <button type="button" title="Move this section down" disabled={gi === colls.length - 1}
                    onClick={() => moveColl(gi, 1)}>↓</button>
                </span>
                {/* the NAME reads as banner text; a click makes it the input
                    (2026-08-21, Jack: "click to edit rather than always show the
                    white background") */}
                {nameEdit === gi ? (
                  <input className="lrc-hname" data-echcnin autoFocus value={nameOf(g)}
                    placeholder="Collection / community name"
                    onChange={(e) => {
                      /* a TOKEN section writes its key; an ADDED section's name is
                         literal text in the markup, so it rewrites in place */
                      if (g.lit != null) { const p: Record<string, unknown> = {}; nameInto(g, e.target.value, p); onData(p); }
                      else onName(g.key as string, e.target.value);
                    }}
                    onBlur={() => setNameEdit(null)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setNameEdit(null); }} />
                ) : (
                  <span className="lrc-hlabel" data-echcname title="Click to edit"
                    onClick={() => setNameEdit(gi)}>{nameOf(g) || "Collection / community name"}</span>
                )}
                <span className="lrc-cnt">{live.length} of {g.homes.length}</span>
                {/* ── REMOVE A COLLECTION — THE HEADING AND ITS HOMES (2026-08-22,
                    widened 2026-08-27) ────────────────────────────────────────────
                    Jack: "should have the option to remove a collection", then
                    "i want the ability to remove a collection … remove the entire
                    collection and homes".

                    The ✕ used to render only when `g.lit != null` — an ADDED
                    collection, whose heading is a `<!--CN-->` literal — on the
                    reasoning that the block's own headings are Layne's structure. In
                    practice that hid the button on every collection a requester
                    actually sees: the grouped fill writes headings as declared
                    `{{collection<n>Name}}` TOKENS (`echGroupedListingPatch`), and
                    `echExpandListingHtml` clones more of the same when a request has
                    more communities than the markup draws. So `g.lit` was undefined
                    for all of them and the control was unreachable.

                    Both kinds are removable now, and they differ only in what is left
                    behind. An ADDED collection's heading row is markup this repo
                    inserted, so it is cut out (`rowDropColl`). A DECLARED one is
                    Layne's row: its heading is emptied and claimed instead, and
                    `dropEmptyCollectionHeaders` drops it from the render — the same
                    way an emptied home row disappears. Either way the homes go with
                    it, which is the part that was missing: a heading on its own was
                    never what "remove this collection" meant. */}
                {(g.lit != null || onDropColl) && (
                  <button type="button" className="lrc-x" data-echdropcoll={gi}
                    title="Remove this collection and its homes"
                    onClick={() => {
                      if (onDropColl) onDropColl(g);
                      if (g.lit == null) return;         /* declared: emptied + claimed above */
                      const ord = colls.filter((x, i) => i < gi && x.lit != null).length;
                      const html = rowDropColl(String(d.__html ?? ""), ord);
                      if (!html) return;                 /* nothing to cut — the clear stands */
                      const extra = extraOf().filter((_e, i) => i !== ord);
                      onData({ __html: html, [EXTRA_COLLS]: extra });
                    }}>×</button>
                )}
                <button type="button" className={"lrc-fold" + (closed.indexOf(gi) > -1 ? " closed" : "")}
                  data-echfold title={closed.indexOf(gi) > -1 ? "Show this section's homes" : "Collapse this section"}
                  onClick={() => setClosed((s) => (s.indexOf(gi) > -1 ? s.filter((x) => x !== gi) : [...s, gi]))}>▾</button>
              </div>
            )}
            {closed.indexOf(gi) > -1 ? null : (<>
            <div className="lrc-body">
              {live.map((n, i) => homeRow(n, g, i))}
            </div>
            <div className="echcap"><b>Homes</b>&nbsp;· {filledOf(g).length} selected
              <span>{full ? "Full" : (g.homes.length - live.length) + " open"}</span></div>
            <button
              type="button"
              className={"echpad" + (line === "pad:" + gi ? " over" : "")}
              disabled={padOff}
              data-echadd={gi}
              onDragOver={(e) => { const dg = dragRef.current; if (!dg || dg.kind !== "home" || full) return; e.preventDefault(); setLine("pad:" + gi); }}
              onDrop={(e) => {
                const dg = dragRef.current;
                if (!dg || dg.kind !== "home" || full) return;
                e.preventDefault();
                const n = dg.n;
                setLine(null); setDrag(null);
                moveHome(n, g, filledOf(g).length);
              }}
              onClick={() => {
                /* ── THE PICKER, SCOPED TO THIS SECTION (2026-08-22) ──────────────
                   Jack: "wondering if add home should do Choose homes from inventory
                   ›" → mockup approved. The blank card was seven fields to type and a
                   link to chase; the list arrives complete. Hand-typing lives in the
                   picker's footer now ("or type one by hand"), which calls back into
                   the draft below, so nothing is lost.
                   A block with no inventory wiring keeps the draft as its only path. */
                /* the first slot this section owns that is not in use becomes a
                   DRAFT card, editable in place — no window (2026-08-21) */
                const draft = () => {
                  const got = freeSlotFor(gi);
                  if (!got) return;
                  if (Object.keys(got.patch).length) onData(got.patch);
                  setDrafts((s) => [...s, got.n]); setInline(got.n + ":Homesite");
                };
                if (onAddFromInventory) {
                  /* the picker's cap is what the TABLE can still take — a pick regroups
                     by community and the fill re-cuts the sections to fit */
                  const own = Math.max(0, g.homes.length - filledOf(g).length);
                  onAddFromInventory(nameOf(g) || "", own > 0 ? own : roomInTable, draft);
                  return;
                }
                draft();
              }}
            >{padOff ? "All " + colls.reduce((a, c) => a + c.homes.length, 0) + " rows used" : "+ Add a home"}</button>
            </>)}
          </div>
        );
      })}
      {/* ── + ADD A COLLECTION (2026-08-21, Jack: "missing the opportunity to add
          more collections than just two") — sanctioned by the block's own note:
          additional collections may be added by duplicating a collection row
          while total home rows stay at 10 or fewer. The new section takes the
          LAST section's unused tail slots; recorded on the block so a markup
          refresh re-applies it. */}
      <button type="button" className="echh-addcoll" data-echaddcoll
        onClick={() => {
          const g = colls[colls.length - 1];
          /* ── NO COLLECTIONS IS THE CASE THAT NEEDS THE BUTTON MOST (2026-08-28,
             Jack: "when no colelctions can't add another") ─────────────────────────
             This opened `if (!g) return;` — a SILENT no-op, so the one state where a
             requester has nothing and is reaching for the button did nothing at all.
             (The other two failure paths at least flash.) With no collections there is
             no "last section" to take a slot from, so the first heading goes at row 1
             and the fill takes it from there. */
          if (!g) {
            const html0 = rowAddColl(String(d.__html ?? ""), 1, "Collection Name");
            if (!html0) { if (onFlash) onFlash("This block's markup has no collection row to duplicate."); return; }
            onData({ __html: html0, [EXTRA_COLLS]: [{ slot: 1, label: "Collection Name" }] });
            return;
          }
          const slot = g.homes[filledOf(g).length];
          if (slot == null) { if (onFlash) onFlash("Every slot in the last section is in use — free one at its end first."); return; }
          const html = rowAddColl(String(d.__html ?? ""), slot, "Collection Name");
          if (!html) { if (onFlash) onFlash("This block's markup has no collection row to duplicate."); return; }
          const extra = [...extraOf(), { slot, label: "Collection Name" }].sort((x, y) => x.slot - y.slot);
          onData({ __html: html, [EXTRA_COLLS]: extra });
        }}>+ Add a collection</button>
    </>
  );
}

/* ── THE FEATURED SECTION AS PLAN CARDS (2026-08-21, Jack, off his reference
   screenshot: "for featured plan block i want the manual to be like this instead…
   same for dynamic in terms of the visual") ────────────────────────────────────
   The panel shows a CARD FOR EVERY Featured block of this type in the document —
   photo (or an "Images coming soon" placeholder), plan name, a full-word spec
   line, and an ✕ that removes that block. A big dashed "Add plan" card inserts
   another block, up to 5. Each card IS one instance of Layne's block on the
   canvas, so the list and the email can never disagree about how many cards
   ship. THE CARD IS THE EDITOR (2026-08-21, Jack: "remove the edit details and
   work a clean set up for all of this") — no Edit details button, no window:
   the name and spec values click-to-edit in place, the photo click-opens the
   Bynder picker, and the block's BUTTON fields render as regular panel sections
   below the cards, on the existing approved-label/URL machinery. Both modes
   share the visual; Dynamic's card is read-only (the feed owns the copy) with
   Choose (the stepped wizard) on the dashed card and a quiet Clear pick. */
/* clicking a SIBLING card's Buttons & links selects that block first (one-click
   rule) — the Inspector remounts keyed by block id, so this latch carries "open
   the section on arrival" across the remount */
let PLAN_BTNS_OPEN: string | null = null;
/* an inline input that is exactly as wide as its own text (2026-09-22, the price pair):
   measured in the input's real font, so a range or $1,200,000 never clips and "From"
   is not a 52px guess. Runs on mount and on every change. */
function fitInput(el: HTMLInputElement | null) {
  if (!el) return;
  try {
    const c = document.createElement("canvas").getContext("2d");
    if (!c) return;
    const cs = getComputedStyle(el);
    c.font = cs.font;
    const w = c.measureText(el.value || el.placeholder || "").width;
    /* box-sizing is border-box here, so the box must carry its own padding + border
       (the panel rule gives inputs 11px side padding) plus a caret's worth of room */
    const chrome = ["paddingLeft", "paddingRight", "borderLeftWidth", "borderRightWidth"]
      .reduce((n, k) => n + (parseFloat((cs as unknown as Record<string, string>)[k]) || 0), 0);
    el.style.width = Math.ceil(w + chrome) + 4 + "px";
  } catch { /* noop */ }
}

function EchPlanCards({ selfId, cards, d, onData, feat, onChoose, editable = true, withChoose = false, onClearPick, onAdd, addLeft, cap, onSelectBlock, onDeleteBlock, onReorder, onPhoto, photo, btns }: {
  /** the SELECTED block's id — its card is the editable one */
  selfId: string;
  /** every Featured block of this type, in document order */
  cards: { id: string; data: Record<string, unknown> }[];
  d: Record<string, unknown>;
  onData: (patch: Record<string, unknown>) => void;
  feat: string;
  /** opens the SAME stepped wizard the Dynamic pane uses */
  onChoose: () => void;
  /** false in Dynamic: the feed owns the copy — the card is read-only */
  editable?: boolean;
  /** an always-dynamic block: editable cards AND a Choose card, because there is no
      mode to switch to for the other half (2026-08-22) */
  withChoose?: boolean;
  /** Dynamic only, when a pick exists: puts the block back on its own content */
  onClearPick?: () => void;
  /** insert another block of this type below (Jack: "i should be able to add up
      to 5") — absent when the section is at its cap */
  onAdd?: () => void;
  addLeft?: number;
  /** the section's cap, read from the template's SFMC guardrail — the copy states
      this number rather than a literal, so raising it in Marketing Cloud is the
      whole change */
  cap?: number;
  onSelectBlock?: (id: string) => void;
  onDeleteBlock?: (id: string) => void;
  /** drop card A on card B: move A's block before/after B's in the document */
  onReorder?: (fromId: string, targetId: string, after: boolean) => void;
  /** clicking the selected Manual card's photo opens the Bynder picker */
  onPhoto?: () => void;
  /** the selected card's photo as an ADJUSTABLE frame (2026-09-17): the slot the block declares
      for PlanImage, the picked asset's pixels, and the writers for the crop URL + learned size */
  photo?: { slot?: { w?: number; h?: number }; nat?: string; onNat: (dims: string) => void; onUrl: (u: string) => void };
  /** the block's non-card field sections — button labels/URLs — housed in the
      card's collapsed "Buttons & links" section (Jack, 2026-08-21: "this stuff
      should be in each card with a drop down section") */
  btns?: React.ReactNode[];
}) {
  const dragId = useRef<string | null>(null);
  const [line, setLine] = useState<string | null>(null);   /* "<id>:before|after" */
  /* which card text is being edited IN PLACE — "PlanName" | "SqFt" | "Beds" |
     "Baths" (2026-08-21, Jack: "keep it how it was, just could click onto it to
     edit it" — the card reads as plain text until a click turns that one value
     into an input, and blur/Enter turns it back) */
  const [inline, setInline] = useState<string | null>(null);
  /* the Buttons & links section, collapsed by default; opens on arrival when a
     sibling's section header was the way in */
  const [btnsOpen, setBtnsOpen] = useState<boolean>(() => {
    const hit = PLAN_BTNS_OPEN === selfId;
    if (hit) PLAN_BTNS_OPEN = null;
    return hit;
  });
  const noun = feat === "comms" ? (editable ? "collection" : "community")
    : feat === "homes" ? "homesite" : "plan";
  /* The ADD card names the LEVEL, always — "add community, add plan, add homesite"
     (Jack, 2026-08-28). `noun` says "collection" in the editable comms case, which is
     the right word for the read-only Choose pad's container but the wrong one on a
     button that adds a community. */
  const addNoun = feat === "comms" ? "community" : feat === "homes" ? "homesite" : "plan";
  return (
    <>
      {cards.map((c) => {
        const cd = c.data || {};
        const v = (k: string) => String(cd[k] ?? "").trim();
        /* ── THE OPEN INPUT SHOWS THE RAW VALUE, NEVER THE TRIMMED ONE (2026-09-15) ──
           Driven live: typing "Charle II QA" into the plan name landed as "CharleIIQA".
           `v()` trims, and a controlled input fed the trimmed value drops every trailing
           space the moment it is typed — the next keystroke then appends to the
           spaceless value. Display keeps trimming; the field you are typing in does not. */
        const raw = (k: string) => String(cd[k] ?? "");
        const img = /^https?:/i.test(v("PlanImage")) ? v("PlanImage") : "";
        /* full words, exactly as the reference reads them */
        /* the unit suffix is the block's OWN label field, not a hardcoded string —
           at Community level the fill clears the labels and puts real facts in the
           value slots ("Lavon, TX · From $412,990 · 3 homes available"), and a panel
           that appended " sq ft" to a city name was two renderings disagreeing
           (2026-08-21). A block whose label field is untouched shows the same units
           it always did. */
        const unit = (k: "SqFt" | "Beds" | "Baths") => {
          const l = v(k + "Label");
          return l ? " " + l : d[k + "Label"] !== undefined ? "" : (k === "SqFt" ? " sq ft" : k === "Beds" ? " Beds" : " Baths");
        };
        /* ── PRICE SITS ON THE SPEC LINE WITH THE REST (Jack, 2026-09-22: "lets just
           have the price … so it goes here with the rest of them") ────────────────
           Only when the block declares a Price field (Layne's 09-18 blocks do; older
           ones do not). It edits in place like sq ft / beds / baths, and the block's
           separate PriceLabel ("From") is no longer an editor of its own — Jack: "why
           do we have prefix?" — a value already there still shows in front. */
        const hasPrice = d.Price !== undefined;
        /* THE PREFIX IS FOR PLANS AND COMMUNITIES ONLY (Layne, Teams 2026-09-22: "with
           plans/communities, they use the From verbiage. if it's just a homesite, they
           don't need From"). A homesite card shows the bare figure, offers no prefix box,
           and an edit on it clears any prefix the block was holding so the email cannot
           ship "From $415,990" on a specific home. */
        const prefixOk = feat !== "homes";
        const priceTxt = hasPrice && v("Price") ? (prefixOk && v("PriceLabel") ? v("PriceLabel") + " " : "") + v("Price") : "";
        const spec = [
          v("SqFt") ? v("SqFt") + unit("SqFt") : "",
          v("Beds") ? v("Beds") + unit("Beds") : "",
          v("Baths") ? v("Baths") + unit("Baths") : "",
        ].filter(Boolean).join(" · ");
        const isSelf = c.id === selfId;
        return (
          <div key={c.id}
            className={"echpc" + (isSelf ? " sel" : "")
              + (line === c.id + ":before" ? " ins-b" : "") + (line === c.id + ":after" ? " ins-a" : "")}
            data-echpcard={c.id}
            /* SAME FIX AS THE HOME CARD (2026-08-25, from an audit): a `draggable`
               ancestor turns press-and-sweep into an HTML5 drag, so text inside these
               inline inputs could not be selected — the division's "you have to hit
               backspace" report. Reordering plan cards stays available; the attribute
               just goes while one of this card's own fields is open. */
            draggable={cards.length > 1 && !inline}
            onDragStart={(e) => { dragId.current = c.id; try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", c.id); } catch { /* noop */ } }}
            onDragEnd={() => { dragId.current = null; setLine(null); }}
            onDragOver={(e) => {
              if (!dragId.current || dragId.current === c.id) return;
              e.preventDefault();
              const r = e.currentTarget.getBoundingClientRect();
              setLine(c.id + (e.clientY < r.top + r.height / 2 ? ":before" : ":after"));
            }}
            onDrop={(e) => {
              const from = dragId.current;
              if (!from || from === c.id || !onReorder) return;
              e.preventDefault();
              const after = String(line || "").indexOf(":after") > -1;
              dragId.current = null; setLine(null);
              onReorder(from, c.id, after);
            }}>
            {cards.length > 1 && <span className="echpc-g" title="Drag to reorder">⠿</span>}
            {onDeleteBlock && cards.length > 1 && (
              <button type="button" className="echpc-x" data-echpcx title="Remove this card"
                onClick={() => onDeleteBlock(c.id)}>✕</button>
            )}
            {/* the selected Manual card's PHOTO is its own editor — click to open
                the Bynder picker (part of the clean no-window setup, 2026-08-21) */}
            {/* ── THE CARD PHOTO ADJUSTS LIKE THE MAIN IMAGE (stand-up 2026-09-17) ────────────
                Justin: "zoom in / zoom out be added to the feature community module images …
                we always try to zoom in enough so that the home is the full point … half the
                driveways are photoshopped in". A Bynder pick on the selected card gets the
                same drag + zoom-in frame the hero has (ImgCard, services/bynderCrop.ts), at
                the block's own PlanImage slot (270×180) — the crop is written into the same
                URL the canvas draws and the email ships, so every card lands at one size. A
                feed photo (lennar.com CDN, which offers no crop) keeps the plain click-to-
                replace picture until the bake path lands. */}
            {isSelf && editable && onPhoto && photo && photo.slot && photo.slot.w && photo.slot.h && isBynderTransform(img) ? (
              <div className="echpc-imgw adj" data-echphoto="1" data-echphotoadj="1">
                <ImgCard v={img} w={photo.slot.w} h={photo.slot.h} nat={photo.nat} onNat={photo.onNat} onUrl={photo.onUrl} openBynder={onPhoto} />
              </div>
            ) : (
            <div className={"echpc-imgw" + (isSelf && editable && onPhoto ? " can" : "")}
              data-echphoto={isSelf && editable && onPhoto ? "1" : undefined}
              onClick={isSelf && editable && onPhoto ? onPhoto : undefined}>
              {img
                ? <img className="echpc-img" src={img} alt="" />
                : <div className="echpc-img ph"><span>🏠</span>Images coming soon</div>}
              {isSelf && editable && onPhoto && <span className="echpc-imghint">📁 Replace photo</span>}
            </div>
            )}
            <div className="echpc-bd">
              {/* THE TEXT EDITS IN PLACE ON CLICK (2026-08-21, Jack: "keep it how it
                  was, just could click onto it to edit it") — the card reads as
                  plain text; clicking the name or a spec value swaps THAT value for
                  an input, blur/Enter puts the text back. The window stays for the
                  photo, labels and buttons. */}
              {isSelf && editable ? (
                <>
                  {inline === "PlanName" ? (
                    <input className="echpc-nin" data-echnin autoFocus onFocus={(e) => { try { e.currentTarget.select(); } catch { /* noop */ } }} value={raw("PlanName")}
                      placeholder="Plan Name"
                      onChange={(e) => onData({ PlanName: e.target.value })}
                      onBlur={() => setInline(null)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setInline(null); }} />
                  ) : (
                    <b className="echpc-n echpc-click" data-echnclick title="Click to edit"
                      onClick={() => setInline("PlanName")}>{v("PlanName") || "Plan Name"}</b>
                  )}
                  <div className="echpc-s">
                    {(["SqFt", "Beds", "Baths"] as const).map((k, i) => (
                      <span key={k}>
                        {i > 0 && " · "}
                        {inline === k ? (
                          <>
                            <input className={"echpc-sin" + (k === "SqFt" ? "" : " tiny")} data-echsin={k} autoFocus onFocus={(e) => { try { e.currentTarget.select(); } catch { /* noop */ } }}
                              value={raw(k)}
                              onChange={(e) => onData({ [k]: e.target.value })}
                              onBlur={() => setInline(null)}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setInline(null); }} />
                            {unit(k)}
                          </>
                        ) : (
                          /* the WHOLE segment is the click target — Jack clicked
                             "sq ft" and got a text selection (2026-08-21 screenshot) */
                          <span className="echpc-click" data-echsclick={k} title="Click to edit"
                            onClick={() => setInline(k)}>
                            {(v(k) || "—") + unit(k)}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                  {/* ── THE PRICE IS ITS OWN LINE UNDER THE SPECS (Jack, 2026-09-22) ──────────
                      Mirrors the email block, where {{PriceLabel}} {{Price}} is a separate
                      paragraph below the spec block. Measured: the spec text is 219px of the
                      card's 304px, so a prefix box + price box (~108px) can never share that
                      line — its own line fits every value, ranges included. Clicking it opens
                      PREFIX + PRICE together ("when clicking on it this is where you can have
                      the prefix and price"): Tab moves between them, blur to the sibling keeps
                      the pair open, Enter / Escape closes. Only when the block declares Price. */}
                  {hasPrice && (
                    <div className="echpc-p" data-echprice>
                      {inline === "Price" ? (
                        <span className="echpc-pricepair" data-echpricepair>
                          {prefixOk && (
                            <>
                              <input className="echpc-sin tiny" data-echsin="PriceLabel" ref={fitInput}
                                value={raw("PriceLabel")} placeholder="From"
                                onChange={(e) => { fitInput(e.currentTarget); onData({ PriceLabel: e.target.value }); }}
                                onBlur={(e) => { const to = e.relatedTarget as HTMLElement | null; if (!(to && to.closest && to.closest("[data-echpricepair]"))) setInline(null); }}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setInline(null); }} />
                              {" "}
                            </>
                          )}
                          {/* both boxes size to what is typed — a range or $1,200,000 must never clip */}
                          <input className="echpc-sin" data-echsin="Price" ref={fitInput} autoFocus onFocus={(e) => { try { e.currentTarget.select(); } catch { /* noop */ } }}
                            value={raw("Price")} placeholder="$415,990"
                            onChange={(e) => { fitInput(e.currentTarget); onData(prefixOk ? { Price: e.target.value } : { Price: e.target.value, PriceLabel: "" }); }}
                            onBlur={(e) => { const to = e.relatedTarget as HTMLElement | null; if (!(to && to.closest && to.closest("[data-echpricepair]"))) setInline(null); }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setInline(null); }} />
                        </span>
                      ) : (
                        <span className="echpc-click" data-echsclick="Price" title="Click to edit"
                          onClick={() => setInline("Price")}>
                          {priceTxt || "$ price"}
                        </span>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <b className="echpc-n">{v("PlanName") || "Plan Name"}</b>
                  <div className="echpc-s">{spec || "—"}</div>
                  {hasPrice && priceTxt && <div className="echpc-p">{priceTxt}</div>}
                </>
              )}
              {/* NO Edit details and NO window (2026-08-21, Jack: "remove the edit
                  details and work a clean set up for all of this") — everything on
                  the card edits in place, and the block's button fields render as
                  regular sections BELOW the cards, reusing the approved-label select
                  and URL machinery instead of duplicating it. Dynamic keeps only the
                  quiet Clear pick. */}
              {/* CLEAR PICK IS NOT A READ-ONLY AFFORDANCE (2026-08-22). It used to
                  render only when `!editable`, because read-only WAS Dynamic. On an
                  always-dynamic block the card is fed AND editable, so gating the
                  clear on read-only would have left a fed card with no way back to
                  the Choose state. It shows whenever the parent supplies it. */}
              {isSelf && onClearPick && (
                <button type="button" className="echpc-clear" data-echclearpick onClick={onClearPick}>Clear pick</button>
              )}
              {!isSelf && (
                <button type="button" className="echpc-btn ghost"
                  onClick={() => {
                    /* one click selects the card FOR EDITING — the Studio flips it
                       to Manual so it lands editable, never on a Choose state */
                    if (onSelectBlock) onSelectBlock(c.id);
                  }}>Edit this plan</button>
              )}
              {/* THE BUTTON FIELDS LIVE ON THE CARD, COLLAPSED (2026-08-21, Jack:
                  "this stuff should be in each card with a drop down section") —
                  the selected card holds the real field sections; a sibling's
                  header selects that card first and opens on arrival */}
              {editable && btns && btns.length > 0 && (isSelf ? (
                <>
                  <button type="button" className={"echpc-sect" + (btnsOpen ? " open" : "")} data-echbtns
                    onClick={() => setBtnsOpen((o) => !o)}>
                    Buttons &amp; links <span className="echpc-chev">▾</span>
                  </button>
                  {btnsOpen && <div className="echpc-sectbd" data-echbtnsbd>{btns}</div>}
                </>
              ) : (
                <button type="button" className="echpc-sect" data-echbtns
                  onClick={() => {
                    PLAN_BTNS_OPEN = c.id;
                    if (onSelectBlock) onSelectBlock(c.id);
                  }}>
                  Buttons &amp; links <span className="echpc-chev">▾</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {editable ? (
        /* ── AN ALWAYS-DYNAMIC BLOCK GETS BOTH DOORS (2026-08-22) ─────────────────
           Choose pulls a plan out of live inventory; Add plan makes a blank one for
           something the feed does not carry. They used to be the two SIDES of the
           mode — Choose in the read-only Dynamic pane, Add in Manual — so with the
           mode gone they have to sit together, or "you pull from dynamic but can just
           edit like regular" is only half true. Blocks that still HAVE a mode are
           unchanged: `withChoose` is off for them, and their Add card keeps the line
           that points at the toggle. */
        <>
          {/* ── NO SECOND "CHOOSE" CARD AT THE BOTTOM (2026-08-22) ──────────────────
              Jack: "for featured plan you can remove this choose a collection button
              at the bottom." The panel already opens with "Choose floorplans from
              inventory ›" above the cards, so a "Choose a collection" pad under them
              is the same door twice — and it reads as a THIRD thing next to Add plan.
              An always-dynamic block keeps only its Add card here; the top control is
              the way into the picker. */}
          {/* ── THE ADD CARD FOLLOWS THE LEVEL (2026-08-28, Jack: "add plan should
              change to reflect the level like add community, add plan, add homesite") ──
              ⚠️ This REVERSES the 2026-08-21 ruling that used to sit here ("should say
              add a plan instead of community"), on Jack's explicit instruction today.
              Do not "correct" it back. `noun` already existed and was read only by the
              read-only Choose pad below, so the two controls in the same panel
              disagreed about what they add. */}
          <button type="button" className="echpad echpad-big" data-echadd
            disabled={!onAdd} onClick={onAdd}
            title={onAdd ? undefined : "This section holds up to " + (cap ?? 5)}>
            <span className="echpad-plus">+</span>
            <b>{onAdd ? "Add " + addNoun : "All " + (cap ?? 5) + " added"}</b>
            {/* no mode to point at on an always-dynamic block — the card above IS
                the auto-fill, so the old "or turn on Dynamic" line would be a
                reference to a control that no longer exists */}
            <i>{withChoose ? "typed in by hand" : "or turn on Dynamic to auto-fill"}</i>
          </button>
        </>
      ) : (
        <button type="button" className="echpad echpad-big" data-echchoose onClick={onChoose}>
          <span className="echpad-plus">+</span>
          <b>Choose a {noun}</b>
          <i>fills this card from live inventory</i>
        </button>
      )}
    </>
  );
}

function RepeatField({ f, items, onItems, openBynder, gr }: {
  f: FieldDescriptor;
  items: Record<string, unknown>[];
  onItems: (a: Record<string, unknown>[]) => void;
  openBynder: OpenBynder;
  gr?: Guardrails | null;
}) {
  const [edit, setEdit] = useState<number | null>(null);
  const sub = (f.sub || []).filter((s) => s.k);
  const nameK = (sub.find((s) => s.type === "text") || {}).k;
  const imgK = (sub.find((s) => s.type === "image") || {}).k;
  const lbl = f.itemLabel || "item";
  const rules = gr?.sub ?? {};
  const { over, rowProps } = useDragReorder((from, to) => {
    const a = items.slice();
    const [mv] = a.splice(from, 1);
    a.splice(to, 0, mv);
    onItems(a);
  });
  const setItem = (i: number, k: string, v: unknown) => {
    const a = items.slice();
    a[i] = { ...a[i], [k]: v };
    onItems(a);
  };
  /* modal fields honour the monolith's s.row grouping (specs on one row, button on one row) */
  const modalFields = (i: number) => {
    const it = items[i] || {};
    const out: React.ReactNode[] = [];
    let j = 0;
    while (j < sub.length) {
      const s = sub[j];
      const fieldEl = (ss: FieldDescriptor) => {
        const val = cx(it[ss.k!]);
        const rule = rules[ss.k!];
        const capd = capOf(rule);
        return (
          <div className="rep-f" key={ss.k}>
            <label>{unent(ss.label)}</label>
            <RepSubInput s={ss} val={val} openBynder={openBynder}
              onVal={(v) => setItem(i, ss.k!, capd ? clamp(v, rule) : v)} />
            <Grc value={val} rule={rule} />
            {ss.type === "text" && (
              /* an <input> gets the chip row, not the squiggle: a mirror behind a
                 single-line field has to track horizontal scroll as the caret moves
                 past the right edge, which is a different problem from wrapping
                 text and not one worth solving for these short fields. */
              <SpellHint value={val} onFix={(nx) => setItem(i, ss.k!, capd ? clamp(nx, rule) : nx)} />
            )}
          </div>
        );
      };
      if (s.row) {
        const grp: FieldDescriptor[] = [];
        const rk = s.row;
        while (j < sub.length && sub[j].row === rk) { grp.push(sub[j]); j++; }
        out.push(
          <div className="rep-grid" key={"row-" + rk} style={{ gridTemplateColumns: `repeat(${grp.length}, 1fr)` }}>
            {grp.map(fieldEl)}
          </div>,
        );
      } else { out.push(fieldEl(s)); j++; }
    }
    return out;
  };
  return (
    <div className="rep">
      {items.map((it, i) => {
        const img = imgK ? cx(it[imgK]) : "";
        const nm = (nameK && it[nameK]) ? cx(it[nameK]) : lbl + " " + (i + 1);
        const sl = repSpecLine(sub, it);
        return (
          <div key={i} className={"repc" + (over === i ? " drag-over" : "")} draggable {...rowProps(i)}>
            <div className={"repc-img" + (img ? "" : " soon")} style={img ? { backgroundImage: `url('${img}')` } : undefined}>
              {!img && (<><span className="repc-soonh">🏠</span><span className="repc-soont">Images coming soon</span></>)}
              <span className="repc-grip" title="Drag to reorder">⋮⋮</span>
              <button type="button" className="repc-x" title="Remove" onClick={() => onItems(items.filter((_x, j) => j !== i))}>×</button>
            </div>
            <div className="repc-meta">
              <div className="repc-n">{nm}</div>
              {sl && <div className="repc-s">{sl}</div>}
              <button type="button" className="repc-edit" onClick={() => setEdit(i)}>Edit details</button>
            </div>
          </div>
        );
      })}
      <div className="repc-blank" onClick={() => { onItems([...items, JSON.parse(JSON.stringify(f.add || {}))]); setEdit(items.length); }}>
        <div className="repc-plus">+</div>
        <div className="repc-bt">Add {String(lbl).toLowerCase()}</div>
        <div className="repc-bs">{items.length ? "or turn on Dynamic to auto-fill" : "nothing added yet"}</div>
      </div>
      {edit != null && items[edit] && (
        <RepModal title={(nameK && cx(items[edit][nameK])) || (f.itemLabel || "Item") + " " + (edit + 1)} onClose={() => setEdit(null)}>
          {modalFields(edit)}
        </RepModal>
      )}
    </div>
  );
}

/* ── listrows: grouped Collection/home cards + edit modal (monolith listRowsEditor) ── */
interface LrRow { t: "h" | "d"; name?: string; plan?: string; specs?: string; price?: string; view?: string }
function lrParse(str: string): LrRow[] {
  return (str || "").split("\n").filter((l) => l.trim() !== "").map((l) => {
    if (l.indexOf("|") < 0) return { t: "h" as const, name: l.trim() };
    const c = l.split("|").map((x) => x.trim());
    return { t: "d" as const, plan: c[0] || "", specs: c[1] || "", price: c[2] || "", view: c[3] || "View Home" };
  });
}
function lrSerialize(items: LrRow[]): string {
  return items.map((it) => (it.t === "h" ? it.name || "" : [it.plan, it.specs, it.price, it.view].join(" | "))).join("\n");
}
const LR_SUB: { k: keyof LrRow; label: string }[] = [
  { k: "plan", label: "Homesite / Plan" },
  { k: "specs", label: "Sq ft / Beds / Baths" },
  { k: "price", label: "Price" },
  { k: "view", label: "Button label" },
];

/**
 * A COLLECTION IS A CONTAINER, NOT A ROW THAT HAPPENS TO SIT ABOVE OTHER ROWS
 * (2026-08-20, Jack — chose this off a mockup of three options, then "go with 2 but
 * remove this" on the header treatment).
 *
 * The stored shape is unchanged: still one newline-delimited string, still a line
 * without a `|` meaning a collection header. This is purely how it is DRAWN — homes
 * render nested inside the header that owns them. `lrSerialize` output is identical,
 * so there is no migration and no risk to an existing saved build.
 *
 * WHY IT MATTERS BEYOND LOOKS: with a flat list, dragging a collection moved the
 * header and left its homes behind, silently regrouping the email. Grouping the view
 * makes that fixable — `moveSpan` below moves a header WITH its homes.
 *
 * Homes before the first header are a real state (a table with no collections at all),
 * so they render as a headerless group rather than being forced under an invented one.
 */
interface LrGroup { head: number | null; rows: number[] }
function lrGroups(items: LrRow[]): LrGroup[] {
  const out: LrGroup[] = [];
  let cur: LrGroup | null = null;
  items.forEach((it, i) => {
    if (it.t === "h") { cur = { head: i, rows: [] }; out.push(cur); return; }
    if (!cur) { cur = { head: null, rows: [] }; out.push(cur); }
    cur.rows.push(i);
  });
  return out;
}

function ListRowsField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const items = lrParse(value);
  const [edit, setEdit] = useState<number | null>(null);
  const push = (a: LrRow[]) => onChange(lrSerialize(a));
  const groups = lrGroups(items);

  /**
   * ↑ / ↓ INSTEAD OF DRAGGING (2026-08-20, Jack: "this is still weird to drag and etc").
   *
   * Two reasons, and the second is the stronger one:
   *   1. HTML5 drag inside a 330px scrolling panel is genuinely awkward — there is no
   *      room to aim, the drop target is a 30px-tall row, and the panel scrolls under
   *      the pointer while you hold the grip.
   *   2. THE STUDIO ALREADY REORDERS WITH ARROWS. Every block on the canvas carries
   *      ↑ ↓ ⧉ ✕. Using a grip here made this panel the only place in the tool where
   *      moving something means dragging it — a second interaction for one job.
   *
   * A home moves one position in the flat list, so moving it up past a header lands it
   * in the collection above — which is what "up" should mean. A COLLECTION moves as a
   * whole group, header and homes together, by rebuilding the list from the group order:
   * cheaper to read than index arithmetic, and it cannot orphan a home.
   */
  const moveHome = (i: number, dir: -1 | 1) => {
    const a = items.slice();
    const j = i + dir;
    if (j < 0 || j >= a.length) return;
    const [mv] = a.splice(i, 1);
    a.splice(j, 0, mv);
    push(a);
  };
  const moveGroup = (gi: number, dir: -1 | 1) => {
    const gj = gi + dir;
    if (gj < 0 || gj >= groups.length) return;
    const order = groups.slice();
    const [mv] = order.splice(gi, 1);
    order.splice(gj, 0, mv);
    const out: LrRow[] = [];
    order.forEach((g) => {
      if (g.head != null) out.push(items[g.head]);
      g.rows.forEach((r) => out.push(items[r]));
    });
    push(out);
  };
  /** the ↑ ↓ pair, matching the canvas block controls */
  const Nudge = ({ up, down, what }: { up?: () => void; down?: () => void; what: string }) => (
    <span className="lr-nudge">
      <button type="button" disabled={!up} title={"Move " + what + " up"} onClick={up}>↑</button>
      <button type="button" disabled={!down} title={"Move " + what + " down"} onClick={down}>↓</button>
    </span>
  );
  const setRow = (i: number, k: keyof LrRow, v: string) => {
    const a = items.slice();
    a[i] = { ...a[i], [k]: v };
    push(a);
  };
  /** a home added INSIDE a collection lands at the end of that collection, not the table */
  const addHomeTo = (g: LrGroup) => {
    const a = items.slice();
    const at = g.rows.length ? g.rows[g.rows.length - 1] + 1 : (g.head == null ? a.length : g.head + 1);
    const row: LrRow = { t: "d", plan: "", specs: "", price: "", view: "View Home" };
    a.splice(at, 0, row);
    push(a);
    setEdit(at);
  };
  const delGroup = (g: LrGroup) => {
    /* removing a collection removes its homes — they belong to it, which is the whole
       point of the container. The count is in the button title so it is never a surprise. */
    const kill = new Set<number>(g.rows);
    if (g.head != null) kill.add(g.head);
    push(items.filter((_x, j) => !kill.has(j)));
  };

  const homeRow = (i: number) => {
    const it = items[i];
    const sl = [it.specs, it.price].filter((x) => x && !/^view\b/i.test(String(x).trim())).join(" · ");
    const blank = !String(it.plan || "").trim();
    return (
      <div key={i} className={"repc repc-lite" + (blank ? " lr-blankrow" : "")}>
        <Nudge what="this home"
          up={i > 0 ? () => moveHome(i, -1) : undefined}
          down={i < items.length - 1 ? () => moveHome(i, 1) : undefined} />
        <div className="repc-meta">
          <div className="repc-n">{it.plan || "Empty row"}</div>
          {/* an empty row is DROPPED from the email (echRows), so the panel says so
              rather than showing a row that looks like every other one */}
          <div className="repc-s">{blank ? "Empty — won't be in the email" : sl}</div>
        </div>
        <button type="button" className="repc-edit repc-edit-sm" onClick={() => setEdit(i)}>Edit</button>
        <button type="button" className="lrc-x" title="Remove" onClick={() => push(items.filter((_x, j) => j !== i))}>×</button>
      </div>
    );
  };

  return (
    <div className="lr">
      {groups.map((g, gi) => {
        const head = g.head == null ? null : items[g.head];
        const name = head ? cx(head.name) : "";
        return (
          <div className="lrc-grp" key={g.head == null ? "g" + gi : "h" + g.head}>
            {head && (
              <div className="lrc-h">
                <Nudge what="this collection and its homes"
                  up={gi > 0 ? () => moveGroup(gi, -1) : undefined}
                  down={gi < groups.length - 1 ? () => moveGroup(gi, 1) : undefined} />
                <input className="lrc-hname" value={name} placeholder="Collection / community name"
                  onChange={(e) => setRow(g.head as number, "name", e.target.value)} />
                <span className="lrc-cnt">{g.rows.length === 1 ? "1 home" : g.rows.length + " homes"}</span>
                <button type="button" className="lrc-x" title={"Remove this collection and its " + g.rows.length + " home(s)"}
                  onClick={() => delGroup(g)}>×</button>
              </div>
            )}
            <div className="lrc-body">{g.rows.map(homeRow)}</div>
            <button type="button" className="lrc-add" onClick={() => addHomeTo(g)}>
              + Add home{name ? " to " + name : ""}
            </button>
          </div>
        );
      })}
      <div className="lr-add">
        <div className="repc-blank lrc-blank" onClick={() => push([...items, { t: "h", name: "New Collection" }])}>
          <div className="repc-plus">+</div><div className="repc-bt">Add collection</div>
        </div>
      </div>
      {edit != null && items[edit] && (
        <RepModal title={items[edit].plan || "Home " + (edit + 1)} onClose={() => setEdit(null)}>
          {LR_SUB.map((s) => (
            <div className="rep-f" key={s.k}>
              <label>{unent(s.label)}</label>
              <input className="rep-in" value={cx(items[edit][s.k])} onChange={(e) => setRow(edit, s.k, e.target.value)} />
            </div>
          ))}
        </RepModal>
      )}
    </div>
  );
}

/* ── sheet: spreadsheet import (monolith data-sheet; CSV contract: Homesite · Plan · Sq ft · Beds · Baths · Price) ── */
function SheetField({ onRows }: { onRows: (rows: string) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  function importCsv(file: File) {
    file.text().then((txt) => {
      const rows = txt.split(/\r?\n/).map((r) => r.split(",").map((c) => c.trim())).filter((r) => r.length > 1 && r[0]);
      const out = rows.slice(1).map((r) => `${r[0] || r[1] || "Home"} | ${r[2] || "—"} / ${r[3] || "—"} / ${r[4] || "—"} | View Home`);
      if (out.length) onRows(out.join("\n"));
    });
  }
  return (
    <>
      <button type="button" className="esbynderb" onClick={() => fileRef.current?.click()}>↓ Import spreadsheet (CSV or Excel)</button>
      <div className="mut2" style={{ marginTop: 6 }}>Columns: Homesite · Plan · Sq ft · Beds · Baths · Price</div>
      <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])} />
    </>
  );
}

/* ── socgrid: footer social icon grid (monolith data-soc) — tap an icon to toggle/edit ── */
function SocGrid({ d, onData }: { d: Record<string, unknown>; onData: SetData }) {
  const [sel, setSel] = useState<string | null>(null);
  return (
    <>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
        {SOC_KEYS.map((kk, i) => {
          const on = !!(d[kk] && String(d[kk]).trim());
          const selp = sel === kk;
          return (
            <button key={kk} type="button" title={LEN.soc[i][0]}
              style={{
                width: 36, height: 36, borderRadius: 9, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px solid " + (on ? "#1B2A58" : "#d3d9e2"), background: on ? "#1B2A58" : "#fff",
                boxShadow: selp ? "0 0 0 2px rgba(27,42,88,.28)" : undefined,
              }}
              onClick={() => {
                if (!on) onData({ [kk]: (socDefaults() as unknown as Record<string, string>)[kk] || "https://" });
                setSel(kk);
              }}>
              <img src={LEN.base + LEN.soc[i][2]} alt={LEN.soc[i][0]} width={16} height={16} style={on ? undefined : { opacity: 0.35 }} />
            </button>
          );
        })}
      </div>
      {sel && SOC_KEYS.indexOf(sel as typeof SOC_KEYS[number]) > -1 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#8a97ab", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 5, textAlign: "left" }}>
            {LEN.soc[SOC_KEYS.indexOf(sel as typeof SOC_KEYS[number])][0]} URL
          </div>
          <input className="mono" value={cx(d[sel])} onChange={(e) => onData({ [sel]: e.target.value })} />
          {/* the monolith stamps data-urlact="1" on this input (L13406) — a social
              handle is a link, so it gets the pair like any other */}
          <UrlBar value={cx(d[sel])} />
          <button type="button"
            style={{ marginTop: 7, border: "1px solid #e6d5d0", background: "#fdf4f2", color: "#8c3a2f", borderRadius: 7, padding: "5px 11px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
            onClick={() => { onData({ [sel]: "" }); setSel(null); }}>
            Turn off {LEN.soc[SOC_KEYS.indexOf(sel as typeof SOC_KEYS[number])][0]}
          </button>
        </div>
      )}
      <div className="mut2">Dynamic mode fills the division's REAL links via /sfmc-social-links; empty URL drops the icon.</div>
    </>
  );
}

/* LiveFillField (the search-box stopgap for the Dynamic pane) was REMOVED 2026-08-20.
   It was replaced by `DynLiveFill`, the port of the monolith's stepped picker, and a
   replacement is not live until the old path is gone — leaving it here would have left
   a second, worse Dynamic pane one prop away from rendering again. Its behaviour is not
   lost: the explicit-pick rule it carried ("content lands only on this explicit pick")
   is the wizard's Done button. See DynFeaturePicker.tsx / DynSelectedPanel.tsx. */

/* ── gifgen: the monolith's live previews + generate button ── */
function sbSpeed(v: unknown): number {
  const s = parseInt(cx(v), 10) || 16;
  if (s <= 10) return 6.2;
  if (s >= 22) return 2.7;
  return 4.4;
}
function GifGenField({ type, data, onData }: { type: string; data: Record<string, unknown>; onData: SetData }) {
  const [pct, setPct] = useState<number | null>(null);
  const gen = GIF_GENERATORS[type];
  if (!gen) return null;
  let prev: React.ReactNode = null;
  if (cx(data.gif)) {
    prev = <div style={{ fontSize: 0 }}><img src={cx(data.gif)} alt="Generated animation" style={{ width: "100%", display: "block", borderRadius: 8 }} /></div>;
  } else if (type === "scrollbanner") {
    const tc = cx(data.tcolor) || "#ffffff";
    const txt = cx(data.text) || " ";
    const rev = cx(data.dir) === "right" ? "reverse" : "normal";
    prev = (
      <div className="sbprev">
        <div className="sbtrack" style={{ animationDuration: sbSpeed(data.speed) + "s", animationDirection: rev as "reverse" | "normal" }}>
          <span style={{ color: tc }}>{txt}</span><span style={{ color: tc }}>{txt}</span>
        </div>
      </div>
    );
  } else if (type === "countdown") {
    const r = cdParts(cx(data.deadline));
    prev = <div className="gigprev cd">{cx(data.lab)}<b>{r.d}d {r.h}h {r.m}m {r.s}s</b></div>;
  } else if (type === "priceflip") {
    prev = <div className="gigprev fl">{pfFmt(data.from, cx(data.unit))} → <b>{pfFmt(data.to, cx(data.unit))}</b></div>;
  } else if (type === "scratchreveal") {
    prev = <div className="gigprev cd">{cx(data.lab)}<b>{cx(data.offer)}</b></div>;
  } else if (type === "splitflap") {
    prev = <div className="gigprev cd">{cx(data.lab)}<b>{cx(data.word).toUpperCase()}</b></div>;
  }
  return (
    <>
      {prev}
      <button type="button" className="esbtn2 solid" style={{ width: "100%", marginTop: 10 }} disabled={pct != null}
        onClick={() => { setPct(0); gen(data as Record<string, string>, setPct).then((url) => { onData({ gif: url }); setPct(null); }).catch(() => setPct(null)); }}>
        {pct != null ? `Rendering GIF… ${pct}%` : cx(data.gif) ? "↻ Regenerate GIF" : "⚡ Generate animated GIF"}
      </button>
      <div className="mut2" style={{ marginTop: 6 }}>{cx(data.gif) ? "Preview GIF ready — host it before sending." : "Generate the animated GIF for the email."}</div>
      {cx(data.gif) && (
        <button type="button" className="esbtn2" style={{ width: "100%", marginTop: 6 }} onClick={() => onData({ gif: "" })}>Remove animation (back to static band)</button>
      )}
      {type === "countdown" && (
        <div className="mut2" style={{ marginTop: 6 }}>Ticks the seconds in the email. For an open-time-accurate countdown, this can point to a hosted countdown-image service.</div>
      )}
    </>
  );
}

/* ── carbuild: film strip (monolith esFilm) — tiles + Bynder "+" + drag reorder ── */
function FilmField({ imgs, onImgs, openBynder, noAdd, selected, onSelect }: { imgs: string[]; onImgs: (a: string[]) => void; openBynder: OpenBynder; /** the GIF builder adds through its own Bynder button (2026-09-16) */ noAdd?: boolean; /** the GIF builder's per-frame editor: the highlighted tile and the tap that opens it */ selected?: number | null; onSelect?: (i: number) => void }) {
  const { over, rowProps } = useDragReorder((from, to) => {
    const a = imgs.slice();
    const [mv] = a.splice(from, 1);
    a.splice(to, 0, mv);
    onImgs(a);
  });
  return (
    <>
      <div className="esfilm">
        {imgs.map((s, i) => (
          <div key={i} className={"esftile" + (over === i ? " drag-over" : "") + (selected === i ? " on" : "")} draggable {...rowProps(i)} style={{ backgroundImage: `url('${s}')` }} onClick={onSelect ? () => onSelect(i) : undefined} data-testid={onSelect ? "gif-tile" : undefined}>
            <span className="esfx" onClick={() => onImgs(imgs.filter((_x, j) => j !== i))}>×</span>
            <span className="esfn">{i + 1}</span>
          </div>
        ))}
        {!noAdd && <div className="esfadd" title="Add photos" onClick={() => openBynder((url) => onImgs([...imgs, url]))}>+</div>}
      </div>
      <div className="mut2">{imgs.length < 2 && !noAdd ? "Drag tiles to reorder · tap + to add from Bynder." : "Drag tiles to reorder."}</div>
    </>
  );
}

/* ── HERO SLIDESHOW GIF (2026-09-16, Jack: "i want to create a gif like we did on the html") ──
   The monolith's "Build GIF / carousel" (`__mediaBuilder`) for the Main Image: 2–5 Bynder photos,
   a speed, Build. Collapsed it is one button under the photo card; open it is the film strip the
   carousel block already uses, so adding photos is the same single Bynder pick as everywhere else.
   The built GIF lands in the image field as a data URL (a preview until submit hosts it) with the
   spec beside it so it can be reopened and rebuilt. services/heroGif.ts holds the engine. */
function HeroGifPanel({ k, data, slot, onData, openBynder }: { k: string; data: Record<string, unknown>; slot: { w: number; h: number } | null; onData: SetData; openBynder: OpenBynder }) {
  const saved = heroGifSpecOf(data[k + "__gifsrc"]);
  const isGif = cx(data[k + "__gif"]) === "1";
  const [open, setOpen] = useState(false);
  const [imgs, setImgs] = useState<string[]>(() => (saved ? saved.imgs : []));
  /* each frame's own pixels, parallel to `imgs` — what the drag + zoom card needs to write a crop */
  const [nats, setNats] = useState<string[]>(() => (saved && saved.nats ? saved.nats : []));
  /* opens ON photo 1's card (Jack 2026-09-16: "have it start in the larger view") */
  const [edit, setEdit] = useState<number | null>(() => (saved && saved.imgs.length ? 0 : null));
  /* one speed, 2 s a photo (stand-up 2026-09-17) — a GIF built before that keeps its own */
  const delay = saved ? saved.delay : HERO_GIF_DEFAULT_DELAY;
  const [pct, setPct] = useState<number | null>(null);
  const [err, setErr] = useState("");
  const W = slot ? slot.w : 600, H = slot ? slot.h : 350;
  const setFrames = (a: string[], n?: string[]) => { setImgs(a.slice(0, HERO_GIF_MAX)); setNats((n || nats).slice(0, HERO_GIF_MAX)); setEdit((e) => (a.length ? (e == null || e >= a.length ? 0 : e) : null)); };
  /* ── MOVE A PHOTO EARLIER OR LATER (2026-09-23, Lauren Gleim, Form Feedback 156: "it will
     show the order in which images are chosen but not the ability to easily move them
     around"). Dragging the tiles already worked, but the tiles are small, a tap on one opens
     its editor, and nothing on screen said they could be dragged. The open photo now has two
     buttons that move it one place; the photo, its crop and the open editor travel together. */
  const moveFrame = (from: number, to: number) => {
    if (from < 0 || from >= imgs.length || to < 0 || to >= imgs.length || from === to) return;
    const a = imgs.slice();
    const n = imgs.map((_x, i) => nats[i] || "");
    const [mi] = a.splice(from, 1);
    a.splice(to, 0, mi);
    const [mn] = n.splice(from, 1);
    n.splice(to, 0, mn);
    setImgs(a);
    setNats(n);
    setEdit(to);
  };
  const build = () => {
    setErr(""); setPct(0);
    const spec: HeroGifSpec = { imgs, delay, nats };
    buildHeroGif(spec, W, H, setPct).then(({ dataUrl, frames }) => {
      setPct(null);
      onData({ [k]: dataUrl, [k + "__gif"]: "1", [k + "__gifsrc"]: heroGifSpecStr({ ...spec, imgs: spec.imgs.slice(0, frames), nats: nats.slice(0, frames) }), [k + "__nat"]: W + "x" + H, ...(data.src__ovbase != null && k === "src" ? { src__ovbase: "" } : {}) });
      setOpen(false);
    }).catch((e: Error) => { setPct(null); setErr(e.message || "Could not build the GIF."); });
  };
  /* `then` — open the modal once the set is back: the FIRST click on "Animated GIF" goes straight
     into Bynder (Jack 2026-09-16: "jump into the photo options in bynder and then go to this edit
     page"); the modal is what the photos land in */
  const pickMore = (then?: boolean) => openBynder(() => {}, { w: W, h: H }, {
    initial: imgs, max: HERO_GIF_MAX, title: "Add hero photos",
    hint: "tap photos across categories, then Done",
    onDone: (urls, assets) => {
      /* a photo already in the strip keeps its URL (and any crop); a new one brings its own size */
      const base = (u: string) => bynderBase(u) || u;
      const nextNats = urls.map((u, i) => { const j = imgs.findIndex((x) => base(x) === base(u)); if (j > -1) return nats[j] || ""; const a = assets && assets[i]; return a && a.w && a.h ? a.w + "x" + a.h : ""; });
      const next = urls.map((u) => { const j = imgs.findIndex((x) => base(x) === base(u)); return j > -1 ? imgs[j] : u; });
      setFrames(next, nextNats);
      if (then) { setErr(""); setOpen(true); }
    },
  });
  /* collapsed: the second button under Replace photo (the monolith's "Carousel" slot) */
  const button = <button type="button" className="imgc-gifbtn" data-testid="gif-open" onClick={() => { if (!imgs.length) { pickMore(true); return; } setErr(""); setEdit(0); setOpen(true); }}>{isGif && saved ? "✦ Edit animated GIF" : "✦ Animated GIF"}</button>;
  if (!open) return button;
  return (
    <>
      {button}
      <div className="herogif-ovl" onClick={(e) => { if (e.target === e.currentTarget && pct == null) setOpen(false); }}>
        <div className="herogif-modal" data-testid="gif-panel">
          <div className="herogif-t">Build a hero GIF</div>
          <div className="herogif-s">Add {HERO_GIF_MIN}–{HERO_GIF_MAX} photos, drag to reorder, then build.</div>
          <button type="button" className="herogif-byn" data-testid="gif-pick" onClick={() => pickMore()}>📁 {imgs.length ? "Add or change photos from Bynder" : "Select photos from Bynder"}</button>
          {imgs.length > 0 && <FilmField imgs={imgs} onImgs={(a) => {
            const n = a.map((u) => nats[imgs.indexOf(u)] || "");
            /* a drag keeps the open photo open, wherever it lands (a delete still goes through setFrames' clamp) */
            const openUrl = edit != null ? imgs[edit] : undefined;
            setFrames(a, n);
            if (openUrl !== undefined && a.length === imgs.length) { const j = a.indexOf(openUrl); if (j > -1) setEdit(j); }
          }} openBynder={() => pickMore()} noAdd selected={edit} onSelect={(i) => setEdit((e) => (e === i ? null : i))} />}
          {edit != null && imgs[edit] && imgs.length > 1 && (
            <div className="herogif-move" data-testid="gif-move">
              <button type="button" className="herogif-mv" data-testid="gif-move-earlier" disabled={edit === 0 || pct != null} onClick={() => moveFrame(edit, edit - 1)}>← Move earlier</button>
              <button type="button" className="herogif-mv" data-testid="gif-move-later" disabled={edit === imgs.length - 1 || pct != null} onClick={() => moveFrame(edit, edit + 1)}>Move later →</button>
            </div>
          )}
          {edit != null && imgs[edit] && (
            <div className="herogif-edit" data-testid="gif-edit">
              <ImgCard
                v={imgs[edit]}
                w={W} h={H}
                nat={nats[edit]}
                onNat={(d) => setNats((cur) => { const n = cur.slice(); n[edit] = d; return n; })}
                onUrl={(u) => { if (!u) { setFrames(imgs.filter((_x, j) => j !== edit), nats.filter((_x, j) => j !== edit)); return; } setImgs((cur) => { const n = cur.slice(); n[edit] = u; return n; }); }}
                openBynder={() => openBynder((u, a) => { setImgs((cur) => { const n = cur.slice(); n[edit] = u; return n; }); setNats((cur) => { const n = cur.slice(); n[edit] = a && a.w && a.h ? a.w + "x" + a.h : ""; return n; }); }, { w: W, h: H })}
              />
            </div>
          )}
          <div className="herogif-n">Plays automatically in every inbox — the safe choice. Outlook shows the first photo only.</div>
          {err && <div className="herogif-err">{err}</div>}
          <div className="herogif-f">
            <span className="herogif-sp" />
            <button type="button" className="herogif-cancel" disabled={pct != null} onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className="herogif-build" data-testid="gif-build" disabled={pct != null || imgs.length < HERO_GIF_MIN} onClick={build}>
              {pct != null ? `Building… ${pct}%` : "✦ Build GIF"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── ovedit: hero overlay baker (monolith openOverlayEditor) — persists the ov* keys
   the legacy def carries and bakes text + scrim + effect into ONE flattened image ── */
function OverlayField({ data, onData }: { data: Record<string, unknown>; onData: SetData }) {
  const [open, setOpen] = useState(false);
  const [spec, setSpec] = useState<OverlaySpec>(() => ({
    kicker: cx(data.ovKicker), head: cx(data.ovHead), sub: cx(data.ovSub),
    col: cx(data.ovCol) || "#ffffff",
    align: (cx(data.ovAlign) === "center" ? "center" : "left"),
    v: (["top", "middle", "bottom"].includes(cx(data.ovV)) ? cx(data.ovV) : "bottom") as OverlaySpec["v"],
    size: parseInt(cx(data.ovSize), 10) || 40,
    scrim: parseInt(cx(data.ovScrim), 10) || 35,
    fx: "none",
  }));
  const [busy, setBusy] = useState(false);
  const active = cx(data.ovOn) === "1" && !!(cx(data.ovHead) || cx(data.ovKicker) || cx(data.ovSub));
  if (!open) {
    return (
      <>
        <button type="button" className="esbtn2 solid" style={{ width: "100%" }} onClick={() => setOpen(true)}>✎ Edit Overlay — Text &amp; Effects</button>
        <div className="mut2" style={{ marginTop: 6 }}>{active ? "Overlay is on — click to edit or change it." : "Add a headline, kicker or effect on top of the photo."}</div>
      </>
    );
  }
  return (
    <div className="repmodal-ovl on" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div className="repmodal">
        <div className="repmodal-hd"><b>Edit Overlay — Text &amp; Effects</b><button type="button" className="repmodal-x" onClick={() => setOpen(false)}>×</button></div>
        <div className="repmodal-bd">
          <div className="rep-f"><label>Kicker</label><input className="rep-in" value={spec.kicker} onChange={(e) => setSpec({ ...spec, kicker: e.target.value })} /></div>
          <div className="rep-f"><label>Headline</label><input className="rep-in" value={spec.head} onChange={(e) => setSpec({ ...spec, head: e.target.value })} /></div>
          <div className="rep-f"><label>Subtext</label><input className="rep-in" value={spec.sub} onChange={(e) => setSpec({ ...spec, sub: e.target.value })} /></div>
          <div className="rep-f"><label>Align</label><Seg2 value={spec.align} opts={[["left", "Left"], ["center", "Center"]]} onPick={(v) => setSpec({ ...spec, align: v as OverlaySpec["align"] })} /></div>
          <div className="rep-f"><label>Position</label><Seg2 value={spec.v} opts={[["top", "Top"], ["middle", "Middle"], ["bottom", "Bottom"]]} onPick={(v) => setSpec({ ...spec, v: v as OverlaySpec["v"] })} /></div>
          <div className="rep-f"><label>Scrim ({spec.scrim}%)</label><input type="range" min={0} max={80} value={spec.scrim} onChange={(e) => setSpec({ ...spec, scrim: +e.target.value })} /></div>
          <div className="rep-f"><label>Effect</label><Seg2 value={spec.fx} opts={OVOPTS} onPick={(v) => setSpec({ ...spec, fx: v })} /></div>
          <button type="button" className="repmodal-done" disabled={busy || !cx(data.src)} onClick={() => {
            setBusy(true);
            const base = cx(data.src__ovbase) || cx(data.src);
            bakeOverlay(base, spec)
              .then((url) => {
                onData({
                  src: url, src__ovbase: base, ovOn: "1",
                  ovKicker: spec.kicker, ovHead: spec.head, ovSub: spec.sub, ovCol: spec.col,
                  ovAlign: spec.align, ovV: spec.v, ovSize: String(spec.size), ovScrim: String(spec.scrim),
                });
                setBusy(false); setOpen(false);
              })
              .catch(() => setBusy(false));
          }}>
            {busy ? "Baking…" : "Bake into photo"}
          </button>
          <div className="mut2">Bakes text + scrim + effect into ONE flattened image (2×, JPEG) — the render stays a single email-safe &lt;img&gt;. Host the result before send.</div>
        </div>
      </div>
    </div>
  );
}

/* Overlay-effect select INSIDE the image band's card (monolith __overlayInlineHtml) */
/* ── ✎ EDIT OVERLAY — TEXT & EFFECTS (2026-09-18, dev/QA) ─────────────────────────────
   Jack: "is that exactly how it is on the html version?" — now it is. The card carries the
   monolith's own control (index.html L14304: the full-width solid button + the `.mut2` status
   line), and clicking it opens the monolith's own editor — `openOverlayEditor(m, key)`, vendored
   verbatim in vendor/ovSky.js with its Text / Style / Effect tabs, approved lockups, scrim,
   position, SKY EXTENSION, fade, badges and seasonal overlays. The editor mutates `m.data` and
   bakes through `__ovCommit` exactly as the old form does; the one React-side job is to copy
   `m.data` back onto the block whenever the editor reports (esLive / esRecord / esInsp) and when
   the modal closes. services/skyExtend.ts still holds the gate. */
/** empty the editor's eyebrow / headline / subtext boxes through their own input events, so the
 *  editor's model and live preview follow (used when a cleared overlay is reopened) */
export function clearSeededText(root: ParentNode = document): void {
  const ins = Array.from(root.querySelectorAll<HTMLInputElement>(".ovm input:not([type=range]), .ovm textarea")).slice(0, 3);
  ins.forEach((el) => {
    const set = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), "value")?.set;
    if (set) set.call(el, ""); else el.value = "";
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
  });
}
export function OverlayEditorCard({ k, data, onData, slot }: { k: string; data: Record<string, unknown>; onData: SetData; slot?: Slot | null }) {
  const latest = useRef(data); latest.current = data;
  const on = ovActive(data, k);
  const busy = useRef(false);
  const [misfit, setMisfit] = useState("");
  const sk = slotKey(slot);
  /* ── KEEP THE BAKE TRUE TO THE BLOCK (2026-10-09) ── services/overlaySession.ts
     Re-bake from the pristine photo when the bake no longer fits what the block shows:
     the Height dropdown changed (the old bake was squashed into the new slot), the bake
     predates slot-aware baking (no `ovSlot`), or the requester replaced the photo (the
     overlay was still sitting on the old picture). */
  const cur = cx(data[k]); const base = cx(data[k + "__ovbase"]); const stamped = cx(data.ovSlot);
  useEffect(() => {
    if (busy.current) { if (/^https?:/i.test(cur) && isBake(cur) && stamped === sk) busy.current = false; return; }
    if (!cur || !on) return;
    if (typeof document !== "undefined" && document.querySelector(".ovm-back")) return;   /* the editor is open — it owns the bake */
    /* A REPLACED PHOTO IS NOT RE-BAKED BEHIND THE REQUESTER'S BACK (measured 2026-10-09: an
       automatic re-apply carried a 30% sky band onto a porch close-up and an interior, painting
       sky over a wall). The new photo shows plain and the card says so; opening the editor
       previews the overlay on it before anything is baked. Only a SIZE change re-bakes on its
       own — same photo, same look the requester already approved, new frame. */
    const resized = !!base && !!sk && isBake(cur) && stamped !== sk;
    if (!resized) return;
    busy.current = true;
    rebake(latest.current, k, slot, (p) => onData(p), () => latest.current, undefined, setMisfit);
  }, [cur, base, stamped, sk, on, k]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = () => {
    const m: OvModel = { type: "echimg", data: { ...latest.current } } as OvModel;
    /* a photo picked after the last bake is the photo now — never re-bake the old one (monolith __ovExtOpen guard) */
    const b0 = cx(m.data[k + "__ovbase"]), c0 = cx(m.data[k]);
    if (b0 && c0 && !isBake(c0) && b0 !== c0) delete m.data[k + "__ovbase"];
    setMisfit("");
    const { sync } = startSession(m, k, slot, (p) => onData(p), () => latest.current, setMisfit);
    /* the editor's own Cancel does not report — sync when the modal leaves the page. The hook
       STAYS registered: Done closes the modal first and bakes + hosts after (async), and each of
       those reports through esLive — clearing the hook here lost the bake (measured 2026-09-18). */
    const mo = new MutationObserver(() => {
      if (document.querySelector(".ovm-back")) return;
      mo.disconnect();
      /* a FIRST open closed without Done (✕ / Cancel) leaves no overlay: the editor's Cancel restores
         the snapshot it took AFTER the Email Refresh look was seeded, so take the seeded fields back
         off or the block keeps text that was never baked. Done sets ovOn="1" (vendor L2468). */
      if (fresh && m.data.ovOn == null) for (const f of FRESH_FIELDS) m.data[f] = preSeed[f];
      sync();
    });
    /* the old form seeds a "First-Time Homebuyer / Webinar" starter whenever the text is empty.
       That is fine the first time, but on an overlay the requester already cleared (badges only,
       sky only) it re-appears on every open and Done bakes it — measured 2026-10-09: removing the
       badges left a Webinar hero nobody typed. So a cleared overlay reopens cleared. */
    const cleared = m.data.ovOn != null && !cx(m.data.ovKicker) && !cx(m.data.ovHead) && !cx(m.data.ovSub);
    /* THE FIRST OPEN STARTS ON THE APPROVED LOOK (2026-10-10, Jack) — not the old form's "First-Time
       Homebuyer / Webinar" starter. A never-edited hero opens on the Email Refresh card: "Just
       Announced / Coming Soon", top, the Figma sizes; its sky passes through the same house-safe cap. */
    const fresh = m.data.ovOn == null && !cx(m.data.ovKicker) && !cx(m.data.ovHead) && !cx(m.data.ovSub);
    /* Seeded BEFORE the editor opens: its preset row was removed 2026-08-28, so there is no card to
       click, and it only plants the Webinar starter when the text is empty — with these set, it
       doesn't. Frame (ovBoxH) stays the slot's; the sky goes through the house-safe cap below. */
    const preSeed: Record<string, unknown> = {};
    for (const f of FRESH_FIELDS) preSeed[f] = m.data[f];
    if (fresh) {
      const p: Record<string, string> = (__OVPRESETS && __OVPRESETS.refresh) || {};
      for (const f of FRESH_FIELDS) if (p[f] != null) m.data[f] = p[f];
    }
    openOverlayEditor(m, k);
    if (cleared) clearSeededText();
    /* SKY NEVER CUTS OFF THE HOUSE (2026-10-10) — before the editor reads the Sky slider, point it at
       a wider Bynder slice of the same photo (services/overlaySession.ts `applySkyBase`), and hold the
       slider at the most sky this photo can take without cropping the house. Capture phase, so the
       editor's own handler sees the corrected value and its live preview shows the real result. */
    const ovmEl = document.querySelector(".ovm");
    if (ovmEl) {
      ovmEl.addEventListener("input", (e) => {
        const t = e.target as HTMLInputElement | null;
        if (!t || !t.dataset || t.dataset.rng !== "ovSky") return;
        m.data.ovSky = t.value;
        const applied = applySkyBase(m, k, slot);
        if (String(applied) !== String(t.value)) t.value = String(applied);
      }, true);
      /* the sky can also change WITHOUT the slider — the Email Refresh card sets 32. Done re-plans it
         before the editor bakes (capture), and after any click the slider is re-fired so the preview
         and the slider show the capped value too. */
      ovmEl.addEventListener("click", (e) => {
        const t = e.target as Element | null;
        if (t && t.closest && t.closest("[data-done]")) applySkyBase(m, k, slot);
      }, true);
      ovmEl.addEventListener("click", () => setTimeout(() => {
        const r = ovmEl.querySelector<HTMLInputElement>('[data-rng="ovSky"]');
        if (!r || !document.body.contains(r)) return;
        const want = String(m.data.ovSky == null ? "0" : m.data.ovSky);
        if (want !== r.value || (+want > 0 && m.data.ovPick == null)) { r.value = want; r.dispatchEvent(new Event("input", { bubbles: true })); }
      }, 0));
      /* the fresh open's seeded sky (32) was set before these listeners — re-plan it through the cap now */
      if (fresh) { const r = ovmEl.querySelector<HTMLInputElement>('[data-rng="ovSky"]'); if (r) { r.value = String(m.data.ovSky == null ? "0" : m.data.ovSky); r.dispatchEvent(new Event("input", { bubbles: true })); } }
    }
    /* EFFECT TAB IS BACK (Jack, 2026-10-09 — roadmap 2.2 "hero overlays: text / sky / event
       image"). It was parked 2026-09-19 by deleting the tab from the rendered modal; the editor
       itself was never touched, so restoring it is just not deleting it. The Effect tab carries the
       Included badge and the seasonal overlays (fireworks, confetti, sparkle, snow), and the
       editor's own rule keeps the two apart — picking one turns the other off. */
    mo.observe(document.body, { childList: true });
  };
  if (!cx(data[k]) && !cx(data[k + "__ovbase"])) return null;
  return (
    <div className="imgc-alt imgc-width" style={{ display: "block" }} data-testid="ov-card">
      <button type="button" className="esbtn2 solid" data-ovedit data-ovk={k} data-testid="ov-open" style={{ width: "100%" }} onClick={open}>✎ Edit Overlay — Text &amp; Effects</button>
      <div className="mut2" data-ovstatus data-ovk={k} style={{ marginTop: 6 }}>{misfit || (on && isBake(cur) ? "Overlay is on — click to edit or change it." : on ? "The overlay isn't on this photo yet — click Edit Overlay to apply it." : "Add a headline, kicker or effect on top of the photo.")}</div>
    </div>
  );
}

function OverlayFxSelect({ data, onData }: { data: Record<string, unknown>; onData: SetData }) {
  const cur = cx(data.src__ovfx) || "none";
  const [busy, setBusy] = useState(false);
  const apply = (fx: string) => {
    const base = cx(data.src__ovbase) || cx(data.src);
    if (fx === "none") { onData({ src: base, src__ovfx: "none" }); return; }
    if (!base) return;
    setBusy(true);
    bakeOverlay(base, { kicker: "", head: "", sub: "", col: "#ffffff", align: "left", v: "bottom", size: 40, scrim: 0, fx })
      .then((url) => { onData({ src: url, src__ovbase: base, src__ovfx: fx }); setBusy(false); })
      .catch(() => setBusy(false));
  };
  return (
    <div className="imgc-alt imgc-width" style={{ display: "block" }}>
      <label>Overlay effect</label>
      <select className="imgc-wsel" value={cur} disabled={busy} onChange={(e) => apply(e.target.value)}>
        {OVOPTS.map(([v, lab]) => (<option key={v} value={v}>{lab}</option>))}
      </select>
      <div className="mut2" style={{ marginTop: 6 }}>{busy ? "Baking…" : cur !== "none" ? "Effect baked into the photo." : "Add fireworks, confetti, sparkle or snow on top of the photo."}</div>
    </div>
  );
}

/* ── the inspector ─────────────────────────────────────────────── */
export default function Inspector({ block, onData, onOpenBynder, gr, onDelete, deleteWhy, echCtx, doc, onFlash, onToast, onSwap, onSwapMany, onPacks, onAddBelow, onInsertBelow, onReplaceSection, onSelectBlock, onDeleteBlock, onMoveBlock, reqKeys, m2 }: Props) {
  /**
   * WHERE THE FIELDS COME FROM (audit row 1).
   *
   * For an ECH block the field list is NOT authored here: `echRegistry` builds it
   * at runtime from the block's own `@GUARDRAILS.fields` map — keys, labels,
   * defaults and DECLARED TYPES all from Content Builder — and registers it as the
   * def's `fields`, exactly as the monolith's `__echRegister` writes `M[key].fields`
   * (index.html L8745–8754). So resolving the def by type covers both worlds:
   * a legacy block gets its hand-written array, an ECH block gets Layne's.
   *
   * The declared type → control mapping lives in `echFields()`, not here:
   *   image → the image card · url → url control · lines>1 → textarea ·
   *   number/tel → text · everything else → text.
   *
   * `blockDef` may legitimately be undefined for a beat: ECH types only exist
   * after /sfmc-blocks resolves, and a doc restored ahead of that would otherwise
   * throw on `.fields`. An honest empty panel beats a white screen.
   */
  const def = blockDef(block.type);
  const d = block.data;
  /* the CAP lookup is deliberately narrower than the field list: `gr.fields` here
     is echRegistry's filtered `limits` map, so an image/url/number/tel field is
     never clamped (monolith `_lim`) while still being editable above. */
  const rules = gr?.fields ?? {};
  /* the WIDER map, for format checks only — see the note on Grc. `gr.fields` is echLimits and
     cannot answer for a url/number/tel field or an uncapped text one, which is where most
     declared patterns live. */
  const patRules = patternRulesFor(block.type);
  /* every hook runs before the guard below — an early return above a useState
     changes hook order between renders (react-hooks/rules-of-hooks) */
  const [dynOn, setDynOn] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  /* ── ECH TEXTAREAS GROW WITH THEIR CONTENT (audit row 18, monolith
     L13599–13623) ────────────────────────────────────────────────────────────
     A fixed-height box scrolls its own first line out of view as soon as the
     copy is longer than the box, and a URL field shows the first third of a
     link. The cap already bounds how tall this can get, so the box follows the
     text.

     THE MULTI-PASS IS NOT SUPERSTITION. A panel measured once, against fallback
     font metrics, showed 3 of a 132-character maps URL's 6 lines. So: on render,
     again next frame, again at +140ms once layout settles, and again when
     `fonts.ready` resolves. The per-render pass also covers typing, because the
     values are controlled and every keystroke re-renders. */
  /* the Featured Plan container's Choose card opens the stepped wizard (2026-08-20,
     Jack: "select feature plan … switch between floor plans and communities") */
  const [planWiz, setPlanWiz] = useState(false);
  /**
   * A SECTION ADD (2026-08-22) — "+ Add a home" inside one section opens the picker
   * narrowed to that section's community, capped at its free slots, with the blank
   * card kept as the picker's own footer link. `draft` is the closure EchHomes handed
   * up, because only it knows which slot is free.
   */
  const [addSec, setAddSec] = useState<{ name: string; open: number; draft: () => void } | null>(null);
  /* the Featured panel's spreadsheet door — a hook, so it lives up here with the
     others and never behind one of this component's early returns */
  const planSheetRef = useRef<HTMLInputElement | null>(null);
  /* (2026-08-21: the level dropdown and its header-bar title sync were removed the
     same day they landed — Jack: "that's my fault, remove the attached." The headline
     is the Table Header Bar's own Section Title field; the level is the wizard's.) */
  /* ── UP TO 5 FEATURED BLOCKS (2026-08-21, Jack: "i should be able to add up to 5").
     The Add card inserts another instance of THIS block below; the cap counts the
     whole document's instances of the type — five featured cards is the section's
     ceiling whichever way they were placed. */
  /* ── THE SECTION IS BOTH VARIANTS (2026-09-18). A document that already holds a
     One-button card next to a Two-button card is ONE section to the requester — the
     swap and the label write below move all of it, and a card list keyed on the exact
     type would silently leave the other variant behind. */
  const planCards = (doc || []).filter((b) => echSameSection(block.type, b.type))
    .map((b) => ({ id: b.id, type: b.type, data: (b.data || {}) as Record<string, unknown> }));
  /* drop card A on card B → move A's BLOCK before/after B's in the document, so
     the canvas order and the card order are the same fact */
  const planReorder = (fromId: string, targetId: string, after: boolean) => {
    if (!onMoveBlock || !doc) return;
    const ti = doc.findIndex((b) => b.id === targetId);
    if (ti > -1) onMoveBlock(fromId, ti + (after ? 1 : 0));
  };
  const planCount = planCards.length || 1;
  /* THE CAP COMES FROM MARKETING CLOUD. `featured-max-5` is authored on the
     template (maxCombinedCount across ECH_Featured1CTA + 2CTA), so Layne can move
     it to 6 and this section follows — no app change, no redeploy. The literal 5
     is only the fallback for a template that declares nothing; a missing rule
     means "no opinion", never "none allowed". */
  const planCap = echFeaturedMax(doc, 5);
  const planAddBelow = onAddBelow && planCount < planCap ? onAddBelow : undefined;
  const planAddLeft = Math.max(0, planCap - planCount);
  const growAll = useCallback(() => {
    const host = rootRef.current;
    if (!host) return;
    host.querySelectorAll<HTMLTextAreaElement>(".essec.echf textarea").forEach((el) => {
      el.style.height = "auto";
      el.style.height = el.scrollHeight + 2 + "px";
    });
  }, []);
  /* no dep array on purpose: after EVERY render, which is what makes typing grow the box */
  useLayoutEffect(() => { growAll(); });
  useEffect(() => {
    /* a panel rendered while the inspector is still closed measures 0 — again next frame */
    if (typeof window !== "undefined" && window.requestAnimationFrame) window.requestAnimationFrame(growAll);
    const t = setTimeout(growAll, 140);
    try { if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => growAll()); } catch { /* no FontFaceSet here */ }
    return () => clearTimeout(t);
  }, [growAll]);
  /* ── WARM THE DIVISION FEEDS WHEN A FED BLOCK IS SELECTED (2026-08-22) ─────────
     Measured live on the previous build: the first open of the Floorplans step took
     12.2s with "Loading the live inventory…" up — `/plans.json?div=JAX` cold. Caching
     it (feedDirect) made the SECOND open instant and left the first one exactly as
     slow, which is not what Jack asked for ("still takes forever to load floor
     plans"). The wait is unavoidable but it does not have to be spent watching a
     modal: selecting the block is minutes of panel work before anyone clicks Choose,
     so the fetch starts there and the click usually finds it already resolved.
     Fire-and-forget by construction — a warm never renders, never toasts, and its
     failure is the step's own problem to report when it is opened. */
  const warmDiv = echCtx ? String(echCtx.divisionCode || "").trim() : "";
  const warmFed = !!def && echAlwaysDyn(echNameOf(block.type));
  useEffect(() => {
    if (!warmFed || !warmDiv) return;
    void feedDirect("plans", warmDiv).catch(() => { /* silent by design */ });
    void feedDirect("homes", warmDiv).catch(() => { /* silent by design */ });
  }, [warmFed, warmDiv]);
  if (!def) {
    return (
      <div className="inspx">
        <div className="esemptyi" style={{ padding: "18px 0" }}>
          {/^sfmc_/.test(block.type) ? "Loading this block from Marketing Cloud…" : "No editable content."}
        </div>
        {onDelete && <button type="button" className={deleteWhy ? "esdelf off" : "esdelf"} aria-disabled={deleteWhy ? true : undefined} title={deleteWhy || undefined} onClick={onDelete}>Delete this block</button>}
      </div>
    );
  }
  /* Manual/Dynamic strip: a real toggle only on blocks with a livefill field
     (the React stand-in for the monolith's Dynamic pane); disabled elsewhere. */
  const liveF = def.fields.find((f) => f.type === "livefill");
  /* `echblock` is the missing-block row: nothing to edit, so nothing to fill either.
     Without it here the panel drew a Manual/Dynamic strip with Dynamic greyed out — a
     dead control on a block that cannot hold a value, which is exactly what this
     codebase strips on sight (the greyed Dynamic toggle, the strip on ECH_SocialIcons). */
  const noStrip = block.type === "divider" || block.type === "spacer" || block.type === "echblock";

  /* ── ECH MODE LIVES ON THE BLOCK, NOT IN THE PANEL (code review 2026-08-19 #1) ──
     THE BUG THIS FIXES. Mode was React `useState(false)`, so (a) it reset on every
     re-select — the Inspector is keyed `key={sel.id}` — and (b) the FILL never saw
     it: `echFillAll` reads `m.data.__echMode`, and nothing wrote that key, so every
     ECH block was permanently Dynamic and every fed field was un-editable. You
     typed, and the next fill put the feed value straight back (probe A: typing into
     ECH_Logo's Community name read back "Elevon"). Worse, the strip drew MANUAL as
     the active state on every ECH block — ECH blocks have no `livefill` field, so
     the Dynamic button never rendered — so the panel claimed Manual while the feed
     overwrote. Mode is now `__echMode` on `block.data`, defaulting to dynamic,
     exactly as the monolith persists it (index.html L11182, L13728). */
  const echName = echNameOf(block.type);
  const echf = isEch(block.type);
  /* ── CONTACT US IS LOCKED EXCEPT ITS URL (audit row 8, monolith L11179,
     L13303) ──────────────────────────────────────────────────────────────────
     Jack, 2026-08-18: Contact Us "should be locked outside the url". The block's
     copy, photo and layout all come from the division's own SFMC asset, so
     nothing here is a per-send choice except where the button points. Only the
     `type:"url"` fields render; the block still gets the locked sentence AND its
     reference rows, with the URL editable above them. */
  const mostlyLocked = echf && echMostlyLocked(echName);
  /* The link the block's own markup already sends with — the placeholder for its url
     field, and what Copy/Open fall back to. Only on a MOSTLY-LOCKED block: everywhere
     else an empty url field genuinely means empty, and offering the block's first href
     as a suggestion would be us inventing a default. See echEffectiveLink. */
  const echLinkPh = mostlyLocked ? echEffectiveLink(d.__html) : "";
  /* What Copy/Open should act on for a url field: the typed value when it is a real
     destination, otherwise the block's own link. `val || echLinkPh` was WRONG and the
     test caught it — Layne's shipped `https://` placeholder is truthy, so it won the
     `||` and left the pair dimmed on a block that plainly has somewhere to go. */
  /* `ph` is the fallback: the block's own link on a single-link block, or on a
     per-button Contact Us field, THAT BUTTON's own href (2026-08-28) — passing it in
     is what stopped both of NEV's fields offering the phone number. */
  const echLinkFor = (v: string, ph: string): string => (urlOk(v) ? v : (ph || v));
  /* ── ONE URL FIELD PER BUTTON ON CONTACT US (2026-08-28, NEV) ─────────────────
     NEV reported two buttons and one URL box. The block declares a single
     `ContactUrl` — and in the bundled pull, nothing at all, because its
     `@GUARDRAILS` header sits in slot content `assetHtml()` never walks — while the
     BUTTONS come from the division's own layoutblock, swapped in by `echIscPatch`:
     NEV's has two ("Call Us" / "Email Us"), the generic fallback has one. A
     constant field count therefore cannot be correct, so they are read out of the
     markup the division actually gets (see echContact).

     Fills ONLY a block that declares nothing — the moment Layne declares one url
     per button in `@GUARDRAILS`, hers win and this goes quiet on its own. Rendered
     in the standard `.essec.echf` boxes with no added prose (Jack, 2026-08-28):
     each field's placeholder is that button's live destination, which is the whole
     explanation and needs no sentence. */
  const contactFlds = contactDerives(echName, def.fields.length) ? contactFields(d.__html) : [];
  /* AND THE BUTTONS A DECLARATION STOPS SHORT OF (2026-08-28). Once the backend
     guardrails fix lands, this block declares ONE url — right for the button every
     division shares, wrong for NEV's second one. Layne's field keeps button 1 with
     her label/cap/pattern; the markup fills in from button 2 on. See
     `contactExtraFields`. */
  const contactExtra = contactExtraFields(echName, d.__html, def.fields.filter((f) => f.type === "url" && f.k).map((f) => f.k as string));
  /** the list the panel RENDERS — declared fields, or Contact Us's derived ones */
  /* ── THE RECORD-FILLED CONTACT US BLOCK (2026-09-25) ────────────────────────────
     Its buttons are edited in the Buttons card below (`ContactButtons`), so the
     read-only "Set from your division's record" label box and the derived per-button
     link boxes (which wrote a key nothing in this block reads) are left out here. */
  const cuSt: CuState | null = echf ? echContactChoiceState(block as DynBlock, echCtx) : null;
  const panelFields: readonly FieldDescriptor[] = cuSt ? def.fields.filter((f) => String(f.k || "").toLowerCase() !== "cta1label")
    : contactFlds.length ? contactFlds
    : contactExtra.length ? [...def.fields, ...contactExtra] : def.fields;
  /** a derived Contact Us field's placeholder is ITS OWN button's current href */
  const contactPh: Record<string, string> = {};
  contactButtons(contactFlds.length || contactExtra.length ? d.__html : "").forEach((b) => { contactPh[b.k] = b.href; });
  const mBlk = block as DynBlock;
  /* ── THE BLOCK'S FEATURE LEVEL, AND THE PANEL'S CAPTIONS FOLLOW IT (2026-08-20) ──
     `echFeed` drives the per-field "from …" caption AND the resolved-value preview
     under each field in Dynamic mode (see the `rawSrc` / `echSrc` pair below). Read at
     the DEFAULT level it would have told a requester at Homesites level that Beds comes
     from `plan.self.beds` and previewed a floorplan's bed count, while the canvas
     showed the home's — the panel and the email disagreeing about the same field.
     One `echFeatOf` read fixes both surfaces because both read `echFeed`. */
  const echFeat = echFeatOf(mBlk);
  const echFeed = echName ? echDynFor(echName, echFeat) : null;
  const echOn = echDynOn(mBlk);
  /* THE STRIP RENDERS ONLY WHERE A FEED SITS BEHIND THE BLOCK AND IT HAS FIELDS
     (monolith L13268–13272). A locked block gets none; a DYNAMIC-ONLY block
     (ECH_SocialIcons) gets none either, because there is no Manual mode to offer —
     a division's social handles are not a per-send decision. The greyed-out
     "Dynamic" this repo used to draw on every block read as broken, which is why
     the monolith removed it on 2026-08-18. */
  /* A STRIP ONLY WHERE BOTH MODES ARE REAL. Dynamic-only (ECH_SocialIcons) and
     manual-only (ECH_MainImage — "remove the dynamic option for main image",
     2026-08-20) both get NO strip, for the same reason from opposite ends: one of
     the two buttons would be a control that cannot change anything.
     ALWAYS-DYNAMIC (the listing table + both Featured blocks, 2026-08-22) gets no
     strip either: their content IS inventory, and Manual only ever meant "freeze
     every row and retype it". Editing a filled row still works — that runs on the
     per-field takeover (`socIsOff`), which never needed a mode. */
  const echStrip = !!(echFeed && def.fields.length && !echDynOnly(echName)
    && !echManualOnly(echName) && !echAlwaysDyn(echName));

  /* ── AN EDIT ON A FED CARD OR ROW HAS TO CLAIM THAT FIELD (2026-08-22) ──────────
     The plan cards and the homes list write with a bare `onData`, which was correct
     while Dynamic was read-only. Now that an always-dynamic block is fed AND editable,
     a bare write would be reverted by the very next fill — you would type a price and
     watch it change back. So the write is wrapped: any key the feed sources is added to
     `__socOff` (the same per-field takeover the field loop uses), and CLEARING that key
     hands it back to the feed. One mechanism for "the requester owns this value", not
     two — identical to how a typed URL has always behaved. */
  /* ── ONE CLAIM, TWO EDIT SURFACES (2026-08-22) ────────────────────────────────
     The body of this moved to `echClaimPatch` in echDyn so the CANVAS can use the
     identical rule: Jack asked to edit the listing table on the email itself, and
     the only reason that was locked is that a canvas edit had no way to claim its
     field. The panel's behaviour is unchanged. */
  const fedEdit = (p: Record<string, unknown>) => onData(echClaimPatch(mBlk, p));

  /* ── THE FEATURED PANEL GETS THE HOMES LIST'S TWO DOORS (2026-08-22) ──────────
     Jack, comparing the two panels: "the featured plan it has a different view" —
     make it match. The listing table offers "Choose homes from inventory" and
     "Fill from a spreadsheet"; a Featured block offered neither, only the small
     Choose on the card itself. Same two controls, same dashed style, same wizard.

     THE SHEET MINTS CARDS, one per row, capped at the section's five — the same
     rule and the same insert-below mechanics as the table→cards swap, so a
     five-row sheet is five cards rather than one card and four dropped rows.
     Only the card's OWN fields are written (plan name, sq ft, beds, baths): a
     URL column belongs to a specific button, and guessing which one would put a
     link on the wrong CTA. */
  const PLAN_CARD_CAP = 5;
  /**
   * HOW MANY ROW SLOTS DOES THIS BLOCK ACTUALLY DECLARE? (2026-08-25)
   *
   * Counted off `home<n>Homesite` in the block's own guardrail — ECH_ListingTable
   * declares ten. Reported to the picker as its cap so the invitation and the markup
   * cannot disagree ("it says 15 … then says 10").
   */
  const echRowSlots = (() => {
    const declared = echMeta(block.type)?.declared || {};
    /* CONTIGUOUS from 1, and either field counts as a slot (2026-08-25, from an audit).
       `Math.max` of the indices would invite 7 picks for a block declaring rows 1, 2 and
       7; and `echPickMode` turns the rows picker on for `home1Homesite` OR `home1Plan`,
       so counting only Homesite would leave a Plan-only block back on the 15 default —
       the very drift this replaced. */
    let n = 0;
    while (declared["home" + (n + 1) + "Homesite"] || declared["home" + (n + 1) + "Plan"]) n += 1;
    return n;
  })();
  const planSheetFields = (h: Record<string, string>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    const nm = String(h.Plan || h.Homesite || "").trim();
    if (nm) out.PlanName = nm;
    if (h.SqFt) out.SqFt = h.SqFt;
    if (h.Beds) out.Beds = h.Beds;
    if (h.Baths) out.Baths = h.Baths;
    return out;
  };
  /* ── WHEN THE FEED HAS NOTHING, SAY SO (2026-08-22) ───────────────────────────
     Jack, on a JAX "Coming Soon" request: "why isn't the info populating." Two
     different answers had the same LOOK — placeholder rows and a grey hero — and the
     panel said neither: one community resolved with 0 plans and 0 homes (Raymonds
     Walk, a genuine pre-sale), the other did not resolve at all. A placeholder that
     never fills reads as a broken tool, so the panel names the case in one line.
     PANEL ONLY: the canvas keeps its placeholders and gets no prose (standing rule),
     and this never blocks anything — the rows stay hand-typeable. */
  const feedNote = (kind: "plans" | "homes"): React.ReactNode => {
    if (!echCtx || !echAlwaysDyn(echName)) return null;
    const ps = echPacks(echCtx);
    if (echCtx.pending) return null;                 /* still fetching — say nothing yet */
    if (!ps.length) {
      if (!(echCtx.picks || []).length) return null; /* nothing picked yet is not a miss */
      const nm = (echCtx.picks || []).map((x) => x.name).filter(Boolean).join(", ");
      return (
        <div className="ech-dyns ech-dynwarn">
          No live SFMC content for <b>{nm}</b> yet — type the rows you want, or pick a
          different community.
        </div>
      );
    }
    const n = ps.reduce((a, p) => a + (kind === "plans"
      ? (p.C.plans || []).length
      : (p.C.homesites || []).filter((h) => !/sold/i.test(String(h.status || ""))).length), 0);
    if (n) return null;
    const nm = ps.map((p) => p.name).filter(Boolean).join(", ") || "this community";
    return (
      <div className="ech-dyns ech-dynwarn">
        No live {kind === "plans" ? "floorplans" : "available homes"} for <b>{nm}</b> yet
        — type the rows you want, or choose from inventory once they are listed.
      </div>
    );
  };

  const onPlanSheet = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    sheetHomesFile(f).then(({ groups, err }) => {
      if (err) { if (onToast) onToast(err); return; }
      const rows = ([] as Record<string, string>[])
        .concat(...groups.map((g) => g.homes))
        .map(planSheetFields)
        .filter((r) => Object.keys(r).length);
      if (!rows.length) { if (onToast) onToast("No plan or homesite rows in that file."); return; }
      const kept = rows.slice(0, PLAN_CARD_CAP);
      /* row 1 fills THIS card — through the claim, so the next fill leaves it alone */
      fedEdit(kept[0]);
      /* rows 2–5 become siblings below, reversed because each insert lands directly
         under this block. Each seed carries its own claim for the same reason. */
      if (kept.length > 1 && onInsertBelow) {
        kept.slice(1).reverse().forEach((r) => {
          onInsertBelow(block.type, echClaimPatch(
            { id: "", type: block.type, data: {} } as DynBlock,
            { ...r, __echMode: "dynamic" },
          ));
        });
      }
      if (onFlash) {
        onFlash(rows.length > kept.length
          ? "Imported " + kept.length + " cards — " + (rows.length - kept.length) + " row"
            + (rows.length - kept.length > 1 ? "s" : "") + " didn't fit (five cards max)."
          : "Imported " + kept.length + " card" + (kept.length > 1 ? "s" : "") + ".");
      }
    });
  };

  /* the level is Step 1's focus, never a second control (monolith `__echLevel`) —
     EXCEPT that a block holding an explicit wizard pick is at community level by
     virtue of the pick, which names a community. `echBlockLevel` is the same function
     `echDynPatch` fills with, so the panel's per-field captions and resolved-value
     previews can never disagree with the canvas about which level a block is at.
     Identical to `echLevel(...)` for every block with no pick. (2026-08-20) */
  const echLvl = echCtx ? echBlockLevel(mBlk, echCtx) : "";
  const setEchMode = (v: string) => {
    /* DERIVE THE CURRENT MODE FROM `echDynOn`, NEVER RE-STATE THE DEFAULT. The
       monolith had this exact bug (Jack: "lot are stuck on dynamic"): the guard
       compared against a default of "manual" while the renderer defaulted to
       dynamic, so clicking Manual matched, returned early, and the mode never
       changed — the fields stayed read-only with no way out (index.html
       L13718–13733). Switching back to Dynamic needs no explicit refill here: the
       Studio's fill effect keys on `blocks`, so writing `__echMode` re-runs it. */
    if ((echDynOn(mBlk) ? "dynamic" : "manual") === v) return;
    onData({ __echMode: v });
  };
  /* inspector-level theme select (monolith __themeControlHtml — NOT a field) */
  const themeDef = block.type === "body" || block.type === "cta" ? "white"
    : block.type === "community" || block.type === "floorplan" || block.type === "event" ? "midnight" : null;
  const compat = COMPAT[block.type];
  /* alt fields directly after an image field merge INTO the image card (hidden) */
  const skip = new Set<number>();
  def.fields.forEach((f, fi) => {
    if (f.type !== "image") return;
    const nx = def.fields[fi + 1];
    if (nx && nx.type === "text" && /alt/i.test(String(nx.label || nx.k || ""))) skip.add(fi + 1);
  });
  /* ── THE SIZE SELECTS RIDE INSIDE THE PHOTO CARD (2026-09-17, Jack: "below the image, like
     between Animated GIF and the URL link") — Layne's imgWidth/imgHeight render as one row
     under the photo's buttons (services/imgSize.ts), so their own sections are skipped here. */
  const sizeKeys = def.fields.some((f) => f.type === "image") ? imgSizeKeys(def.fields) : null;
  if (sizeKeys) def.fields.forEach((f, fi) => { if ((sizeKeys.w && f.k === sizeKeys.w) || f.k === sizeKeys.h) skip.add(fi); });
  const sizeDefaults: Record<string, unknown> = {};
  if (sizeKeys) { const decl = echMeta(block.type)?.declared || {}; for (const k of [sizeKeys.w, sizeKeys.h]) if (decl[k] && decl[k].default != null) sizeDefaults[k] = decl[k].default; }
  /* a block with a hardcoded width (ECH_MainImage 500 / ECH_MainImage600 600) declares it on the
     image field or draws it on the img tag — either is the width beside a height-only dropdown */
  const sizeImgF = sizeKeys ? def.fields.find((f) => f.type === "image") : undefined;
  const sizeFixedW = sizeKeys && !sizeKeys.w ? ((sizeImgF && sizeImgF.w) || markupImgWidth(d.__html, (sizeImgF && sizeImgF.k) || "imgSrc")) : 0;
  const sizeSlot = sizeKeys ? imgSizeSlot(d, sizeKeys, sizeDefaults, sizeFixedW) : null;

  /* ── AMENITIES ARE PICKED, NOT TYPED (audit row 17, monolith L13294–13300 +
     L11032–11049) ──────────────────────────────────────────────────────────────
     The amenity blocks declare `iconN` + `labelN` pairs — 3, 4, or 8 of them — and
     the repo rendered every one of them raw: up to 17 controls, a Bynder card and
     a text box per amenity, with two of the eight cells showing a broken-image
     placeholder because their slots ship empty. Jack, 2026-08-19: "amenity block
     isn't reflecting what we did in the html version."
     So: hide the raw pairs, render ONE list from the library, click to toggle. The
     pairs come off the block's DECLARED fields, never a hardcoded cell count —
     which is why the live combined "Amenities Section (Bar + 1-8 Icons)" block
     works here with no code change. */
  const amPairs = echf ? amenPairs(def.fields) : [];
  /* no library (the amenity blocks haven't been pulled yet) → no picker, and the
     raw fields render exactly as before rather than leaving the block un-editable */
  const amLib = amPairs.length ? echAmenLib() : [];
  const amOn = !!(amPairs.length && amLib.length);
  const amKeys: Record<string, 1> = {};
  if (amOn) amPairs.forEach((p) => { amKeys[p.icon] = 1; amKeys[p.label] = 1; });
  const amPicks = amOn ? amenPicked(d, amPairs) : [];
  const pickAmen = (label: string) => {
    const next = amenToggle(amPicks.map((p) => p.label), label, amPairs.length);
    /* refused: removing this one would leave fewer than two, and one icon in a row
       built as a grid reads as a mistake (monolith L13697) */
    if (!next) { if (onToast) onToast("Keep at least " + AMEN_MIN + " amenities — pick another to swap this one out."); return; }
    /* `__amenTouched` — from here on the community preselect never overwrites their
       choice (monolith L13705), the same ownership idea as `__socOff` on a URL */
    onData({ ...amenApply(amPairs, amLib, next), __amenTouched: 1 });
  };

  /* ── THE SOCIAL CHANNEL ROWS (audit row 9, monolith L13296–13297 + L11699) ──
     ECH_SocialIcons' five URL fields are handled by ONE picker: name · the full
     url · a tick, "not on file" and unselectable where the division has no
     handle, `N of 5` on the label row, and a mini Copy/Open per row. The five
     raw fields are skipped in the loop below, exactly as the amenity pairs are. */
  const socOn = echf && socPickerOn(echName);
  const socK = socOn ? socKeys() : {};
  const soc = socOn ? socRows(mBlk, echCtx, echLvl, doc) : { rows: [], on: 0 };
  /* TOGGLING OFF HAS TO STICK. Clearing the URL alone lasts until the next fill —
     this block is DYNAMIC-ONLY, so `echFillAll` rewrites all five fields on every
     Step-1 change. The patch also writes `__socOff`, and `echDynFill` skips a
     flagged field from then on (monolith L11846). That is the whole feature. */
  const toggleSoc = (k: string, feedUrl: string) => onData(socTogglePatch(mBlk, k, feedUrl));

  /* ── THE LISTING TABLE'S HOME ROWS (audit row 10, monolith L13287–13288) ────
     Since 2026-08-21 the cards edit six of the seven fields IN PLACE, so only
     the URL field's built section is kept (rowUrl) — it carries the copy-link /
     open-in-tab machinery the card's Link section reuses. */
  const rowsOn = hasHomeRows(def.fields);
  const rowUrl: Record<number, React.ReactNode[]> = {};
  const rowOrder: number[] = [];

  /* ── THE FEATURED PLAN GETS THE SAME ROW (2026-08-20, Jack: "i would also like
     featured plan 2 buttons to reflect this new layout as well") ────────────────
     ECH_Featured1CTA / ECH_Featured2CTA declare ONE flat plan — PlanName, SqFt,
     Beds, Baths plus their three label fields — which rendered as seven stacked
     field cards. They fold into the plan CARD (photo, name, spec line — see
     EchPlanCards); the button fields stay ordinary panel sections below it.
     Detected off the DECLARED fields, not a block-name list, so a future plan
     block gets it for free. */
  const planOn = echf && !rowsOn
    && ["PlanName", "SqFt", "Beds", "Baths"].every((k) => (def.fields || []).some((f) => f.k === k));
  /* does a card hold a real pick? — hoisted to component scope 2026-08-22 because both
     the Dynamic pane and the (now always-dynamic) editable panel gate their cards on it */
  /* ── A COMMUNITY-LEVEL CARD HAS NO PICK KEY TO HAVE (2026-08-28, Jack:
     "cards doesn't show community elvel") ────────────────────────────────────────
     This tested `__planPick` / `__homePick` only. `ECH_FEAT.comms.pick` is `""`
     (echDyn L296) — community level stores NEITHER, by design — so every community
     card failed the test and the Dynamic pane rendered nothing but the dashed Choose
     pad. The editable mount has no such filter, so the two renderings of one block
     already disagreed at community level (the Veev cards-vs-table shape again).

     At comms level the pick IS the community, which lives in Step 1, so the honest
     test is whether the card carries community content. */
  const planHasPick = (bd: Record<string, unknown>) => {
    if (echFeat === "comms") {
      return Boolean(String(bd.CommunityName ?? bd.PlanName ?? "").trim()
        || String(bd[ECH_PLAN_PICK] ?? "").trim()
        || String(((echCtx && echCommInfo(echCtx)) || {}).name ?? "").trim());
    }
    return Boolean(String(bd[ECH_PLAN_PICK] ?? "").trim() || String(bd[ECH_HOME_PICK] ?? "").trim());
  };
  /* THE CARD OWNS THESE FIELDS (2026-08-21, Jack: "remove the edit details and
     work a clean set up for all of this") — they edit ON the card (click-to-edit
     name/specs, click-the-photo Bynder picker), so their flat field sections are
     dropped instead of buffered into a window. Everything else the block declares
     — BtnLabel/FloorplanUrl, btn1/btn2 pairs — falls through to the NORMAL field
     loop below the cards, keeping the approved-CTA select and the URL derivation
     machinery instead of duplicating them. The three *Label suffix fields ship
     the block's own defaults: the card renders the approved full-word reading
     ("sq ft · Beds · Baths") and exposes no editor for them — deliberate, not an
     oversight. */
  const PLAN_CARD_KEYS = new Set([
    "PlanName", "SqFt", "SqFtLabel", "Beds", "BedsLabel", "Baths", "BathsLabel",
    "PlanImage", "PlanImageAlt",
    /* Price edits on the spec line with the rest (Jack, 2026-09-22); PriceLabel
       ("From") gets no editor of its own, same as the three unit labels */
    "Price", "PriceLabel",
  ]);
  /* …and the block's OTHER fields — the button labels/URLs — ride in a collapsed
     "Buttons & links" section ON the card (2026-08-21, Jack off the flat-sections
     build: "also this stuff should be in each card with a drop down section").
     Same built field elements, so the approved-CTA select, URL bars, caps and
     counters all still apply — just housed per card instead of stacked below. */
  const planBtns: React.ReactNode[] = [];
  let planAt = -1;

  /* THE DYNAMIC PANE IS THE MONOLITH'S STEPPED PICKER (2026-08-20, per Jack: "for
     the dynamic portion i want to leverage the html version"). `DynLiveFill` takes
     `LiveFillField`'s props exactly, so this is a swap and not a rewire: it renders
     the selected-cards panel ("Homes · N selected" + Choose ›) and opens the
     Communities → Floorplans → Homesites wizard, in place of the search-box stopgap
     this pane used to show. `divisionCode` scopes the community step to Step 1's
     division rather than listing every community in the country. */
  /* `wizOnly` renders the SAME picker with no selected-cards panel, opened at once —
     the Featured Plan container's Choose card (2026-08-20). One picker, two doors. */
  const liveFill = (f: FieldDescriptor, wizOnly = false) => (
    <DynLiveFill mode={f.mode || "plans"} divisionCode={echCtx?.divisionCode} onToast={onToast}
      {...(wizOnly ? {
        hidePanel: true,
        autoOpen: true,
        onWizClose: () => { setPlanWiz(false); setAddSec(null); },
        ...(addSec ? {
          scopeName: addSec.name,
          onHandType: addSec.draft,
        } : {}),
      } : {})}
      /* ── THE COMMUNITY STEP IS THE DIVISION'S LIST AGAIN (2026-08-20) ─────────────
         `commNames={Step 1's picks}` lived here for one day. It stopped a
         cross-community pick being a silent no-op by making the pick impossible —
         the right fix for the wrong bug. Jack: "i want the right panel to have the
         other view where i don't even need a community — i click the + button and can
         select the info myself." So the CAUSE is fixed instead (the pick carries its
         community code; `echPacks` includes the wizard's packs; `echBlockLevel` reads
         a picked block at community level) and the step lists the division, which is
         what the live monolith's `commList()` does. `fixedFor` below is what keeps
         Step 1's own pick resolving from the packs already in hand. */
      /* ── AND ITS COLLECTIONS' INVENTORY WITH IT (2026-08-20, Jack: "full plan
         should pull in all related collections"). Scoping the community step to
         Step 1's pick was right and was not enough: a MASTERPLAN's own
         /sfmc-content pack has no plans at all — they live on its child collections
         — so the Floorplans step drew zero cards for Arlington Heights and again for
         Timber Ridge. `echPacks` is the picked community's pack plus one per
         collection, already fetched by `useEchDyn`; the wizard groups them by
         collection and the resolver reads the pick back out of whichever pack owns
         it. Empty for a plain community, so the legacy blocks and the
         single-community case are untouched. */
      fixedPacks={echf && echCtx ? echPacks(echCtx) : undefined}
      /* WHICH NAMES THOSE PACKS ANSWER FOR — Step 1's own picks, and nothing else.
         Without it `fixedPacks` would answer for every community the requester
         chooses, which is the cross-community no-op again one layer out. With it, a
         Step-1 masterplan still resolves to its collections in one hop and any other
         community is fetched. */
      fixedFor={echf ? (echCtx?.picks || []).map((p) => p.name).filter(Boolean) : undefined}
      /* ── THE PANE REOPENS WHERE THE REQUESTER LEFT IT (2026-08-20) ─────────────
         The level used to be `DynLiveFill` state seeded from `mode`, so switching a
         Featured block to Homesites, pressing Done and clicking the block again drew
         a Floorplans pane over a canvas full of homes. It is stored on the block now
         (`__featLevel`) and handed straight back in. The Listing table has exactly one
         level, so `echFeatOf` answers "homes" for it either way. */
      featLevel={echf ? echFeat : undefined}
      /* ── ONE CARD, ONE PICK — BUT THE SECTION HOLDS FIVE CARDS (2026-08-21,
         Jack: "why is there a max of one for the featured homes? sfmc should
         state the rules for that" — SFMC doesn't: the Featured blocks' authored
         guardrails carry layout and copy rules only, NO repetition cap; the
         section's cap of 5 is Jack's own Add-plan rule). So the wizard takes up
         to the section's remaining capacity, the first pick fills THIS block,
         and every extra pick becomes its own sibling block below (see onFill).
         ── AND THE LISTING TABLE PASSES ITS OWN ROW COUNT (2026-08-25) ────────────
         A division, via Feedback: "when you select the homes for a listing table it says
         it allows 15 homes to be selected, but once selected it then says it allows 10."
         Both numbers were real and neither was the block's: `DYN_MAX_HOMES` is 15 for
         monolith parity (its Dynamic_ListingTable emitted 15 text rows), while Layne's
         ECH_ListingTable declares home1..home10 — TEN slots. So the picker invited five
         picks that had nowhere to land. The cap is now counted off the block's own
         declared fields, so it cannot drift from the markup again; a block that declares
         no rows falls through to the picker's default. */
      pickCap={addSec ? Math.max(1, addSec.open)
        /* a pick now REPLACES the section (2026-09-25), so the whole section's cap is open */
        : (echf && f.mode !== "rows" ? (onReplaceSection && planOn ? planCap : Math.max(1, 6 - (planCards.length || 1))) : (echRowSlots || undefined))}
      /* ── THE PANE SEEDS FROM THE BLOCK, NOT FROM ITS OWN MEMORY (2026-08-20) ────
         `DynLiveFill` is unmounted in Manual mode and re-created on every re-select
         (the Inspector is keyed by block id), so picks held only in its state died
         with it: Manual → Dynamic redrew "Floorplans · 0 selected" over a canvas
         still showing the picked plan. `pickKeysBack` is the reverse of
         `planPickKey` / `homePickList`, resolved against every pack in hand, so the
         pane reads the block's own durable answer. */
      /* ── A SECTION ADD STARTS EMPTY (2026-08-22) ────────────────────────────────
         Seeding the block's existing picks into a scoped add read as "9 selected ·
         max 1" with every row disabled — the cap is the section's FREE slots, so the
         homes already in the table must not count against it. Driven live. */
      /* ── THE PICKER OPENS ON WHAT THE EMAIL SHOWS (2026-09-25) ───────────────────────
         Featured: the SECTION's picks for each level (echSectionMemory), so switching
         Floorplans ⇄ Homesites inside the picker lands on what was chosen there before.
         Listing table: a fed table holds no pick list, so the homes on screen are read back
         off its rows (`homePickFromRows`) — it opened with 0 ticked over five rows. */
      storedPicks={echf && !addSec ? (f.mode !== "rows" && onReplaceSection && planOn && planCards.length ? (() => {
        /* every card on screen opens ticked, positional ones included, and the memory is
           read for the SECTION's level, not the selected card's: a card just added from
           "Add card" sits at community level next to plan cards (2026-10-27, Layne) */
        const shown = echCtx && doc ? echPinShown(planCards as SectionCard[], doc as DynBlock[], echCtx) : planCards as SectionCard[];
        const mem = echSectionMemory(shown, echSectionLevel(planCards as SectionCard[]) || echFeat);
        return {
          plans: pickKeysBack(echCtx ? echPacks(echCtx) : [], "plans", mem.plans.join("\n")),
          homes: pickKeysBack(echCtx ? echPacks(echCtx) : [], "homes", mem.homes.join("\n")),
        };
      })() : {
        plans: pickKeysBack(echCtx ? echPacks(echCtx) : [], "plans", mBlk.data[ECH_PLAN_PICK]),
        homes: pickKeysBack(echCtx ? echPacks(echCtx) : [], "homes",
          String(mBlk.data[ECH_HOME_PICK] || "").trim()
            || (f.mode === "rows" && echCtx ? homePickFromRows(echPacks(echCtx), mBlk.data) : "")),
      }) : undefined}
      onFill={(packs, level, picks) => {
      /* ── THE WIZARD'S PACKS GO UP TO THE STUDIO FIRST (2026-08-20) ────────────────
         The pick is stored as a community CODE, so whatever resolves it later — the
         next `echFillAll` pass, a Step-1 change, a re-select of this block — needs the
         PACK for that code, and Step 1 may never have named that community. `onPacks`
         hands them to the Studio, which holds them as `EchCtx.pickPacks` for the rest
         of the session. Without this the canvas would move on this click and revert on
         the next fill pass: the silent no-op with a one-render delay. */
      if (onPacks && packs.length) onPacks(packs);
      /* ── ROUTE THE PICK BY BLOCK FAMILY (2026-08-20 — the second of the three
         faults behind Jack's "dynamic is not set up") ──────────────────────────
         The two branches below write `{items}` and `{rows, __title, __cols}` —
         the keys the LEGACY hand-written renderers consume (registry-extended.ts
         :375 and :417). An ECH block has no renderer of ours: it emits LAYNE'S OWN
         markup with its declared tokens substituted, so `items` would sit inert on
         the block and the pick would appear to work while changing nothing.
         An ECH pick therefore lands on the block's DECLARED fields, via
         `echPickPatch`, which reuses `echDynPatch` rather than mapping a second
         time — `ECH_DYN` stays the single place that knows which of Layne's fields
         a plan or a home fills. It also writes the explicit pick key in the SAME
         patch, so the canvas moves on this click instead of waiting for the next
         Step-1 change to trigger a refill pass. */
      if (echf) {
        /* ── AND ROUTE IT BY THE FEATURE LEVEL, NOT BY THE BLOCK'S MODE (2026-08-20,
           Jack: "i do want this to be dynamic where it can be used for communities,
           floor plan or homesites… should be dynamic") ──────────────────────────
           This used to read `f.mode === "rows"` — the block's ONE declared mode — so a
           Featured block's pick was always filed as a plan pick however the wizard's
           FEATURE LEVEL control was set. Pick Homesites, choose a home, press Done:
           `planPickKey` was handed a home key, returned "" (it refuses to invent an
           identifier), the plan pick was CLEARED, and the canvas fell back to the
           positional floorplan. The wizard offered three levels and two of them did
           nothing.
           WHICH KEY A LEVEL USES IS THE LEVEL TABLE'S ANSWER, not this file's:
           `echFeatPickKind` reads `ECH_FEAT[level].pick`. Community level consumes
           NEITHER key — Step 1 already chose the community — and deliberately leaves
           both stored picks alone rather than clearing them, so switching plans →
           community → plans comes back to the same floorplan.
           THE LISTING TABLE HONOURS THE LEVEL TOO (unpinned 2026-08-21 — Jack:
           "with both the homesite listing and the feature plan should be able to
           include the community or floor plan level as well … should adapt to each
           level"). Its plans and comms maps exist in `ECH_FEAT` now, so the wizard's
           level control drives real fills: Floorplans = plan rows, Community = one
           comparison row per community. The old pin was written when only the homes
           map existed and silently discarded the level the requester chose — the
           panel said Floorplans over a canvas of homesites, two renderings of one
           decision disagreeing. */
        const rows = f.mode === "rows";
        const feat = level;
        const want = echFeatPickKind(feat);
        /* AND THE SAME PACKS GO INTO **THIS** PATCH, not just into the Studio's state.
           `onPacks` above is a setState: `echCtx` is still the pre-click value on this
           tick, so resolving against it would return "" for a community Step 1 never
           named and the canvas would only catch up on the NEXT render. Merged here so
           the fill this click performs already sees the inventory the wizard loaded. */
        const fillCtx: EchCtx = {
          ...(echCtx || EMPTY_CTX),
          pickPacks: [...((echCtx || EMPTY_CTX).pickPacks || []), ...packs],
        };
        /* ── SWITCHING LEVEL CLEARS THE OLD LEVEL'S RENDERED CONTENT (2026-08-28) ──
           Jack: "when switching level and selecting it should only show from that
           level like i have homesites but then select and add floor plan level should
           only show the floor plan level", and "if i toggle back they should show
           again".

           The PICKS were never the problem — `__planPick` and `__homePick` are
           separate keys and this handler deliberately leaves the other one alone, so
           both levels' selections survive and toggling back restores them. What
           lingered was the RENDERED fields: each level's writer sets only the fields
           it owns, so a homesite's row/card values stayed on screen under a plan-level
           panel — two renderings of one decision disagreeing.

           So on a real level CHANGE the previous level's content fields are blanked
           and their claims dropped, and the new level's writer refills from its own
           pick. Nothing is destroyed: the picks stay, so the toggle is reversible. A
           re-pick at the SAME level is untouched — clearing there would throw away
           edits for no reason. */
        const prevFeat = String(mBlk.data.__featLevel || echFeatOf(mBlk) || "");
        const levelChanged = !!prevFeat && prevFeat !== feat;
        const stale: Record<string, unknown> = {};
        if (levelChanged) {
          const rowSfx = ["Homesite", "Plan", "SqFt", "Beds", "Baths", "Price", "Url"];
          for (let n = 1; n <= 10; n++) rowSfx.forEach((sfx) => { stale["home" + n + sfx] = ""; });
          PLAN_CARD_KEYS.forEach((k) => { stale[k] = ""; });
          const keep = String(mBlk.data.__socOff || "").split(",").filter(Boolean)
            .filter((k) => !(k in stale));
          stale.__socOff = keep.join(",");
        }
        /* ── A FEATURED PICK REPLACES THE WHOLE SECTION (2026-09-25, Jack) ───────────────
           One card per pick, in pick order, every card on the section's block and labels;
           the level's picks are remembered on every card for the toggle back. See
           echSection.echReplaceSection. Community level has no per-card pick, so it keeps
           the single-card path below. */
        if (!rows && onReplaceSection && planOn && planCards.length && (want === "plan" || want === "home")) {
          const allPacks = packs.concat(echCtx ? echPacks(echCtx) : []);
          const keys = want === "plan"
            ? (picks.plans || []).map((k) => planPickKey(allPacks, k)).filter(Boolean)
            : (picks.homes || []).map((k) => homePickList(allPacks, [k])).filter(Boolean);
          const staleFor = (c: SectionCard): Record<string, unknown> => {
            const pf = String(c.data.__featLevel || echFeatOf(c as DynBlock) || "");
            if (!pf || pf === feat) return {};
            const out: Record<string, unknown> = {};
            PLAN_CARD_KEYS.forEach((k) => { out[k] = ""; });
            out.__socOff = String(c.data.__socOff || "").split(",").filter(Boolean).filter((k) => !(k in out)).join(",");
            return out;
          };
          const next = echReplaceSection({ cards: planCards as SectionCard[], level: feat, want, keys,
            ctx: fillCtx, doc: (doc && doc.length ? doc : [mBlk]) as DynBlock[], stale: staleFor });
          if (next) { onReplaceSection(planCards.map((c) => c.id), next); return; }
        }
        onData({ ...stale, ...echPickPatch(mBlk, fillCtx, doc && doc.length ? doc : [mBlk], {
          featLevel: feat,
          /* only the key this level can actually use is touched; `undefined` leaves
             the other one alone rather than clearing a sibling concern */
          /* ── A TABLE TAKES THE WHOLE FLOORPLAN LIST (2026-08-28) ───────────────
             This was `planPickKey(packs, picks.plans[0])` on every path, so the
             Listing table stored ONE plan and silently dropped the rest — and the
             `!rows` overflow-into-siblings branch below, which is what rescues the
             extras for a Featured card, is switched off for a table. Four picks
             became one stored plan and no rows. A table stores the list the same
             way it stores homes; a Featured card still holds exactly one. */
          planPick: want === "plan"
            ? (rows
              /* a SECTION ADD appends to the table's plan list, exactly as the homes
                 branch below does (2026-09-15, see planPickMerge) */
              ? (addSec
                ? planPickMerge(packs.concat(echCtx ? echPacks(echCtx) : []), String(mBlk.data[ECH_PLAN_PICK] || "").trim(), picks.plans || [])
                : planPickList(packs.concat(echCtx ? echPacks(echCtx) : []), picks.plans || []))
              : planPickKey(packs, (picks.plans || [])[0] || ""))
            : undefined,
          /* a FEATURED block renders one card, so it takes ONE home; the listing
             table (rows) still takes the whole list */
          /* ── A SECTION ADD APPENDS (2026-08-22) ───────────────────────────────
             The block-level picker REPLACES the pick list — that is what "Choose
             homes from inventory" means. "+ Add a home" is the opposite: the homes
             already in the table stay, and the new one joins them. `homePickList`
             regroups by community on the way in, so an added home lands in its own
             section without this code knowing which slot that is. */
          homePick: want === "home"
            ? (addSec
              /* the stored list is TRUSTED, only the new keys are verified — see
                 homePickMerge for the live defect this replaced (every other
                 community's pick was dropped because the scoped wizard's packs
                 could not verify them) */
              ? homePickMerge(packs.concat(echCtx ? echPacks(echCtx) : []),
                /* a FED table has no pick list — seed it from the rows on screen
                   first, or the add would replace ten fed homes with one pick */
                String(mBlk.data[ECH_HOME_PICK] || "").trim()
                  || homePickFromRows(packs.concat(echCtx ? echPacks(echCtx) : []), mBlk.data),
                picks.homes || [])
              /* EVERY PACK IN HAND, like the section-add branch above (2026-08-25). With
                 only the wizard's packs a key could be refused by the `known` rule when
                 Step 1's own 30-row pack for that community was the one missing the home
                 — measured while auditing the "6 selected, 1 added" fix. */
              : homePickList(packs.concat(echCtx ? echPacks(echCtx) : []),
                rows ? (picks.homes || []) : (picks.homes || []).slice(0, 1)))
            : undefined,
        }) });
        /* ── EVERY EXTRA PICK IS ITS OWN CARD (2026-08-21, Jack: "why is there a
           max of one for the featured homes? sfmc should state the rules for
           that" — it states none). Each additional pick lands on a NEW sibling
           block below this one, seeded with its own stored pick; the
           document-wide fill pass draws its content from the packs the wizard
           just handed up via onPacks. */
        if (!rows && onInsertBelow) {
          const extra = want === "plan" ? (picks.plans || []).slice(1)
            : want === "home" ? (picks.homes || []).slice(1) : [];
          extra.slice().reverse().forEach((k) => {
            onInsertBelow(block.type, {
              __echMode: "dynamic",
              __featLevel: feat,
              ...(want === "plan"
                ? { [ECH_PLAN_PICK]: planPickKey(packs, k) }
                : { [ECH_HOME_PICK]: homePickList(packs, [k]) }),
            });
          });
        }
        return;
      }
      if (block.type === "listing") {
        const R = rowsForLevel(packs, level);
        onData({ rows: R.rowsStr, __title: R.title, __cols: R.cols, ...(R.viewUrl && R.viewUrl !== "#" ? { viewurl: R.viewUrl } : {}) });
      } else {
        onData({ items: itemsForLevel(packs, level) });
      }
    }} />
  );

  const sections: React.ReactNode[] = [];
  if (themeDef) {
    sections.push(
      <Sec head="Theme" key="__theme">
        <select className="imgc-wsel" style={{ width: "100%", margin: "4px 0 2px", padding: 8, border: "1px solid #d3d9e2", borderRadius: 8, fontFamily: "inherit", fontSize: 12, background: "#fff" }}
          value={cx(d.__theme) || themeDef} onChange={(e) => onData({ __theme: e.target.value })}>
          {THEME_OPTS.map(([v, lab]) => (<option key={v} value={v}>{lab}</option>))}
        </select>
      </Sec>,
    );
  }

  /* ── IN DYNAMIC, THE PANE IS THE PANEL — LAYNE'S FIELDS GO AWAY (2026-08-20) ─────
     Jack: "i want the right panel to have the other view where i don't even need a
     community — i click the + button and can select the info myself. use control
     chrome to replicate this."
     READ OFF THE LIVE MONOLITH rather than approximated (visit.lennar.com/Emarketing
     Request Form → Creative Studio → Featured Community/Plan → Dynamic, 2026-08-20).
     Its whole Dynamic panel is:
         Manual | Dynamic
         Floorplans  · 0 selected                    Choose ›
         [ + Add a floorplan ]
         THEME …
     and nothing else — no CONTENT repeater, no declared fields. That is `esInsp`
     L13944: `def.fields.forEach(function(f,fi){ if(_skipIdx[fi]||dynOn||…)return; …`
     — the pane at L13916 REPLACES the field loop, it does not sit above it.
     THIS REVERSES THE 2026-08-20 MORNING DECISION, deliberately. The port had reasoned
     that an ECH block is "the opposite" of a legacy one because its fields stay
     editable in Dynamic; Jack looked at the result and asked for the monolith's
     surface. Manual is where the fields live, and Manual is one click away.
     ONLY BLOCKS WITH A PICKER. A block with no `livefill` field (the Logo, the CTAs,
     Get Directions) has no pane to replace its fields with, so its Dynamic panel is
     unchanged — that is what `echPickF` gates, and it is why this is not a blanket
     "hide everything in Dynamic". */
  /* ── AN ALWAYS-DYNAMIC BLOCK KEEPS ITS FIELDS (2026-08-22) ─────────────────────
     The pane-replaces-the-fields rule above exists because Manual was one click away:
     the fields lived there. With no Manual to click, replacing the fields with a pane
     would leave a listing table whose rows can never be edited and a Featured card
     that can never be reworded — the opposite of Jack's ruling ("pull from dynamic but
     can just edit like regular"). So these blocks render the EDITABLE panel (the homes
     list / the plan cards) and the picker is reached from a control inside it. */
  const echPickF = echf && echOn && liveF && !echAlwaysDyn(echName) ? liveF : null;
  if (echPickF) {
    /* ── THE SECTION HEAD SAYS WHICH LEVEL THIS BLOCK IS AT (2026-08-20) ─────────
       `echPickF.label` is baked at registration time from the block's declared fields
       ("Live floorplans from SFMC" / "Live homes from SFMC", echRegistry L262), so a
       Featured block switched to Homesites carried a head that said Floorplans over a
       pane that said Homes over a canvas full of homes. The monolith renames the whole
       section per level (`__dynCardTitle`, index.html L10377); this is the panel's half
       of that, off `ECH_FEAT[level].label` — the same string the pane draws.
       The Listing table has one level, and `echFeatOf` returns "homes" for it, so its
       head is unchanged. */
    /* ── …AND SINCE THE FIELDS WENT AWAY, NO HEAD AT ALL ON AN ECH BLOCK (second
       pass, same day). The level-derived head was the only line left above a pane
       whose own first word is already "Floorplans" — the same noun twice, which is
       the duplicated chrome Jack strips on sight, and the monolith has no head here
       either (its Dynamic panel is the mode strip and then `__dynPaneHtml`, L13916).
       `nohd` is the existing "this section labels itself" mode, the one an ECH image
       field already uses. A LEGACY block keeps its head: its pane is a control inside
       a labelled card and has no noun of its own. */
    const head = echf ? "" : (echPickF.label || "Live content from SFMC");
    /* ── DYNAMIC REFLECTS MANUAL ON A FEATURED BLOCK (2026-08-20, Jack: "dynamic
       needs to reflect manual"). Manual got the container — navy bar with the level
       dropdown, the plan row, the Choose card — and Dynamic still drew the old
       selected-cards pane: two different panels for one block. Dynamic now renders
       the SAME container. The mode contract holds: the row is read-only here (the
       feed owns the copy; the edit window is Manual's), and its ✕ clears the pick —
       the old pane tile's ×. The Listing table keeps its pane: its Manual panel is
       the homes list, a different shape. */
    if (planOn) {
      /* ── AN UNPICKED DYNAMIC CARD IS NO CARD (2026-08-21, Jack: "for dynamic
         should start with add homesite not a blank card — it's weird"). A Dynamic
         block with no pick is still on its shipped placeholder, so its card said
         "Plan Name / 270x180" with a Clear pick for nothing. It renders only once
         a pick exists; Manual siblings keep their cards (real content). Empty
         list = the dashed Choose card stands alone, which is the start he wants. */
      const hasPick = planHasPick;
      const dynCards = (planCards.length ? planCards : [{ id: block.id, data: d }])
        .filter((c) => hasPick(c.data || {}) || String((c.data || {}).__echMode ?? "") === "manual");
      sections.push(
        <Sec head="" key="__echpick">
          <EchPlanCards
            selfId={block.id}
            cards={dynCards}
            /* still read-only here: this branch is the Dynamic PANE, and as of
               2026-08-22 an always-dynamic block never reaches it (`echPickF` is null
               for those) — it renders the editable card panel below instead. */
            d={d} onData={onData} editable={false}
            feat={echFeat}
            onAdd={planAddBelow} addLeft={planAddLeft} cap={planCap}
            onSelectBlock={onSelectBlock} onDeleteBlock={onDeleteBlock} onReorder={planReorder}
            onChoose={() => setPlanWiz(true)}
            /* ── AND IT WORKS AT COMMUNITY LEVEL TOO (2026-08-28, Jack: "make sure
               we can easily rmeove add plans/featured whatever the level they're on")
               ──────────────────────────────────────────────────────────────────────
               The gate was `echFeatPickKind(echFeat)`, which is "" at community level
               (`ECH_FEAT.comms.pick`), so the one level that renders a card had no way
               to clear it — the glitch where remove simply is not there. At comms the
               pick IS the community, so clearing means dropping this card's community
               content and letting Step 1 refill it; the card's own ✕ still removes the
               card itself, which is the other, coarser action. */
            onClearPick={hasPick(d) ? () => {
              const want = echFeatPickKind(echFeat);
              if (!want) {
                /* community level: no pick key to blank, so clear what the card shows
                   and hand the fields back to the feed by dropping their claims */
                const cleared: Record<string, unknown> = {};
                PLAN_CARD_KEYS.forEach((k) => { cleared[k] = ""; });
                cleared.__socOff = String(d.__socOff || "").split(",").filter(Boolean)
                  .filter((k) => !PLAN_CARD_KEYS.has(k)).join(",");
                onData(cleared);
                return;
              }
              onData(echPickPatch(mBlk, echCtx || EMPTY_CTX, doc && doc.length ? doc : [mBlk], {
                planPick: want === "plan" ? "" : undefined,
                /* the stored shape is one newline-joined string — "" clears it */
                homePick: want === "home" ? "" : undefined,
              }));
            } : undefined}
          />
          {planWiz && liveF ? liveFill(liveF, true) : null}
        </Sec>,
      );
    } else {
      sections.push(
        <Sec head={head} key="__echpick">{liveFill(echPickF)}</Sec>,
      );
    }
  }

  panelFields.forEach((f, fi) => {
    if (skip.has(fi) || f.type === "livefill") return;
    /* THE DYNAMIC PANE REPLACES THE FIELDS — monolith `esInsp` L13944's `||dynOn`,
       see the `echPickF` comment above. Only on a block that HAS a pane. */
    if (echPickF) return;
    /* the raw icon/label pairs are handled by the amenity picker below */
    if (amOn && f.k && amKeys[f.k]) return;
    /* …and the five social URLs by the social picker (monolith L13303) */
    if (socOn && f.k && socK[f.k]) return;
    /* mostly-locked: only the URL is a decision, the rest is the division's block */
    if (mostlyLocked && f.type !== "url") return;
    const val = f.k ? cx(d[f.k]) : "";
    /* guardrail enforcement (monolith __grClampVal/__grWire): hard maxlength,
       red .gr-block ring at the cap, clamped on every write. */
    const rule = f.k ? rules[f.k] : undefined;
    const patRule = f.k ? patRules[f.k] : undefined;
    const cap = capOf(rule);
    const isOver = cap ? capState(val, rule) === "over" : false;
    /* ── ECH FIELD TYPOGRAPHY (audit row 18, monolith `_echCls` L13313–13316) ──
       The panel mirrors the email's own type hierarchy: a head/title/eyebrow/
       kicker field gets `.ech-h`, a textarea or a body/copy/description field
       gets `.ech-b`. `.ech-url` (monospace + word-break) is applied by the url
       branches. Non-ECH blocks are untouched. */
    let echCls = "";
    if (echf) {
      const kl = (String(f.k || "") + " " + String(f.label || "")).toLowerCase();
      if (/head|title|eyebrow|kicker/.test(kl)) echCls = "ech-h";
      else if (f.type === "textarea" || /body|copy|paragraph|descript|message/.test(kl)) echCls = "ech-b";
    }
    /* maxLength and the red ring are applied SEPARATELY: the ECH typography
       classes have to compose with `.gr-block` rather than be overwritten by it. */
    const grMax = cap ? { maxLength: cap } : {};
    const cls = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(" ") || undefined;
    /* THE FLASH FIRES ONLY ON AN ACTUAL TRUNCATION (monolith L13736–13739), and
       says exactly what the counter says. `maxLength` stops most typing before
       it gets here; a paste is what reaches this. */
    /* ── TYPING IN A FED FIELD CLAIMS IT (2026-08-27) ───────────────────────────
       Required by removing the Manual/Dynamic strip: with no mode to switch to, an
       edit has to be what protects the value, and `echClaimPatch` is the mechanism
       that already does it for the canvas and the always-dynamic blocks. It only ever
       claims a key the block's own feed map names, and only when the value actually
       changed, so a blur or a re-render still leaves the field with the feed.
       Before this, a plain `onData` here meant the next fill wrote straight over what
       someone had just typed on any fed block that wasn't already always-dynamic. */
    const wr = (k: string, v: string) => {
      const cl = clamp(v, rule);
      if (cl !== v && onFlash) onFlash(limitMessage(rule));
      onData(echClaimPatch(mBlk, { [k]: cl }));
    };
    /* ── PUT THE PROTOCOL ON A BARE DOMAIN, ON BLUR (2026-08-24) ─────────────────
       A division typed `www.myurl.com` and the CTA broke, because a browser reads a
       schemeless href as a RELATIVE path. Reported to Layne 2026-08-24; her fix
       attempt was `"default": "https://"` on the field, which cannot work — the
       value is typed OVER the default.

       ON BLUR, NOT ON CHANGE. Normalising per keystroke rewrites the box while the
       requester is still mid-word: typing `w`,`w`,`w`,`.` would jump to
       `https://www.` and the caret with it. Committing when they leave the field is
       invisible when it is right and never fights the typing.

       Silent by design. There is exactly one correct completion here, so telling
       someone to add `https://` is a worse product than adding it. Only fires when
       the value actually changes, so it can't loop. */
    const wrBlurUrl = (k: string, v: string) => {
      const fixed = normalizeUrl(v);
      if (fixed !== v) onData({ [k]: fixed });
    };
    /* THE PHOTO IS THE LABEL on an SFMC image field (monolith L13459): the card
       runs flush at the top of the panel with the block's locked size on it, and
       a "Main image" header above a picture of the main image says nothing. */
    const head = echf && f.type === "image"
      ? ""
      : f.label || (f.type === "listrows" || f.type === "repeat" ? "Content" : "Field");
    /* the value the ECH label-row count and the headroom meter measure. null =
       no count and no bar: either the block declares no cap for this field, or
       the value is one you cannot type into (monolith `_echC=null`, L13330). */
    let cntVal: string | null = echf && cap ? val : null;

    /* ── DYNAMIC: WHAT THE FEED RESOLVES TO, AND WHO OWNS THE FIELD ────────────
       A sourced field in Dynamic shows the FEED's value and is READ-ONLY — typing
       into a value the next fill overwrites is a trap, which is precisely what the
       repo shipped. Two kinds stay editable, and both are the monolith's decision:

         · a URL, because a destination is not a fact about the community (Jack,
           2026-08-19: "should still be able to link the url in the dynamic
           section"). This is also the fix for review finding #3 — at Market /
           Division / AOI focus `echScopeUrl` legitimately resolves to "", the fill
           writes that empty over the block's shipped "https://", and without a
           takeover the requester could not type a replacement: a Market send
           shipped `href=""` on every CTA button.
         · a SEED, because it is copy the feed OFFERS rather than owns.

       Typing in either marks the field taken over (`__socOff`), so the next fill
       skips it; clearing it hands the field back and the Step-1 value returns on the
       next fill. A field with no source — or one whose source does not apply at this
       level, e.g. a street address on a division send — is untouched and stays
       editable exactly as before (monolith L13325–13391, L13741–13756). */
    const rawSrc: string | { src: string; levels?: string[] | null; seed?: boolean } | "" =
      echFeed && echOn && f.k ? (echFeed[f.k] ?? "") : "";
    const echSrc = rawSrc && echLvl && echSrcOk(rawSrc, echLvl) ? echSrcOf(rawSrc).src : "";
    let body: React.ReactNode = null;
    if (echSrc && echCtx) {
      /* A CTA URL'S VALUE COMES FROM ITS LABEL, so the panel must ask the same
         question the fill does: the map lists these against `community.pageurl`, but
         `ctaUrlFor` routes them — "Schedule a Tour" resolves to /schedule-tour, and
         on a Featured block "View Floorplan" resolves to that plan's own page.
         Reading the raw map source instead left the panel showing empty while the
         email carried a real link (monolith L13345–13351). */
      const pairKey = (ECH_CTA_PAIRS[echName] || {})[f.k!];
      const dv = String(
        (pairKey
          ? ctaUrlFor(d[pairKey], echLvl, echCtx, doc, mBlk)
          : echDynVal(echSrc, echLvl, echCtx, doc, mBlk)) || "",
      );
      /* AN INHERITED STREET SAYS WHOSE IT IS (Jack, 2026-08-20). "From the community
         on Step 1." would be a lie when the street came off a child collection
         because the masterplan has none — and it is the difference between a value
         the requester can trust and one they should check. Only ever set on
         AddressLine1, and only when `echCommInfo` actually borrowed. */
      const borrowed = echSrc === "community.address1"
        ? (echCommInfo(echCtx) || {}).addrFrom || ""
        : "";
      const from = borrowed
        ? `No street on ${(echCommInfo(echCtx) || {}).name || "this community"} — using ${borrowed}'s. Switch to Manual to change it.`
        : (ECH_FROM[echLvl] || ECH_FROM.community);
      /* THREE STATES, NOT TWO: still loading · Step 1 unanswered at this level ·
         answered and the source has nothing. Only the middle one is the
         requester's to act on. */
      /* TWO STATES NOW, NOT THREE: loading, or nothing to say. The "answered but empty"
         and "not answered yet" cases both leave the line blank (2026-08-27) — see the
         note on ECH_WAIT. */
      const wait = echCtx.pending ? ECH_LOADING
        : (ECH_NONE[echLvl] || ECH_WAIT[echLvl] || "");
      const own = socIsOff(mBlk, f.k!);
      /* the takeover flag flips only on the empty↔non-empty transition, so the feed
         stops writing from the first keystroke and resumes the moment the box is
         cleared. Re-filling on hand-back is the Studio's fill effect, not a call
         from here — it keys on `blocks`, so this patch is what re-runs it. */
      /* ── A CLEARED BOX STAYS CLEARED (2026-09-22, pilot #124) ───────────────────────
         Kailey White (NEV): "When I remove the city and state at the top, it repopulates."
         The rule above — hand the field back the moment it is empty — was the url
         branch's defect on 2026-09-11 too ("unable to delete/change the url"), fixed there
         with `claimUrl`; the text and seed boxes kept the old behaviour, so deleting
         "Las Vegas, NV" put "Las Vegas, NV" straight back. A keystroke now CLAIMS the
         field whatever it leaves behind, empty included, and the way back to the Step-1
         value is the one action under the box ("Restore auto-fill") — the same shape the
         url field already has. Jack approved both strings 2026-09-22. */
      const takeover = (v: string) => {
        const cl = clamp(v, rule);
        if (cl !== v && onFlash) onFlash(limitMessage(rule));
        const patch: Record<string, unknown> = { [f.k!]: cl };
        if (!own) patch.__socOff = socOffList(mBlk, f.k!, true);
        onData(patch);
      };
      /* hand the field back: the fill effect keys on `blocks`, so this patch re-runs it */
      const restoreAuto = () => onData({ [f.k!]: "", __socOff: socOffList(mBlk, f.k!, false) });
      const ownNote = (cur: string) => (
        <div className="ech-dyns">{cur.trim() ? "Your text — " : "Cleared — stays blank. "}
          <button type="button" className="ech-dynact" data-testid="restore-auto" onClick={restoreAuto}>Restore auto-fill</button>
        </div>
      );
      /* no counter and no headroom bar on a value you cannot type into; a SEED
         restores it below because a cap the requester CAN exceed has to be visible */
      cntVal = null;
      /* ── THE TEMPLATE OWNS THIS ONE (2026-08-28) ────────────────────────────────
         `ECH_FIXED` fields are furniture, not content: the section title says what
         the list under it IS, and "View All" is the approved wording of that block's
         one link. So the box shows the value and refuses the keystroke — the one
         read-only case left on a modeless block, and the reason it is not a
         contradiction of the 2026-08-27 rule (that rule is about fields a requester
         has a legitimate answer for; these have one right answer per template).
         `Dyns` says where it comes from so it does not read as a broken field. */
      if (echFixed(echName, f.k!) && echSrc === "table.title") {
        /* ── A DROPDOWN, NOT A LOCK (2026-09-18, pilot item 4) — see SECTION_TITLE_OPTIONS.
           `dv` is the template's answer; `own` means the requester picked something else. */
        const cur = own ? String(d[f.k!] ?? "") || dv : dv;
        body = (
          <>
            <select
              className="ech-choice"
              data-k={f.k}
              data-testid="section-title"
              value={cur}
              onChange={(e) => {
                const v = e.target.value;
                if (v === dv) {
                  /* back to the template's answer: value AND hand the field back to the feed */
                  const patch: Record<string, unknown> = { [f.k!]: dv };
                  if (own) patch.__socOff = socOffList(mBlk, f.k!, false);
                  onData(patch);
                } else takeover(v);
              }}
            >
              {echTitleChoices(dv, own ? String(d[f.k!] ?? "") : "").map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {!own && <Dyns>Set by the template — it follows the section below it.</Dyns>}
          </>
        );
      } else if (echFixed(echName, f.k!)) {
        body = (
          <>
            <textarea className="ech-dynv ech-fixed" rows={1} value={dv} readOnly />
            {/* the note says what is TRUE of THIS field: only the title follows the
                block underneath, and saying so on the link label would be wrong */}
            <Dyns>{echSrc === "table.title"
              ? "Set by the template — it follows the section below it."
              : /^de:/.test(String(rawSrc || "")) ? "Set from your division's record."
              : "Set by the template."}</Dyns>
          </>
        );
      } else if (f.type === "seg" && f.opts) {
        /* ── A FED YES/NO STAYS BUTTONS (2026-10-29, Next Gen logo) ──────────────────
           At homes level ShowNextGen comes from the homesite's Next Gen flag. The
           buttons show that answer; picking the other one makes it the author's, and
           "Restore auto-fill" hands it back. */
        const cur = own ? String(d[f.k!] ?? "") : dv;
        body = (
          <>
            <Seg2 value={cur} opts={f.opts} onPick={(v) => { if (v !== dv) takeover(v); else if (own) restoreAuto(); }} />
            {own
              ? <div className="ech-dyns"><button type="button" className="ech-dynact" data-testid="restore-auto" onClick={restoreAuto}>Restore auto-fill</button></div>
              : <Dyns>{dv ? from : wait}</Dyns>}
          </>
        );
      } else if (echIsSeed(rawSrc)) {
        /* the authored default ("City, State") shows as the grey PLACEHOLDER, never
           as typed text — the block factory strips it from the value (echSeedStrip),
           and this is the other half of the same rule (Jack, 2026-08-21: "how would
           the city, state show as the label but not fill in text") */
        const hint = echSeedHint(mBlk.type, f.k!);
        let cur = String(d[f.k!] ?? "");
        if (!cur && !own) cur = dv;
        cntVal = echf && cap ? cur : null;
        body = (
          <>
            {/* one line tall, following the text (Jack 2026-08-21: "community box can
                be one line and expand into two if the text runs long") — ech-grow
                overrides ech-b's 68px authoring min-height */}
            <SpellField noBreaks={echf && !echBreaksAllowed(block.type, String(f.k || ""))} bulletBreaks={echf && echBulletsAllowed(block.type, String(f.k || ""))} className={cls(echCls || "ech-b", "ech-grow", isOver && "gr-block")} rows={1} value={cur} placeholder={hint || undefined} {...grMax} onChange={takeover} />
            {/* THE NOTE NAMES THE REAL SOURCE (Jack, 2026-08-21: "note fields should
                be updated in the dynamic section for header"). The initiative
                wording belongs to `initiative.*` seeds (the parked BodyCopy row);
                a community/market-sourced seed like cityState was showing
                "Approved copy from the initiative on Step 1" — a source it never
                reads. Scope-sourced seeds reuse the same From/Wait sentences every
                non-seed field shows, plus the edit-freely grant that makes a seed
                a seed. */}
            {own ? ownNote(cur) : (
              <Dyns>{echSrc.indexOf("initiative.") === 0
                ? (dv ? ECH_SEED_FROM : ECH_SEED_WAIT)
                : (dv ? from + " Edit freely." : wait)}</Dyns>
            )}
            <Grc value={cur} rule={rule} patRule={patRule} />
            {/* ── ONE LINE MEANS PIXELS, NOT CHARACTERS (2026-08-28 standup) ──────
                The subhead's guardrail says it outright: "The real constraint is pixel
                width, not character count — 520px… 14 capital Ws would already
                overflow." The block declares cpl:30, so the counter says 14/30 while
                the name is already off the end of the line. This measures the ink in
                Layne's own font and says the one thing her guardrail permits: shorten
                it. Empty for every field that has no such rule. */}
            {oneLineOverflow(echName, f.k, cur) && (
              <div className="grc over patbad">{oneLineOverflow(echName, f.k, cur)}</div>
            )}
          </>
        );
      } else if (f.type === "url") {
        /* the raw value — `UrlBox` owns the locked `https://` affix now, so nothing
           here prefills it (2026-08-27) */
        const cur = own ? String(d[f.k!] ?? "") : dv;
        /* ── A CLEARED LINK STAYS CLEARED (2026-09-11, Jack: "url is auto populating which is
           fine but unable to delete/change the url"). `takeover` hands a field back to the
           feed the moment it is empty — right for a text box, wrong here: the natural way to
           replace a link is to delete it and type the new one, and the Step 1 link snapped
           back before the first character landed. A url keystroke now CLAIMS the field
           whatever the value, empty included; the Done gate still refuses to ship a blank
           link, and the way back to the Step 1 link is the one action under the box. */
        const claimUrl = (v: string) => {
          const cl = clamp(v, rule);
          if (cl !== v && onFlash) onFlash(limitMessage(rule));
          const patch: Record<string, unknown> = { [f.k!]: cl };
          if (!own) patch.__socOff = socOffList(mBlk, f.k!, true);
          /* a typed CTA link remembers the label it was typed for (#268) */
          if (pairKey) Object.assign(patch, urlLabelPatch(d, f.k!, d[pairKey]));
          onData(patch);
        };
        const handBack = () => onData({ [f.k!]: "", __socOff: socOffList(mBlk, f.k!, false), ...(pairKey ? urlLabelRelease(d, f.k!) : {}) });
        const contactKind = CONTACT_EMAIL_BLOCK.test(echName) && f.k === "ContactUrl";
        body = (
          <>
            {contactKind ? (
              <ContactLinkKind value={cur} onChange={claimUrl} onWeb={handBack} bar={<UrlBar value={cur} />}
                seedSubject={defaultSubject(String(((echCtx && echCommInfo(echCtx)) || {}).name ?? ""))}>
                <UrlBox value={cur} cls={cls("ech-url", isOver && "gr-block")} max={capOf(rule) || undefined}
                  onChange={claimUrl} />
              </ContactLinkKind>
            ) : (
              <>
                <UrlBox value={cur} cls={cls("ech-url", isOver && "gr-block")} max={capOf(rule) || undefined}
                  onChange={claimUrl} />
                <UrlBar value={cur} />
              </>
            )}
            {/* ── THIS BRANCH SAID NOTHING AT ALL UNTIL NOW (2026-09-01) ────────────
                Layne, reopening the pattern report: 30 url patterns silently doing
                nothing. The check was fine; this branch drew no line for it to appear
                on. A fed url field showed neither the block's `patternMessage` NOR the
                broken-link warning every other url field gets — so a community record
                holding `lennar.com/x` produced a dead button with no sign anywhere in
                the panel. Both lines now render here, same order as the static path. */}
            <UrlWarn v={cur} />
            {/* ── THE LINK FOLLOWS THE LABEL, OR SAYS IT DIDN'T (#268, CHB 2026-09-11) ── */}
            {pairKey && ctaLinkWarning(mBlk, f.k!) && (
              <div className="urlbad" data-testid="cta-link-warn">{ctaLinkWarning(mBlk, f.k!)}</div>
            )}
            <Grc value={cur} rule={rule} patRule={patRule} fed={!own} />
            {/* ── THE CAPTION ONLY SPEAKS WHEN IT HAS NEWS (2026-08-28, per Jack) ──
                Was `own ? OWN : dv ? from + TAKE : wait`, so a URL field that had
                resolved perfectly well still carried "From the community on Step 1.
                Type over it to use a different link." — provenance the requester can
                already see in the box, plus an instruction for a thing they can just
                do. That is the explanatory-prose-on-a-built-surface class, and Jack
                asked for it gone.

                The OTHER two states stay, because both are news: `own` says the value
                is theirs and how to hand it back, and `wait` is the state that
                diagnosed Katy's blank CTAs on 2026-08-26 ("Nothing on file for the
                community on Step 1" — a feed resolving to nothing is precisely what
                nobody could see). Silence now means "resolved, nothing to say". */}
            {own
              ? <div className="ech-dyns">Your link — <button type="button" className="ech-dynact" data-testid="url-handback" onClick={handBack}>use the one from Step 1</button></div>
              : <Dyns>{dv ? "" : wait}</Dyns>}
          </>
        );
      } else {
        /* the FEED's value, not `d[f.k]` — a leftover manual value rendered under
           "From the community on Step 1." is a lie. */
        /* A MARKET SEND HAS NO COMMUNITY — SAY THAT, NOT "NOTHING ON FILE"
           (2026-08-21). community.name resolves "" at market scope by design now,
           and the generic empty-note reads like missing data when the truth is a
           structural one: there is no single community to name. */
        /* ── NO MANUAL MODE MEANS THE FIELD ITSELF HAS TO BE EDITABLE (2026-08-27) ──
           Jack: "auto dynamically pull at the start with the ability to change/edit
           afterwards." Read-only was the right answer while a Manual strip existed —
           it removed the trap where a typed value was silently overwritten, and Manual
           was the way out. On an always-dynamic block there is no way out, so the same
           read-only box would make the value uneditable rather than protected.
           Seeds and url fields already behave this way (both use `takeover` above);
           this is the third sub-branch catching up, gated on the block having no
           strip. `takeover` claims the field on the first keystroke, so the feed stops
           writing it and hands it back if the box is cleared. */
        const noMode = echAlwaysDyn(echName);
        /* the market-scope explanation goes too (2026-08-27): the box is editable, so
           "there is no single community" is something the requester can just answer by
           typing, and the empty field says it more briefly than a sentence does */
        const noComm = "";
        if (noMode) cntVal = echf && cap ? String(d[f.k!] ?? dv) : null;
        body = noMode ? (
          <>
            <SpellField noBreaks={echf && !echBreaksAllowed(block.type, String(f.k || ""))} bulletBreaks={echf && echBulletsAllowed(block.type, String(f.k || ""))} className={cls(echCls || "ech-b", "ech-grow", isOver && "gr-block")} rows={1}
              value={own ? String(d[f.k!] ?? "") : dv} placeholder={echSeedHint(mBlk.type, f.k!) || undefined} {...grMax}
              onChange={takeover} />
            {own ? ownNote(String(d[f.k!] ?? "")) : <Dyns>{noComm || (dv ? from + " Edit freely." : wait)}</Dyns>}
            <Grc value={own ? String(d[f.k!] ?? "") : dv} rule={rule} patRule={patRule} fed={!own} />
          </>
        ) : (
          <>
            <textarea className={"ech-dynv" + (/^https?:/i.test(dv) ? " ech-url" : "")} rows={2} value={dv} readOnly />
            {/* ── A READ-ONLY FIELD STILL REPORTS A BROKEN FORMAT (2026-09-01) ──────
                The requester cannot type here, so this is the one place where the
                message is purely informational — it is telling them the RECORD is
                wrong, not that they are. Kept anyway rather than hidden: an email that
                ships `399990` where `$399,990` belongs is wrong whether or not anyone
                in this panel could have prevented it, and the alternative is finding
                out from the send. The wording carries the provenance for that reason. */}
            <Grc value={dv} rule={rule} patRule={patRule} fed />
            {/* NO Copy/Open here, and it is not an omission: this branch is only
                reached when the field is neither a seed nor `type:"url"`, so by
                construction there is no declared link. The monolith stamps
                `__urlActAttr(f)` on this same textarea (L13386) and it is dead
                there for the identical reason — a value that merely LOOKS like a
                URL (an image's hosted address) never gets the pair. */}
            <Dyns>{dv ? from : noComm || wait}</Dyns>
          </>
        );
      }
    }
    /* ── THE APPROVED-CTA SELECT (audit row 15, monolith L13390–13392) ─────────
       A button label is a choice from 25 approved labels, not free text — free
       text is how a 200px button ends up with copy that can't fit and how the same
       action gets five different names across 47 divisions. Sits AFTER the dynamic
       branch, exactly as the monolith's `else if` does: no label field is in
       ECH_DYN, so in practice the select renders in both modes.
       The counter is suppressed (`_echC=null`) — there is nothing to type. */
    const isChoice = !!(echf && f.k && echChoiceFor(echName, f.k) === "cta");
    if (!body && isChoice) {
      cntVal = null;
      body = (
        <select
          className="ech-choice"
          data-k={f.k}
          value={val}
          onChange={(e) => {
            const v = e.target.value;
            const patch: Record<string, unknown> = { [f.k!]: v };
            /* a label picked by hand on a Featured card is the section's wording from now on:
               switching homes ⇄ floorplans no longer rewrites it (2026-09-25) */
            if (planOn) patch[ECH_LBL_SET] = "1";
            /* THE LABEL DECIDES THE DESTINATION, so changing "View Homes" to
               "Contact Us" has to re-derive the paired URL (monolith L13660–13666
               calls `__echDynFill` straight from the change handler). Resolved off
               a block carrying the NEW label — the monolith writes `m.data[k]`
               before it fills, and reading the old value would derive last
               choice's link. A URL the requester has typed over is skipped inside
               `echDynPatch`, so a hand-set destination survives a label change. */
            try {
              if (echName && echCtx) Object.assign(patch, echDynPatch({ ...mBlk, data: { ...d, [f.k!]: v } }, echName, echCtx, doc || []));
            } catch { /* a resolver miss must not lose the label the user picked */ }
            onData(patch);
            /* ── ONE LABEL FOR THE WHOLE SECTION (2026-09-18, Jack: "ctas buttons
               should be standardized"). A Featured card's button wording is the
               section's wording: every sibling card takes the same label and
               re-derives its own paired URL. A URL a requester typed on a sibling
               survives, exactly as it does on this card (`echDynPatch` skips a
               taken-over field). Nothing else on the siblings is touched. */
            if (planOn && onSwapMany && planCards.length > 1) {
              const sib = echSectionLabels(planCards, { id: block.id, type: block.type, data: { ...d, ...patch } }, echCtx || null, doc || []);
              if (sib.length) onSwapMany(sib);
            }
          }}
        >
          {!val && <option value="" disabled>{CHOICE_PLACEHOLDER}</option>}
          {echChoiceGroups(val, cap, echName ? echFieldList(echName, f.k!) : "").map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.opts.map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled}>{o.text}</option>
              ))}
            </optgroup>
          ))}
        </select>
      );
      /* THE VOCABULARY MAY BE STALE, AND THE AUTHOR SHOULD KNOW (Layne's spec,
         2026-08-27). The list is served from the last good cache when the live pull
         did not carry the block. Non-blocking on purpose: her stated preference is
         that authors keep working and someone finds out, rather than a transient
         fetch failure stopping work outright. */
      if (echName && echListStale(echFieldList(echName, f.k!))) {
        body = (
          <>
            {body}
            <div className="ech-note ech-note-warn">
              Showing the last loaded list — the CTA vocabulary may be out of date.
            </div>
          </>
        );
      }
    }
      /* THE VOCABULARY MAY BE STALE, AND THE AUTHOR SHOULD KNOW (Layne's spec,
         2026-08-27). The list is served from the last good cache when the live pull
         did not carry the block. Non-blocking on purpose: her stated preference is
         that authors keep working and someone finds out, rather than a transient
         fetch failure stopping work outright. */
      if (echName && echListStale(echFieldList(echName, f.k!))) {
        body = (
          <>
            {body}
            <div className="ech-note ech-note-warn">
              Showing the last loaded list — the CTA vocabulary may be out of date.
            </div>
          </>
        );
      }
    if (!body) switch (f.type) {
      case "seg":
        /* through `wr`, so a pick on a field another level fills (ShowNextGen on a plans
           card) is claimed and the next fill leaves it alone (2026-10-29) */
        body = f.opts ? <Seg2 value={val} opts={f.opts} onPick={(v) => wr(f.k!, v)} /> : null;
        break;
      case "textarea": {
        /* A SHORT-cap textarea is a one-liner that only wraps when the text runs
           long (Jack 2026-08-21, on Get Directions' Community name, cap 120: "can
           be one line and expand into two if the text runs long"). ech-grow zeroes
           ech-b's 68px authoring min-height and turns on field-sizing:content.
           Real body copy — bigger caps or none — keeps the 4-row authoring box. */
        const short = !!(cap && cap <= 160);
        body = (<><SpellField noBreaks={echf && !echBreaksAllowed(block.type, String(f.k || ""))} bulletBreaks={echf && echBulletsAllowed(block.type, String(f.k || ""))} className={cls(echCls, short && "ech-grow", isOver && "gr-block")} rows={short ? 1 : 4} value={val} {...grMax} onChange={(nx) => wr(f.k!, nx)} /><Grc value={val} rule={rule} patRule={patRule} /></>);
        break;
      }
      case "image": {
        const nx = def.fields[fi + 1];
        const hasAlt = !!(nx && nx.type === "text" && /alt/i.test(String(nx.label || nx.k || "")));
        /* the GIF builder is a HERO thing: the legacy hero block, or an ECH block whose name says
           Main Image / Hero — and only its first image field (2026-09-16) */
        const isFirstImg = def.fields.findIndex((x) => x.type === "image") === fi && (block.type === "hero" || /main\s*_?image|hero/i.test(String(echName || "")));
        /* the slideshow GIF builder — Main Image only, as the second button under Replace photo
           (where the monolith kept "Carousel"), 2026-09-16 */
        /* the slot: a size DROPDOWN wins over the image field's own declaration, which is the
           fallback (Layne, 2026-09-17: imgSrc keeps `width:500, height:350` in the header — the
           state that works today — and the dropdown must still be the source of truth) */
        const slotW = (sizeKeys && sizeKeys.w && sizeSlot ? sizeSlot.w : f.w) || (sizeSlot ? sizeSlot.w : undefined);
        const slotH = (sizeKeys && sizeSlot ? sizeSlot.h : f.h) || (sizeSlot ? sizeSlot.h : undefined);
        const gifBtn = !!f.k && isFirstImg ? <HeroGifPanel k={f.k} data={d} slot={slotW && slotH ? { w: slotW, h: slotH } : null} onData={onData} openBynder={onOpenBynder} /> : null;
        /* ── WIDTH · HEIGHT UNDER THE PHOTO (2026-09-17) ─────────────────────────────────
           One row, two strict dropdowns off Layne's lists. Changing either re-asks a Bynder
           photo from the transform at the new fill size — that is what makes the height
           choice visible; the block's own height:auto would otherwise ignore it. */
        const sizeRow = sizeKeys && f.k && isFirstImg ? (
          <div className={"imgc-size" + (sizeKeys.w ? "" : " one")} data-testid="img-size">
            {([["w", "Width"], ["h", "Height"]] as const).filter(([which]) => !!sizeKeys[which]).map(([which, lab]) => {
              const k = sizeKeys[which];
              const cur = cx(d[k]) || cx(sizeDefaults[k]);
              const listName = echName ? echFieldList(echName, k) : "";
              const groups = echChoiceGroups(cur, 0, listName);
              const opts = groups.flatMap((g) => g.opts);
              return (
                <label key={k} className="imgc-size-f">
                  <span>{lab}</span>
                  <select className="imgc-wsel" value={cur} data-k={k} onChange={(e) => {
                    const v = e.target.value;
                    const next = { w: which === "w" ? parseInt(v, 10) : (slotW || 0), h: which === "h" ? parseInt(v, 10) : (slotH || 0) };
                    const patch: Record<string, unknown> = { [k]: v };
                    if (next.w && next.h) {
                      const src = cx(d[f.k!]);
                      const re = refillForSlot(src, next, { gif: cx(d[f.k! + "__gif"]) === "1" });
                      if (re !== src) patch[f.k!] = re;
                    }
                    onData(patch);
                  }}>
                    {opts.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
                  </select>
                </label>
              );
            })}
          </div>
        ) : null;
        const extra = (
          <>
            {block.type === "hero" && (
              <div className="imgc-alt imgc-width" style={{ display: "block" }}>
                <label>Image width</label>
                <select className="imgc-wsel" value={cx(d.__inset) || "full"} onChange={(e) => onData({ __inset: e.target.value })}>
                  {INSET_OPTS.map(([v, lab]) => (<option key={v} value={v}>{lab}</option>))}
                </select>
              </div>
            )}
            {block.type === "image" && f.k === "src" && <OverlayFxSelect data={d} onData={onData} />}
            {sizeRow}
            {/* ✎ Edit Overlay — Main Image only, never on a GIF, dev/QA only (2026-09-18) */}
            {!!f.k && isFirstImg && cx(d[f.k + "__gif"]) !== "1" && skyExtendEnabled() && <OverlayEditorCard k={f.k} data={d} onData={onData} slot={slotW && slotH ? { w: slotW, h: slotH } : null} />}
          </>
        );
        /* ── REPOSITION UNDER AN OVERLAY (2026-10-10, Jack: "in the actual app I can move the
           centre of the image… how come I'm unable to do it here?") ───────────────────────────
           Zoom + drag only works on a Bynder transform URL, and after a bake the field holds the
           baked JPEG — so the tool switched itself off. While an overlay is on, the card now edits
           the ORIGINAL photo (`<key>__ovbase`): it shows that photo with the same zoom + drag, and a
           new framing of the same asset re-bakes the overlay from it. A different photo is still a
           replace (the plain photo shows, and the editor puts the overlay back). */
        /* the requester's own crop — a sky bake keeps it in `ovPick` while the stash holds the wider slice */
        const ovBase = f.k ? (cx(d.ovPick) || cx(d[f.k + "__ovbase"])) : "";
        const ovFraming = !!f.k && isFirstImg && skyExtendEnabled() && isBake(val) && !!ovBase && isBynderTransform(ovBase) && ovActive(d, f.k);
        body = (
          <ImgCard
            v={ovFraming ? ovBase : val}
            altV={hasAlt ? cx(d[nx!.k!]) : undefined}
            onUrl={(u) => {
              if (ovFraming && u && isBynderTransform(u) && bynderBase(u) === bynderBase(ovBase)) {
                rebake(d, f.k!, slotW && slotH ? { w: slotW, h: slotH } : null, (p) => onData(p), () => d, u);
                return;
              }
              /* photo changed by hand → the stashed overlay base is stale (monolith parity);
                 the sky bake that was made from it goes with it (2026-09-18) */
              const drop: Record<string, unknown> = d.src__ovbase != null && f.k === "src" ? { src__ovbase: "" } : {};
              if (d[f.k! + "__ovbase"] != null) { drop[f.k! + "__ovbase"] = ""; drop[f.k! + "__sky"] = ""; drop[f.k! + "__skyforce"] = ""; }
              if (d.ovPick != null) drop.ovPick = "";
              /* a URL typed or cleared by hand is a photo again unless it still ends in .gif */
              const gifFlag = d[f.k! + "__gif"] != null || /\.gif(\?|$)/i.test(u) ? { [f.k! + "__gif"]: /\.gif(\?|$)/i.test(u) ? "1" : "" } : {};
              onData({ [f.k!]: u, ...drop, ...gifFlag });
            }}
            onAlt={hasAlt ? (a) => onData({ [nx!.k!]: a }) : undefined}
            /* the asset's own pixels, stored beside the photo at pick time (2026-09-15) — the zoom +
               drag maths needs the source size to write a crop rectangle Bynder understands */
            nat={cx(d[f.k! + "__nat"])}
            onNat={(dims) => onData({ [f.k! + "__nat"]: dims })}
            gif={cx(d[f.k! + "__gif"]) === "1"}
            /* the SLOT's declared pixels ride along so the picker can ask Bynder's
               transform for exactly that size (2026-08-22) */
            openBynder={() => onOpenBynder((url, asset) => {
              const patch: Record<string, unknown> = { [f.k!]: url, [f.k! + "__nat"]: asset && asset.w && asset.h ? asset.w + "x" + asset.h : "" };
              /* ── ANIMATED GIF (2026-09-16, Jack: "add the option to add a gif to the hero image", Bynder only) ──
                 The picker already handed us the bare base for a GIF (no transform → no flattened
                 frame); the flag beside the photo is what turns off the crop card and turns on the
                 note. A later photo pick clears it. */
              patch[f.k! + "__gif"] = isGifAsset(asset) ? "1" : "";
              /* the overlay's remembered crop belonged to the old photo (2026-10-10) */
              if (d.ovPick != null) patch.ovPick = "";
              /* ── A DERIVED ALT TEXT IS CLAMPED TO ITS OWN CAP (2026-08-24) ──────────
                 BOI's report, verbatim: "Giving an error or Alt Text character limit, but
                 there is no area to update." Both halves were true. `imgAlt` is capped at
                 100 by ECH_MainImage's own guardrail, Bynder asset names run far past that
                 (the reported violation was 42 over), and this write put the whole derived
                 string in — so the requester got a blocking "Fix before sending" card about
                 a field the panel doesn't surface. Clamped at the source: the alt still
                 describes the asset, and a value that cannot pass the guardrail can no
                 longer be written in the first place. */
              /* ── ONE PLACE DECIDES A PICK'S ALT (2026-08-27) ────────────────────
                 Was `asset.alt || bynAltFromName(asset.name)`, which trusted the feed
                 first — and the feed's alt is a Box migration note on most of the DAM
                 (measured: 75 of 116 assets across four categories; none carry real
                 alt text). `bynAlt` distrusts a description that is machinery and
                 falls back to the asset name, and it clamps on a WORD boundary so the
                 100-char cap no longer cuts mid-word. Division report via Layne:
                 "Title and alt text for images selected from Bynder are incorrect." */
              if (hasAlt) patch[nx!.k!] = bynAlt(asset, capOf((gr?.fields || {})[nx!.k!]) || 100);
              if (f.k === "src" && d.src__ovbase != null) patch.src__ovbase = "";
              /* a new photo starts with no sky band (2026-09-18) */
              if (d[f.k! + "__ovbase"] != null) { patch[f.k! + "__ovbase"] = ""; patch[f.k! + "__sky"] = ""; patch[f.k! + "__skyforce"] = ""; }
              onData(patch);
            }, { w: slotW, h: slotH })}
            extra={extra}
            secondary={gifBtn}
            /* the DECLARED size drives the chip, the preview's shape and the fit
               check — legacy blocks declare neither and are unchanged (row 21);
               since 2026-09-17 the Main Image's size is the chosen width × height pair */
            w={slotW}
            h={slotH}
          />
        );
        break;
      }
      case "pair":
        body = (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {(f.items || []).map((p) => (
              <div key={p.k} style={{ textAlign: "left" }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: "#8a97ab", letterSpacing: ".05em", textTransform: "uppercase", marginBottom: 5 }}>{unent(p.label)}</div>
                <input className={p.mono ? "mono" : ""} value={cx(d[p.k])} onChange={(e) => onData({ [p.k]: e.target.value })} />
                {/* ── A THIRD SILENT PATH, FOUND BY THE GUARD (2026-09-01) ──────────
                    Neither Layne nor I had spotted this one; the render-path scan in
                    Inspector.paths.test.ts turned it up on its first run. Each half of
                    a pair is its OWN key with its own declared rule, so the lookup is
                    `patRules[p.k]` — `patRule` above belongs to the pair's container
                    field and would check the wrong rule against the wrong value. */}
                <Grc value={cx(d[p.k])} rule={rules[p.k]} patRule={patRules[p.k]} />
              </div>
            ))}
          </div>
        );
        break;
      case "datetime":
        body = <input type="datetime-local" value={val} onChange={(e) => onData({ [f.k!]: e.target.value })} />;
        break;
      case "amchips": {
        const AI = AMENITY_ICONS as Record<string, string>;
        const picks = val.split(",").map((x) => x.trim()).filter(Boolean);
        body = (
          <div className="amchips">
            {Object.keys(AI).map((k) => (
              <button key={k} type="button" className={"amchip" + (picks.includes(k) ? " on" : "")}
                onClick={() => onData({ [f.k!]: (picks.includes(k) ? picks.filter((x) => x !== k) : [...picks, k]).join(", ") })}>
                <img src={AI[k]} alt="" />{k}
              </button>
            ))}
          </div>
        );
        break;
      }
      case "socgrid":
        body = <SocGrid d={d} onData={onData} />;
        break;
      case "repeat":
        body = <RepeatField f={f} items={(d[f.k!] as Record<string, unknown>[]) || []} onItems={(a) => onData({ [f.k!]: a })} openBynder={onOpenBynder} gr={gr} />;
        break;
      case "listrows":
        body = <ListRowsField value={val} onChange={(v) => onData({ [f.k!]: v })} />;
        break;
      case "sheet":
        body = <SheetField onRows={(rows) => onData({ [f.k!]: rows })} />;
        break;
      case "carbuild":
        body = <FilmField imgs={(d.imgs as string[]) || []} onImgs={(a) => onData({ imgs: a })} openBynder={onOpenBynder} />;
        break;
      case "gifgen":
        body = <GifGenField type={block.type} data={d} onData={onData} />;
        break;
      case "ovedit":
        body = <OverlayField data={d} onData={onData} />;
        break;
      default:
        /* AN ECH URL IN MANUAL MODE IS A TEXTAREA, NOT AN INPUT (monolith
           L13450–13451). A 120-character maps link in a single-line input shows
           the first third of itself; `.ech-url` wraps mid-string and the
           auto-grow above lets the box follow the text. */
        /* THE FIELD CARRIES THE LINK THE BLOCK WILL ACTUALLY SEND WITH (2026-08-20).
           An empty url field on a block whose markup already holds a working link reads
           as broken — that is exactly what Jack hit on Contact Us. So the placeholder is
           the block's own effective href and Copy/Open act on it, which turns four panel
           elements into one honest control: empty means that link is used, typing
           overrides it. Read out of `d.__html`, so nothing here is authored — if the
           block has no link, the placeholder is empty and behaviour is unchanged. */
        /* ON A PER-BUTTON CONTACT US FIELD THE PLACEHOLDER IS *THAT BUTTON'S* HREF
           (2026-08-28). `echLinkPh` is the block's FIRST real link, which is the right
           answer when a block has one link and the wrong one the moment it has two —
           both of NEV's fields would have offered the phone number. */
        const urlPh = (f.k && contactPh[f.k]) || echLinkPh;
        body = echf && f.type === "url" ? (
          <>
            {CONTACT_EMAIL_BLOCK.test(echName) && f.k === "ContactUrl" ? (
              <ContactLinkKind value={val} onChange={(v) => wr(f.k!, v)} onWeb={() => wr(f.k!, "")} bar={<UrlBar value={echLinkFor(val, urlPh)} />}
                seedSubject={defaultSubject(String(((echCtx && echCommInfo(echCtx)) || {}).name ?? ""))}>
                <UrlBox value={val} cls={cls("ech-url", isOver && "gr-block")} max={capOf(rule) || undefined}
                  ph={urlPh} onChange={(v) => wr(f.k!, v)} onBlur={(v) => wrBlurUrl(f.k!, v)} />
              </ContactLinkKind>
            ) : (
              <>
                <UrlBox value={val} cls={cls("ech-url", isOver && "gr-block")} max={capOf(rule) || undefined}
                  ph={urlPh} onChange={(v) => wr(f.k!, v)} onBlur={(v) => wrBlurUrl(f.k!, v)} />
                <UrlBar value={echLinkFor(val, urlPh)} />
              </>
            )}
            {/* ── SAY IT WHERE THEY TYPED IT (2026-08-27) ──────────────────────────
                OCH: "no validation on URLs, could include commas & pass." The Done
                gate now refuses it, but a refusal at Done about a field three panels
                back is a worse product than a line under the box. Its own class, not
                `.grc` — the ECH sections hide `.grc` on purpose (the count moved to
                the label row), and this is not a count. */}
            <UrlWarn v={val} />
            <Grc value={val} rule={rule} patRule={patRule} />
          </>
        ) : (
          <>
            {f.type === "url" ? (
              <UrlBox value={val} cls={cls("mono", echCls, isOver && "gr-block")} max={capOf(rule) || undefined}
                ph={urlPh} onChange={(v) => wr(f.k!, v)} onBlur={(v) => wrBlurUrl(f.k!, v)} />
            ) : (
              <input className={cls(echCls, isOver && "gr-block")} value={val}
                {...grMax} onChange={(e) => wr(f.k!, e.target.value)} />
            )}
            {/* DECLARED type only — `__urlActAttr` (monolith L12899) tests
                `f.type==="url"` and never the value, so an image's hosted address
                gets nothing even though it starts https:// */}
            {f.type === "url" && <UrlBar value={echLinkFor(val, urlPh)} />}
            {f.type === "url" && <UrlWarn v={val} />}
            <Grc value={val} rule={rule} patRule={patRule} />
            {f.type === "text" && <SpellHint value={val} onFix={(nx) => wr(f.k!, nx)} />}
          </>
        );
    }
    /* ── THE COUNT RIDES THE LABEL ROW, THE HEADROOM BAR SITS UNDER THE BOX
       (audit row 2, monolith L13455 + L13461) ──────────────────────────────
       So the limit is visible from the FIRST keystroke rather than at 90% of
       the cap, and without spending a line of text on it. Both only exist when
       the block DECLARES a cap for this field — the dashboard defines no rules. */
    const cnt = cntVal != null
      ? <span className={"ech-cnt " + capState(cntVal, rule)} data-echc={f.k}>{echCntTxt(cntVal, rule)}</span>
      : null;
    const meter = cntVal != null
      ? <div className={"ech-meter " + capState(cntVal, rule)} data-echm={f.k}><i style={{ width: echPct(cntVal, rule) + "%" }} /></div>
      : null;
    const secEl = <Sec head={head} key={fi} cls={[echf ? "echf" : "", reqKeys && f.k && reqKeys.has(f.k) ? "echreq" : ""].filter(Boolean).join(" ") || undefined} cnt={cnt}>{body}{meter}</Sec>;
    /* a home<N><Field> key goes into its row's bucket rather than straight onto
       the card (monolith L13464–13466) */
    /* the collection name is rendered in its group's HEADER below, so it must not
       also land on the card — that would be the same field twice, and the two copies
       would fight over focus while typing */
    if (rowsOn && /^collection\d+Name$/.test(String(f.k || ""))) return;
    const rr = rowsOn ? rowOf(f.k) : null;
    if (rr) {
      if (rowOrder.indexOf(rr.n) < 0) rowOrder.push(rr.n);
      /* only the URL section survives as a built element — the other six edit
         in place on the card (2026-08-21) */
      if (/Url$/.test(String(f.k || ""))) {
        if (!rowUrl[rr.n]) rowUrl[rr.n] = [];
        rowUrl[rr.n].push(secEl);
      }
    } else if (planOn) {
      /* the FIRST plan field anchors where the cards splice in; the card-owned
         keys are edited on the card itself, everything else goes into the card's
         collapsed Buttons & links section */
      if (planAt < 0) planAt = sections.length;
      if (!(f.k && PLAN_CARD_KEYS.has(f.k))) planBtns.push(secEl);
    } else sections.push(secEl);
  });

  /* ── EMIT THE FEATURED PLAN CARDS (2026-08-21, the clean setup) ──────────────
     The cards splice in at the first card-owned field's position — the top of the
     block's fields — and the button/URL sections the loop let through sit BELOW
     them as ordinary panel fields. The photo click hands the Bynder pick straight
     onto PlanImage/PlanImageAlt, the same patch shape ImgCard writes.
     `!echPickF` = Manual only: in Dynamic the pick branch above already emitted
     the read-only cards, and emitting twice would be two renderings of one fact. */
  if (planOn && !echPickF) {
    sections.splice(Math.max(planAt, 0), 0,
      <Sec head="" key="__plan">
        {feedNote(echFeatPickKind(echFeat) === "home" ? "homes" : "plans")}
        {/* the two doors, in the listing table's own order and spacing */}
        {echAlwaysDyn(echName) && liveF ? (
          <button type="button" className="echh-addcoll" data-echchooseplans
            onClick={() => setPlanWiz(true)}>
            {echFeatPickKind(echFeat) === "home" ? "Choose homes from inventory ›" : "Choose floorplans from inventory ›"}
          </button>
        ) : null}
        {echAlwaysDyn(echName) ? (
          <>
            <input ref={planSheetRef} type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              style={{ display: "none" }} data-echplancsv onChange={onPlanSheet} />
            <button type="button" className="echh-upload" data-echplanupload
              title="Headers like Plan (or Homesite), Sq Ft, Beds, Baths — one card per row, five cards max. Extra columns are ignored."
              onClick={() => { if (planSheetRef.current) planSheetRef.current.click(); }}>
              ⬆ Fill from a spreadsheet (.xlsx / .csv)
            </button>
          </>
        ) : null}
        <EchPlanCards
          selfId={block.id}
          /* ── THE CARD ALWAYS RENDERS HERE, AND THAT IS THE POINT ────────────────
             The 2026-08-21 "no blank card before a pick" rule was about the READ-ONLY
             Dynamic card: a placeholder you could not edit, carrying a Clear pick for
             a pick that did not exist. This card is editable and fed, so it is the
             thing the requester works in — hiding it until a pick would mean an
             always-dynamic Featured block with nothing to edit. (2026-08-22) */
          cards={planCards.length ? planCards : [{ id: block.id, data: d }]}
          /* on an always-dynamic Featured block the card is FED, so an in-place edit
             has to claim its field or the next fill overwrites it (2026-08-22) */
          d={d} onData={echAlwaysDyn(echName) ? fedEdit : onData} btns={planBtns}
          withChoose={echAlwaysDyn(echName)}
          feat={echFeat}
          onAdd={planAddBelow} addLeft={planAddLeft} cap={planCap}
          onSelectBlock={onSelectBlock} onDeleteBlock={onDeleteBlock} onReorder={planReorder}
          onChoose={() => setPlanWiz(true)}
          /* ── CLEAR PICK BELONGS HERE NOW TOO (2026-08-22) ────────────────────────
             It used to live only on the read-only Dynamic card. On an always-dynamic
             block this IS the card that holds a pick, so without it a requester who
             chose the wrong plan would have no way back to the block's own content. */
          onClearPick={echAlwaysDyn(echName) && echFeatPickKind(echFeat)
            && Boolean(String(d[ECH_PLAN_PICK] ?? "").trim() || String(d[ECH_HOME_PICK] ?? "").trim())
            ? () => {
              const want = echFeatPickKind(echFeat);
              onData(echPickPatch(mBlk, echCtx || EMPTY_CTX, doc && doc.length ? doc : [mBlk], {
                planPick: want === "plan" ? "" : undefined,
                homePick: want === "home" ? "" : undefined,
              }));
            } : undefined}
          /* the photo is a FED field on a Featured block (`home.self.img`), so a Bynder
             pick has to claim it the same way a typed value does — otherwise the next
             fill would put the feed's photo back over the one just chosen (2026-08-22) */
          /* the slot's DECLARED pixels ride along, as they do on the ImgCard path above
             — without them the picker had nothing to ask Bynder's transform for and
             stored the grid preview, which the Done gate then flagged as a missing
             photo on every card Katy re-pictured (2026-09-09) */
          photo={{
            slot: planImgNeed(gr, d.__html),
            nat: cx(d.PlanImage__nat),
            onNat: (dims) => (echAlwaysDyn(echName) ? fedEdit : onData)({ PlanImage__nat: dims }),
            onUrl: (u) => (echAlwaysDyn(echName) ? fedEdit : onData)({ PlanImage: u }),
          }}
          onPhoto={() => onOpenBynder((url, asset) => (echAlwaysDyn(echName) ? fedEdit : onData)({
            PlanImage: url,
            /* the asset's pixels ride beside the photo, as on the Main Image — the adjust frame's
               first guess before it learns the transform's own size (2026-09-17) */
            PlanImage__nat: asset && asset.w && asset.h ? asset.w + "x" + asset.h : "",
            /* same decision as the MainImage path above, through the same helper so
               the two cannot drift (2026-08-27). The DECLARED cap, not a hard-coded
               100 — they agree today and would part company the moment Layne edits
               the block (audit 2026-08-24). */
            PlanImageAlt: bynAlt(asset, capOf((gr?.fields || {}).PlanImageAlt) || 100),
          }), planImgNeed(gr, d.__html))}
        />
        {planWiz && liveF ? liveFill(liveF, true) : null}
      </Sec>,
    );
  }

  /* ── EMIT THE ROW GROUPS (audit rows 10 + 22, monolith L13468–13487) ─────────
     Each home collapsed, summarised by its own homesite / plan · price, with an ×
     that empties its 7 keys — after which the RENDER pass drops the row, which is
     the same Homesite-OR-Plan test `rowSummary` shows here. `N of 10 shown` on
     the section head is the number that will actually ship. */
  if (rowsOn && rowOrder.length) {
    rowOrder.sort((a, b) => a - b);
    /* ── THE HOMES SIT INSIDE THEIR COLLECTION (2026-08-20) ───────────────────────
       Ownership is parsed from her markup's token order (`rowCollections`), currently
       collection1Name → homes 1–5 and collection2Name → 6–10, so a re-cut block
       regroups itself with no code change. The collection NAME renders in the group
       header and is skipped in the flat field list above, so it appears exactly once.

       Everything about HOW the list behaves — filled-only rows, the plus card, the
       insert drag, the section swap, the edit window — lives in `EchHomes`. The count
       on the section head is still the number that will actually ship. */
    const colls = rowCollections(d.__html, rowOrder);
    const filled = rowOrder.filter((n) => rowSummary(d, n).used).length;
    sections.push(
      <Sec head="Homes" key="__homes" cnt={<span className="ech-rc">{filled} of {rowOrder.length} shown</span>}>
        {/* ── THE PICKER IS REACHED FROM THE LIST, NOT FROM A MODE (2026-08-22) ────
            Jack: "when selecting adding a community home etc you pull from dynamic
            but can just edit like regular." The rows arrive filled from inventory;
            this is how you go choose different ones. It replaces the Dynamic pane's
            "Choose ›" — same wizard (`liveFill(liveF, true)`), reached without
            leaving the panel that can actually edit a row. "+ Add a home" inside a
            section still types one in by hand, for a home inventory doesn't carry. */}
        {feedNote("homes")}
        {echAlwaysDyn(echName) && liveF ? (
          <button type="button" className="echh-addcoll" data-echchoosehomes
            onClick={() => setPlanWiz(true)}>Choose homes from inventory ›</button>
        ) : null}
        {echAlwaysDyn(echName) && planWiz && liveF ? liveFill(liveF, true) : null}
        <EchHomes
          colls={colls}
          blockType={block.type}
          onAddFromInventory={echAlwaysDyn(echName) && liveF
            ? ((name, open, draft) => { setAddSec({ name, open, draft }); setPlanWiz(true); })
            : undefined}
          d={d}
          rowUrl={rowUrl}
          lookupUrl={(v) => homeUrlLookup(echCtx ? echPacks(echCtx) : [], v)}
          linkMiss={new Set(requiredBlanks([block])
            .map((r) => { const mm = /^home(\d+)Url$/.exec(r.k); return mm ? +mm[1] : 0; })
            .filter(Boolean))}
          /* a row you edit or add by hand keeps what you typed — `fedEdit` claims the
             keys it writes so the next fill skips them (2026-08-22) */
          onData={echAlwaysDyn(echName) ? fedEdit : onData}
          /* ── REMOVE A WHOLE COLLECTION — HEADING, HOMES AND ALL (2026-08-27) ──────
             Jack: "i want the ability to remove a collection" … "remove the entire
             collection and homes".

             It lives HERE rather than in `EchHomes` because clearing the slots is only
             half the job and the other half needs the packs. `echGroupedListingPatch`
             rebuilds its groups from `__homePick` and deals them into slots BY GROUP
             INDEX (`rowsOf[g]`), re-sorted each pass by row count. So a collection
             whose slots were merely blanked-and-claimed does not disappear — the
             surviving communities re-sort, shift up into the claimed slots, and the
             claim then holds down the WRONG rows. The community has to leave the pick
             list, and the pick list is keyed by community CODE, which only the packs
             can resolve from the heading text.

             Three moves, all in one patch so a single undo restores the section:
               · blank every `home<n><field>` slot the section owns, and its heading;
               · CLAIM those keys in `__socOff` — the same mechanism the single-home ✕
                 uses, and the reason a deleted row stays deleted through the next fill
                 ("can't delete homes, you keep re-adding them", 2026-08-22);
               · drop the community's lines from `__homePick`, so the group is not
                 rebuilt at all. Matched by CODE via the packs; if the heading resolves
                 to no pack the pick list is left alone rather than guessed at — the
                 claim still holds the section empty, which fails visibly-correct
                 instead of silently deleting someone else's rows. */
          onDropColl={(g) => {
            const patch: Record<string, unknown> = {};
            const claimed = String(d.__socOff || "").split(",").filter(Boolean);
            const claim = (k: string) => { if (claimed.indexOf(k) < 0) claimed.push(k); };

            g.homes.forEach((n) => {
              ROW_FIELDS.forEach((sfx) => {
                const k = "home" + n + sfx;
                if (d[k] === undefined) return;          /* never invent a key */
                patch[k] = "";
                claim(k);
              });
            });
            /* an ADDED collection has no field key at all — its name is `lit`, sitting
               in the markup, and the `rowDropColl` branch below cuts that row out. Only
               a DECLARED one has a heading field to empty and claim. */
            const heading = String(g.key != null ? (d[g.key] ?? "") : (g.lit ?? "")).trim();
            if (g.key != null) {
              patch[g.key] = "";
              claim(g.key);
            }

            /* the heading names a community; the pick list names its code */
            const packs = echCtx ? echPacks(echCtx) : [];
            const hit = packs.find((p) => String(p.name || "").trim().toLowerCase() === heading.toLowerCase());
            const code = hit ? String(hit.code || "") : "";
            const hp = String(d.__homePick ?? "");
            if (code && hp) {
              const kept = hp.split("\n").filter((ln) => String(ln).split("|")[0].trim() !== code);
              if (kept.length !== hp.split("\n").length) patch.__homePick = kept.join("\n");
            }

            patch.__socOff = claimed.join(",");
            (echAlwaysDyn(echName) ? fedEdit : onData)(patch);
          }}
          onFlash={onFlash}
          onName={(k, v) => {
            /* `wr` lives inside the field loop's scope; here the write is direct, but
               the collection name still has to obey its own declared cap — a pasted
               name must not out-type the guardrail just because it arrived through the
               header instead of the card. */
            const r = grMeta(block.type, k);
            const cl = clamp(v, r);
            if (cl !== v && onFlash) onFlash(limitMessage(r));
            /* ── AND IT HAS TO CLAIM THE FIELD (2026-08-28, Jack: "collection name
               just refuses to save the change… I can edit but nothing sticks") ──────
               The row writers already respect a claim — every one of them opens its
               `set()` with `if (socIsOff(m, k)) return;` — and `collection{n}Name` is
               written by three of them (the grouped, plans-at-community and
               comms-level branches all `set("collection1Name", …)`). Nothing ever
               ADDED the claim, so a typed header was overwritten by the next pass and
               the edit vanished. Same class as the 2026-08-22 card/row sweep; the
               collection HEADER was the write path it missed.

               `echClaimPatch` cannot do it: it gates on `map[k]`, and collection names
               are deliberately NOT in the dyn map ("Collection names stay manual",
               echDyn L468). So the claim is set directly, with `socOffList` — the same
               helper the takeover path uses. Claimed even when the value is emptied:
               an emptied header is a decision, exactly as `echRowOwnedKey` says. */
            const np: Record<string, unknown> = { [k]: cl };
            if (!socIsOff(mBlk, k)) np.__socOff = socOffList(mBlk, k, true);
            onData(np);
          }}
        />
      </Sec>,
    );
  }

  /* the amenity list goes at the BOTTOM of the card, after every other field —
     monolith `_card += __amenPickerHtml(m, def)` (L13487). The head carries the
     RANGE, not just the count: the ceiling is the block's own cell count, which is
     why a template wanting more than four has to move to the merged block. */
  if (amOn) {
    sections.push(
      <Sec
        head="Amenities"
        key="__amen"
        cls="echf"
        cnt={<span className={"ech-cnt" + (amPicks.length <= AMEN_MIN ? " near" : "")}>{amPicks.length} of {amPairs.length}</span>}
      >
        <AmenPicker
          lib={amLib}
          order={amenGroupOrder()}
          picked={amPicks.map((p) => p.label)}
          onPick={pickAmen}
        />
      </Sec>,
    );
  }

  /* ── THE FIVE SOCIAL ROWS (audit row 9, monolith `__socPickerHtml` L11699–11726,
     emitted last at L13489) ─────────────────────────────────────────────────────
     THE ROW IS A DIV WRAPPING A TOGGLE BUTTON, NOT A BUTTON. Copy and Open have
     to be real controls beside it, and a <button> cannot nest inside a <button>
     (the monolith notes this at L11709–11710 — it shipped the other way first). */
  if (socOn) {
    sections.push(
      <Sec
        head="Icons"
        key="__soc"
        cls="echf"
        cnt={<span className="ech-cnt">{soc.on} of {soc.rows.length}</span>}
      >
        <div className="soclist">
          {soc.rows.map((r) => (
            <div className={"socrow" + (r.live ? " on" : "") + (r.has ? "" : " off")} key={r.k}>
              <button
                type="button"
                className="soctogb"
                data-soctog={r.has ? r.k : undefined}
                disabled={!r.has}
                title={r.has ? undefined : "No handle on file for this division"}
                onClick={() => toggleSoc(r.k, r.feedUrl)}
              >
                <span className="socnm">{r.name}</span>
                {/* the FULL url, per Jack: "make sure it shows the url" — it is
                    what a division sanity-checks, not the logo */}
                <span className="sochd">{r.has ? r.url : "not on file"}</span>
                <span className="soctk">{r.live ? "✓" : ""}</span>
              </button>
              {r.url && <span className="socact"><UrlBar value={r.url} mini /></span>}
            </div>
          ))}
        </div>
        <div className="ech-dyns">{socNote(echCtx ? echCtx.divisionCode : "")}</div>
      </Sec>,
    );
  }

  /* ── ONE BUTTON OR TWO IS A SIBLING BLOCK (audit row 16, monolith
     `__echSwapSegHtml` L11145–11150, emitted ABOVE the mode strip at L13267) ──── */
  const swap = echf && onSwap ? (echSwapFor(echName) || (m2 ? m2SwapFor(echName, (n) => !!echByName(n)) : null)) : null;
  /* each look's picture, read off its own Content Builder markup — only when EVERY look in
     the family has one (the hero banners); otherwise the text strip stays */
  const swapThumbs = (() => {
    if (!swap || !m2 || swap.opts.length < 3) return null;
    const out: Record<string, string> = {};
    for (const [n] of swap.opts) {
      const meta = echByName(n);
      const def = meta ? (blockDef(meta.key) as unknown as { def?: () => Record<string, unknown> } | undefined) : undefined;
      const html = String((def && typeof def.def === "function" ? def.def().__html : "") || "");
      const m = html.match(/<img\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/i);
      if (!m || !/^https?:\/\//i.test(m[1])) return null;
      out[n] = m[1];
    }
    return out;
  })();
  const doSwap = (to: string) => {
    if (!swap || !onSwap || to === echName) return;
    /* ── THE WHOLE FEATURED SECTION MOVES (2026-09-18, Jack: "buttons 1 or 2 should
       affect all not just one"). Five cards are five blocks, but one listing — the
       strip answers for all of them in one document write, and the section lands on
       the selected card's labels so no card keeps a stray default. A lone card, or a
       Studio without the many-write, takes the single swap as before. */
    if (planOn && onSwapMany && planCards.length > 1) {
      const all = echSectionSwap(planCards, block.id, to, echCtx || null, doc || []);
      if (!all) { if (onToast) onToast("That block isn't loaded yet — try again in a moment."); return; }
      onSwapMany(all);
      return;
    }
    const r = echSwapTo(mBlk, echName, to);
    /* not registered yet — the monolith says so rather than silently no-op'ing */
    if (!r) { if (onToast) onToast("That block isn't loaded yet — try again in a moment."); return; }
    onSwap(r.type, r.data);
  };

  /* ── SECTION LAYOUT: FEATURED CARDS ⇄ LISTING TABLE (2026-08-21, MVP) ─────────
     Both real SFMC blocks — "07 - Listing Table" (1027211) has authored
     guardrails and is admitted to the registry by id, it just isn't in the ECH
     template order yet. Cards → table stashes the WHOLE card section on the
     table block and absorbs the siblings; table → cards restores the stash
     verbatim. See services/echLayout.ts for the doctrine. */
  /* BOTH tables carry the strip (2026-08-21, Jack on the Community Home Table:
     "why can't we choose between listing table and featured home") */
  const layTbl = echf && isListingTable(block.type);
  const layOn = onSwap && (layTbl ? layCardsReady() : planOn && layTableReady());
  const doLayout = (toTable: boolean) => {
    if (!onSwap || toTable === layTbl) return;
    if (toTable) {
      const cards = (planCards.length ? planCards : [{ id: block.id, data: d }])
        .map((c) => ({ type: block.type, data: c.data }));
      const r = layToTable(mBlk, echName, cards, echCtx ? echPacks(echCtx) : []);
      if (!r) { if (onToast) onToast("The Listing Table block isn't loaded yet — try again in a moment."); return; }
      /* the table IS the section now — the sibling cards fold into the stash */
      planCards.filter((c) => c.id !== block.id).forEach((c) => { if (onDeleteBlock) onDeleteBlock(c.id); });
      onSwap(r.type, r.data);
    } else {
      const out = layToCards(mBlk, echName);
      if (!out) { if (onToast) onToast("The Featured block isn't loaded yet — try again in a moment."); return; }
      /* ── THE PANEL AND THE CARD AGREE ON WHAT IS SHOWING (2026-08-22) ─────────
         `layToCards` carries the block's state across, so the card paints — but a
         table that was auto-filled POSITIONALLY has no stored pick, and the panel
         reads its tiles off the stored pick. First swap therefore drew "HAILEY II ·
         1,897 sq ft" on the canvas over a panel saying "Choose a plan": two
         renderings of one fact disagreeing, which this codebase treats as a defect
         whichever way round it happens.
         So the pick is MINTED from the row the fill is already using — the packs in
         hand name it — and nothing about what ships changes. Only when the block
         carries no pick of its own; an explicit pick is never overwritten. */
      const data = { ...out.first.data } as Record<string, unknown>;
      /* the level is INFERRED on a listing table — nothing writes `__featLevel` on
         one, because `echFeatOf` answers "homes" for it from its declared mode. So
         ask the same function the fill asks, or this mint silently never runs (it
         didn't, first try: `data.__featLevel` was empty and both branches skipped). */
      const lvl = String(data.__featLevel || echFeatOf(mBlk) || "");
      const ps = echCtx ? echPacks(echCtx) : [];
      /* ── EVERY TABLE ROW BECOMES A CARD, NOT JUST THE FIRST (2026-08-22) ───────
         Jack, after the carry landed: "better but why only one floor plan showing?"
         A ten-row table became ONE card because the mint only read row 1. A table
         and a card set are two renderings of the SAME homes, so the swap has to
         carry all of them — capped at the section's five cards (his 2026-08-21
         rule), which is also why a ten-row table cannot come back as ten cards.
         Row order is preserved: row 1 fills the swapped block, rows 2–5 arrive as
         siblings below it, each seeded with its OWN pick so its panel tile is right.
         Matching is per row — advertised homesite number at homes level, plan name
         at plans level — against the packs already in hand. A row nothing matches
         is skipped rather than guessed at. */
      /* ── THE MINT LIVES IN echCardMint (2026-09-10) ─────────────────────────────
         Measured on prod (ATL, Division focus, the template's own table, five
         communities across three markets): Plan cards gave ONE card. The mint that
         used to sit here matched each row to a home by its ADVERTISED number across
         every pack and took the first pack that had a lot ending in it — Asbury Oak,
         pack one, shortened to most of them, so rows 3–10 all "belonged" to Asbury
         Oak and the one-per-community rule skipped every one. `echMintCards` resolves
         a row by its FULL lot (`__lots`, unique) and groups the way Jack ruled: one
         card per market when the rows span several, else one per community, else
         three from the one community; five at most. */
      /* ── SEED FROM THE TABLE'S ROWS ON A FRESH SWAP, NEVER ON A STASH RESTORE ────
         (2026-08-27, Jack: "featured floor plans still isn't showing when i switch
         from table to featured plan".) `carryDyn` copies the table's `__homePick`
         onto the new card, so a FRESH swap can arrive already holding a pick — the
         old `!hadPick` gate read that as "leave it" and nothing seeded. `fresh` is the
         honest test: a stash restore carries the requester's OWN card selection and
         must not be touched; a fresh swap is seeded from the rows. */
      const hadPick = !!String(data[ECH_PLAN_PICK] || "").trim() || !!String(data[ECH_HOME_PICK] || "").trim();
      const mints: CardMint[] = (out.fresh || !hadPick) ? echMintCards(d, ps, lvl, CARD_CAP) : [];
      if (mints[0]) data[mints[0].key] = mints[0].val;
      /* ── THE LEVEL TRAVELS WITH THE CARD, ALWAYS (2026-08-22, re-sited 2026-08-27) ──
         The siblings below get `__featLevel` written explicitly in their seed; the
         swapped block was getting the pick WITHOUT it, so `echFeatOf` fell back to a
         Featured block's own default — "plans" — and a card holding a HOME pick read
         the plans map, found nothing, and rendered the template's placeholder.

         Originally written two conditions deep (`if (!hadPick) { if (mints[0]) { … } }`),
         which is why it kept not firing: the swap that most needs a level is exactly
         the one that arrives carrying a pick. A card's level is never conditional on
         whether we happened to mint something for it, so it is written unconditionally
         here, on the one line before the swap. */
      if (lvl) data.__featLevel = lvl;
      onSwap(out.first.type, data);
      /* the rest of the stashed card set, back below in its original order
         (each insert lands directly below the swapped block, so reversed) */
      out.rest.slice().reverse().forEach((e) => { if (onInsertBelow) onInsertBelow(e.type, e.data); });
      /* …and, on a FRESH swap (nothing came back from a stash), one sibling card per
         remaining row. Reversed for the same reason: each insert lands directly below
         this block, so the last one queued ends up first. */
      if (!out.rest.length && mints.length > 1 && onInsertBelow) {
        mints.slice(1).reverse().forEach((m) => {
          onInsertBelow(out.first.type, {
            __echMode: "dynamic",
            ...(lvl ? { __featLevel: lvl } : {}),
            [m.key]: m.val,
          });
        });
      }
    }
  };

  /* ── LOCKED BLOCKS SAY WHY, AND STILL SHOW THEIR VALUES (audit rows 7 + 8,
     monolith L13493–13573) ────────────────────────────────────────────────────
     A block with nothing editable — Contact Us, the disclaimers, the divider —
     used to render an empty field area with rules floating under it. Now: ONE
     grey sentence saying what it is and that it is set elsewhere, then the
     block's own image and links as read-only reference rows. */
  /* ── MOSTLY-LOCKED IS NOT LOCKED, AND THE PANEL NOW SAYS SO ONCE (2026-08-20) ──
     Jack, on the Contact Us panel: "lot of crap, all of this copy is very overwhelming
     and unneeded on the front end… also why do you have LINKS TO but the contact us url
     is blank".

     Both complaints, one cause. The panel was rendering FOUR things for one fact: an
     apparently-empty URL field, a sentence we invented, an Image reference row, and a
     "Links to" row carrying a real resolved address — and the field and the row read as
     a contradiction. They never were: `echIscPatch` swaps in the division's own Contact
     Us layoutblock (its own ISC photo, its own /contact href), and `ContactUrl`'s
     declared default is the bare `https://` placeholder, correctly treated as "not
     overridden".

     So a block with something editable gets NO read-only card at all. The one editable
     field carries the truth as its placeholder (below), which makes it self-explanatory:
     empty = the division's link is used, type = override it. The image and the link are
     both already visible on the canvas.

     The read-only card stays for a FULLY locked block — a disclaimer, a hero banner, the
     divider — where the reference rows are the only content there is to show. */
  /* THE MISSING-BLOCK ROW IS LOCKED TOO (2026-08-20, per Jack: "stating locked and not
     editable"). It is not an `sfmc_*` type, so `echf` is false and it would otherwise
     fall through to the bare "No editable content." line — which says nothing about WHY.
     Its own sentence names the block and points at Content Builder, because that is the
     only place it can be fixed. */
  const isMissingBlock = block.type === "echblock";
  /* a Contact Us block with derived per-button fields is NOT fully locked — it has
     one editable url per button (2026-08-28, see `contactFlds` above) */
  const fullyLocked = (echf || isMissingBlock) && !panelFields.length;
  const lockWhy = !fullyLocked ? ""
    : isMissingBlock
      ? String(d.name || "This block") + " isn’t in Marketing Cloud, so there’s nothing to edit here. Ask eMarketing to add the block, then reopen the Studio."
      : echLockSentence(echMeta(block.type)?.lockNote, echName);
  const refRows = fullyLocked && !isMissingBlock ? echRefRows(d.__html) : [];
  /* ── CONTACT US: WITH TEAM PHOTOS | NO PHOTOS (2026-09-23, Justin Bradford) ──────
     Offered only when the DIVISION's block has photos to remove — read off the untouched
     division markup (`echCtx.iscHtml`), not the canvas copy, which no longer holds them
     once "No photos" is picked. A block that declares a data source is skipped: its image
     treatment comes from the DE row. Same strip as "Plan cards | Listing table". */
  const iscOff = echIscNoPhotos(d as Record<string, unknown>);
  const iscSeg = (echf && ECH_ISC_RE.test(echName) && !echDataSource(echName)
    && (iscOff || echIscHasPhotos(echCtx?.iscHtml)))
    /* the record-filled block (2026-09-25): offered when the division's row has photos */
    || !!(cuSt && (cuSt.hasPhotos || cuSt.noPhotos));

  return (
    <div className="inspx" ref={rootRef}>
      {/* the swap strip sits ABOVE the Manual/Dynamic toggle (monolith L13267):
          which block you are editing is a bigger decision than how it is filled */}
      {/* ── A FAMILY OF PICTURES PICKS BY PICTURE (Jack chose option 3, 2026-10-12). When
          every look is a locked banner with art (the hero family), the strip becomes the
          banners themselves, grouped by the stage a community is in. Same doSwap. */}
      {swap && swapThumbs && (
        <div className="m2looks" data-echswapseg data-m2looks style={{ margin: "0 0 14px" }}>
          <div className="m2lk-h">Banner</div>
          {m2LookGroups(swap.opts, (n) => {
            const g = (echByName(n)?.full || null) as unknown as { stage?: string } | null;
            return (g && typeof g.stage === "string" && g.stage) || "";
          }).map(([stage, opts]) => (
            <div key={stage} className="m2lk-g">
              <div className="m2lk-s">{stage}</div>
              <div className="m2lk-grid">
                {opts.map(([name, lab]) => (
                  <button key={name} type="button" className={"m2lk" + (name === echName ? " on" : "")} data-echswap={name}
                    aria-pressed={name === echName} title={lab} onClick={() => doSwap(name)}>
                    <img src={swapThumbs[name]} alt="" loading="lazy" />
                    <span>{lab}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {swap && !swapThumbs && (
        <div className={"dynseg" + (swap.opts.length > 4 ? " many" : "")} data-echswapseg style={{ margin: "0 0 14px" }}>
          {swap.opts.map(([name, lab]) => (
            <button key={name} type="button" className={name === echName ? "on" : ""} data-echswap={name} onClick={() => doSwap(name)}>{lab}</button>
          ))}
        </div>
      )}
      {/* the SECTION LAYOUT strip — same rank as the sibling swap: which block
          renders this section is a bigger decision than how it is filled */}
      {iscSeg && (
        <div className="dynseg" data-echiscseg style={{ margin: "0 0 14px" }}>
          <button type="button" className={iscOff ? "" : "on"} data-echisc="on" onClick={() => onData({ [ECH_ISC_NO_PHOTOS]: "" })}>With team photos</button>
          <button type="button" className={iscOff ? "on" : ""} data-echisc="off" onClick={() => onData({ [ECH_ISC_NO_PHOTOS]: "1" })}>No photos</button>
        </div>
      )}
      {layOn && (
        <div className="dynseg" data-echlayseg style={{ margin: "0 0 14px" }}>
          <button type="button" className={layTbl ? "" : "on"} data-echlay="cards" onClick={() => doLayout(false)}>Plan cards</button>
          <button type="button" className={layTbl ? "on" : ""} data-echlay="table" onClick={() => doLayout(true)}>Listing table</button>
        </div>
      )}
      {/* An ECH block gets the ECH strip or NO strip — never the legacy one, whose
          greyed-out "Dynamic" showed Manual as active on every SFMC block while the
          feed was overwriting it (code review #1). Legacy blocks are untouched:
          a real Manual/Dynamic toggle where live fill exists, Dynamic disabled
          everywhere else (monolith 2026-07-22 parity). */}
      {isEch(block.type) ? (
        echStrip ? (
          <div className="dynseg" data-echdynseg>
            <button type="button" className={echOn ? "" : "on"} onClick={() => setEchMode("manual")}>Manual</button>
            <button type="button" className={echOn ? "on dy" : ""} onClick={() => setEchMode("dynamic")}>Dynamic</button>
          </div>
        ) : null
      ) : !noStrip ? (
        <div className="dynseg">
          <button type="button" className={dynOn ? "" : "on"} onClick={() => setDynOn(false)}>Manual</button>
          {liveF ? (
            <button type="button" className={dynOn ? "on dy" : ""} onClick={() => setDynOn(true)}>Dynamic</button>
          ) : (
            <button type="button" style={{ opacity: 0.45, cursor: "not-allowed" }} title="No live community feed for this block — content is edited by hand">Dynamic</button>
          )}
        </div>
      ) : null}
      {/* `!echf` is explicit, not decorative: `dynOn` is the LEGACY useState and
          nothing on an ECH block ever calls `setDynOn` (ECH mode lives on
          `block.data.__echMode`), so this could not fire today — but an ECH block
          now HAS a `liveF`, and one stray `setDynOn` would swap Layne's whole field
          card for a bare picker. The picker's real home on an ECH block is the
          `__echpick` section pushed above. */}
      {!echf && dynOn && liveF ? (
        <div className="inspcard">
          <Sec head={liveF.label || "Live content from SFMC"}>{liveFill(liveF)}</Sec>
        </div>
      ) : sections.length > 0 ? (
        <div className="inspcard">
          {compat && (
            <div className={"cmpt " + compat.risk}><span className="cmpt-i">⚠</span><span>{compat.note}</span></div>
          )}
          {sections}
        </div>
      ) : (
        <>
          {compat && <div className={"cmpt " + compat.risk}><span className="cmpt-i">⚠</span><span>{compat.note}</span></div>}
          {/* the ECH read-only card below says this better and says it once —
              don't stack a generic "No editable content." above it (monolith L13494) */}
          {!fullyLocked && <div className="esemptyi" style={{ padding: "18px 0" }}>No editable content.</div>}
        </>
      )}
      {cuSt && <ContactButtons st={cuSt} d={d as Record<string, unknown>} onData={onData} />}
      {fullyLocked && <div className="ech-ro">{lockWhy}</div>}
      {fullyLocked && <RefRows rows={refRows} />}
      {onDelete && (
        <button type="button" className={deleteWhy ? "esdelf off" : "esdelf"} aria-disabled={deleteWhy ? true : undefined} title={deleteWhy || undefined} onClick={onDelete}>Delete this block</button>
      )}
    </div>
  );
}
